import React, { useState, useEffect, useMemo, useRef } from 'react';

const TABLE_PAGE_SIZE = 100;

function PaginationControls({ currentPage, totalRows, pageSize, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const startRow = currentPage * pageSize + 1;
  const endRow = Math.min((currentPage + 1) * pageSize, totalRows);
  if (totalRows <= pageSize) return null;
  return (
    <div className="pagination-controls">
      <button disabled={currentPage === 0} onClick={() => onPageChange(0)}>⏮ First</button>
      <button disabled={currentPage === 0} onClick={() => onPageChange(currentPage - 1)}>◀ Prev</button>
      <span className="pagination-info">
        Rows {startRow}–{endRow} of {totalRows} (page {currentPage + 1} of {totalPages})
      </span>
      <button disabled={currentPage >= totalPages - 1} onClick={() => onPageChange(currentPage + 1)}>Next ▶</button>
      <button disabled={currentPage >= totalPages - 1} onClick={() => onPageChange(totalPages - 1)}>Last ⏭</button>
    </div>
  );
}

const HEB_BOOK_NAMES = {
  Gen: 'Genesis', Exo: 'Exodus', Lev: 'Leviticus', Num: 'Numbers', Deu: 'Deuteronomy',
  Jos: 'Joshua', Judg: 'Judges', Rth: 'Ruth', '1Sa': '1 Samuel', '2Sa': '2 Samuel',
  '1Ki': '1 Kings', '2Ki': '2 Kings', '1Cr': '1 Chronicles', '2Cr': '2 Chronicles',
  Eza: 'Ezra', Neh: 'Nehemiah', Est: 'Esther', Job: 'Job', Psa: 'Psalms',
  Pro: 'Proverbs', Ecc: 'Ecclesiastes', SS: 'Song of Solomon', Isa: 'Isaiah',
  Jer: 'Jeremiah', Lam: 'Lamentations', Ezk: 'Ezekiel', Dan: 'Daniel', Hos: 'Hosea',
  Joe: 'Joel', Amo: 'Amos', Obd: 'Obadiah', Jon: 'Jonah', Mic: 'Micah',
  Nah: 'Nahum', Hab: 'Habakkuk', Zep: 'Zephaniah', Hag: 'Haggai', Zch: 'Zechariah',
  Mal: 'Malachi',
};

function App() {
  console.log('App component mounted');
  const viewerTableContainerRef = useRef(null);
  const [allColumns, setAllColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMenu, setActiveMenu] = useState('viewer');
  const [selectedBook, setSelectedBook] = useState('MAT');
  const [selectedChapter, setSelectedChapter] = useState('1');
  const [availableBooks, setAvailableBooks] = useState([]);
  const [availableChapters, setAvailableChapters] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedClassFilter, setSelectedClassFilter] = useState('');
  const [selectedGenderFilter, setSelectedGenderFilter] = useState('');
  const [selectedCaseFilter, setSelectedCaseFilter] = useState('');
  const [selectedNumberFilter, setSelectedNumberFilter] = useState('');
  const [selectedTenseFilter, setSelectedTenseFilter] = useState('');
  const [selectedVoiceFilter, setSelectedVoiceFilter] = useState('');
  const [selectedMoodFilter, setSelectedMoodFilter] = useState('');
  const [selectedPersonFilter, setSelectedPersonFilter] = useState('');
  const [loadingProgress, setLoadingProgress] = useState({ phase: 'downloading', percent: 0 });
  const [frequencyStrongNumber, setFrequencyStrongNumber] = useState(null);
  const [columnPanelOpen, setColumnPanelOpen] = useState(false);
  const [showStrongPanel, setShowStrongPanel] = useState(false);
  const [translationReturnMenu, setTranslationReturnMenu] = useState('');
  const [searchMenuOpen, setSearchMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchSelectedValue, setSearchSelectedValue] = useState(null);
  const [translationData, setTranslationData] = useState({});
  const [selectedTranslations, setSelectedTranslations] = useState(['greek_nt', 'acf_nt']);
  const [translationsLoading, setTranslationsLoading] = useState(false);
  const [translationSearchValue, setTranslationSearchValue] = useState('');
  const [translationSearchField, setTranslationSearchField] = useState('');
  const [translationSearchLemma, setTranslationSearchLemma] = useState('');
  const [translationSearchPortuguese, setTranslationSearchPortuguese] = useState('');
  const [translationFilterStrongs, setTranslationFilterStrongs] = useState(null);
  const [selectedSearchRow, setSelectedSearchRow] = useState(null);
  const [searchPortugueseSelectedStrong, setSearchPortugueseSelectedStrong] = useState(null);

  // Bibles viewer state (independent from Data Viewer)
  const [biblesBook, setBiblesBook] = useState('MAT');
  const [biblesChapter, setBiblesChapter] = useState('1');
  const [biblesAvailableChapters, setBiblesAvailableChapters] = useState([]);
  const [biblesSelectedTranslations, setBiblesSelectedTranslations] = useState(['greek_nt', 'nkjv_nt', 'niv_nt', 'acf_nt', 'nvi_nt']);
  const [biblesData, setBiblesData] = useState({});
  const [biblesLoading, setBiblesLoading] = useState(false);

  // Interlinear viewer state (independent book/chapter from Data Viewer)
  const [interlinearBook, setInterlinearBook] = useState('MAT');
  const [interlinearChapter, setInterlinearChapter] = useState('1');
  const [interlinearAvailableChapters, setInterlinearAvailableChapters] = useState([]);
  const [interlinearShowLemma, setInterlinearShowLemma] = useState(true);
  const [interlinearShowPortuguese, setInterlinearShowPortuguese] = useState(true);
  const [interlinearShowStrong, setInterlinearShowStrong] = useState(true);
  const [interlinearShowMorph, setInterlinearShowMorph] = useState(true);
  const [interlinearShowGloss, setInterlinearShowGloss] = useState(true);

  // ==================== LXX STATE ====================
  const [lxxMenuOpen, setLxxMenuOpen] = useState(false);
  // LXX data
  const [lxxVerseData, setLxxVerseData] = useState([]); // LXX_Rahlfs_by_verse.csv
  const [lxxWordData, setLxxWordData] = useState([]);   // LXX_Rahlfs_by_word_hebrew.csv
  const [lxxOtTranslations, setLxxOtTranslations] = useState({}); // { acf_ot: {}, hebrew_bible: {}, niv_ot: {}, nkjv_ot: {}, nvi_ot: {} }
  const [lxxDataLoading, setLxxDataLoading] = useState(false);
  const [lxxDataLoaded, setLxxDataLoaded] = useState(false);
  // LXX book/chapter selectors
  const [lxxAvailableBooks, setLxxAvailableBooks] = useState([]);
  const [lxxSelectedBook, setLxxSelectedBook] = useState('Gen');
  const [lxxSelectedChapter, setLxxSelectedChapter] = useState('1');
  const [lxxAvailableChapters, setLxxAvailableChapters] = useState([]);
  // LXX Bible state
  const [lxxBiblesSelectedTranslations, setLxxBiblesSelectedTranslations] = useState(['lxx', 'hebrew_bible', 'nkjv_ot', 'niv_ot', 'acf_ot', 'nvi_ot']);
  const [lxxBiblesLoading, setLxxBiblesLoading] = useState(false);
  // LXX Interlinear toggles
  const [lxxInterlinearShowLemma, setLxxInterlinearShowLemma] = useState(true);
  const [lxxInterlinearShowGloss, setLxxInterlinearShowGloss] = useState(true);
  const [lxxInterlinearShowMorph, setLxxInterlinearShowMorph] = useState(true);
  const [lxxInterlinearShowStrong, setLxxInterlinearShowStrong] = useState(true);
  const [lxxInterlinearShowHebStrong, setLxxInterlinearShowHebStrong] = useState(true);
  // LXX Search Strong
  const [lxxSearchText, setLxxSearchText] = useState('');
  const [lxxSearchSelectedValue, setLxxSearchSelectedValue] = useState(null);
  const [lxxSearchTranslationsData, setLxxSearchTranslationsData] = useState({});
  const [lxxSearchTranslationsLoading, setLxxSearchTranslationsLoading] = useState(false);
  const [lxxSearchSelectedTranslations, setLxxSearchSelectedTranslations] = useState(['lxx', 'hebrew_bible', 'nkjv_ot', 'niv_ot', 'acf_ot', 'nvi_ot']);
  // LXX Search Lemma
  const [lxxLemmaSearchText, setLxxLemmaSearchText] = useState('');
  const [lxxLemmaSelectedValue, setLxxLemmaSelectedValue] = useState(null);
  // LXX Verse Grid
  const [lxxGridColumns, setLxxGridColumns] = useState([]);
  const [lxxGridSelectedColumns, setLxxGridSelectedColumns] = useState([]);
  const [lxxGridColumnPanelOpen, setLxxGridColumnPanelOpen] = useState(false);
  const [lxxGridNumberingMode, setLxxGridNumberingMode] = useState('lxx'); // 'lxx' | 'acf'
  const [lxxGridChapter, setLxxGridChapter] = useState('1');
  // LXX Strong Frequency — pre-aggregated to avoid blocking the main thread on 587k rows
  const [lxxStrongGrouped, setLxxStrongGrouped] = useState(null); // Map<strong, {lemma,gloss,hebrew_strong,total,classBuckets}>
  const [lxxFilterValues, setLxxFilterValues] = useState({ classes: [], genders: [], cases: [], numbers: [], tenses: [], voices: [], moods: [], persons: [] });
  const [lxxSelectedClassFilter, setLxxSelectedClassFilter] = useState('');
  const [lxxSelectedGenderFilter, setLxxSelectedGenderFilter] = useState('');
  const [lxxSelectedCaseFilter, setLxxSelectedCaseFilter] = useState('');
  const [lxxSelectedNumberFilter, setLxxSelectedNumberFilter] = useState('');
  const [lxxSelectedTenseFilter, setLxxSelectedTenseFilter] = useState('');
  const [lxxSelectedVoiceFilter, setLxxSelectedVoiceFilter] = useState('');
  const [lxxSelectedMoodFilter, setLxxSelectedMoodFilter] = useState('');
  const [lxxSelectedPersonFilter, setLxxSelectedPersonFilter] = useState('');
  const [lxxFrequencyStrongNumber, setLxxFrequencyStrongNumber] = useState(null);
  const [lxxFreqOccColumnPanelOpen, setLxxFreqOccColumnPanelOpen] = useState(false);
  // LXX Verse Grid selected row + verse translations drill-down
  const [lxxGridSelectedRow, setLxxGridSelectedRow] = useState(null);
  const [lxxVerseTranslationsLemma, setLxxVerseTranslationsLemma] = useState('');
  const [lxxVerseTranslationsSelectedTranslations, setLxxVerseTranslationsSelectedTranslations] = useState(['lxx', 'hebrew_bible', 'nkjv_ot', 'niv_ot', 'acf_ot', 'nvi_ot']);
  // LXX Interlinear verse mode toggle (main view)
  const [lxxInterlinearVerseMode, setLxxInterlinearVerseMode] = useState('lxx'); // 'lxx' | 'acf'
  // LXX Interlinear word detail modal
  const [lxxInterlinearSelectedWord, setLxxInterlinearSelectedWord] = useState(null);
  const [lxxInterlinearWordColumnPanelOpen, setLxxInterlinearWordColumnPanelOpen] = useState(false);
  const [lxxInterlinearWordVerseMode, setLxxInterlinearWordVerseMode] = useState('lxx'); // 'lxx' | 'acf'

  // ==================== HEBREW STATE ====================
  const [hebMenuOpen, setHebMenuOpen] = useState(false);
  // Hebrew data
  const [hebWordData, setHebWordData] = useState([]);   // macula-hebrew.tsv
  const [hebOtTranslations, setHebOtTranslations] = useState({}); // { hebrew_bible: {}, lxx: {}, nkjv_ot: {}, niv_ot: {}, acf_ot: {}, nvi_ot: {} }
  const [hebDataLoading, setHebDataLoading] = useState(false);
  const [hebDataLoaded, setHebDataLoaded] = useState(false);
  // Hebrew book/chapter selectors
  const [hebAvailableBooks, setHebAvailableBooks] = useState([]);
  const [hebSelectedBook, setHebSelectedBook] = useState('Gen');
  const [hebSelectedChapter, setHebSelectedChapter] = useState('1');
  const [hebAvailableChapters, setHebAvailableChapters] = useState([]);
  // Hebrew MT Bible state
  const [hebBiblesSelectedTranslations, setHebBiblesSelectedTranslations] = useState(['hebrew_bible', 'lxx', 'nkjv_ot', 'niv_ot', 'acf_ot', 'nvi_ot']);
  const [hebBiblesLoading, setHebBiblesLoading] = useState(false);
  // Hebrew Interlinear toggles
  const [hebInterlinearShowLemma, setHebInterlinearShowLemma] = useState(true);
  const [hebInterlinearShowTranslit, setHebInterlinearShowTranslit] = useState(true);
  const [hebInterlinearShowEnglish, setHebInterlinearShowEnglish] = useState(true);
  const [hebInterlinearShowMorph, setHebInterlinearShowMorph] = useState(true);
  const [hebInterlinearShowStrong, setHebInterlinearShowStrong] = useState(true);
  const [hebInterlinearShowFrequency, setHebInterlinearShowFrequency] = useState(true);
  const [hebInterlinearFontSize, setHebInterlinearFontSize] = useState(16);
  // Hebrew translation font sizes
  const [hebMtTranslationFontSize, setHebMtTranslationFontSize] = useState(16);
  const [hebSearchStrongFontSize, setHebSearchStrongFontSize] = useState(16);
  const [hebSearchLemmaFontSize, setHebSearchLemmaFontSize] = useState(16);
  // Hebrew Search Strong
  const [hebSearchText, setHebSearchText] = useState('');
  const [hebSearchSelectedValue, setHebSearchSelectedValue] = useState(null);
  const [hebSearchTranslationsData, setHebSearchTranslationsData] = useState({});
  const [hebSearchTranslationsLoading, setHebSearchTranslationsLoading] = useState(false);
  const [hebSearchSelectedTranslations, setHebSearchSelectedTranslations] = useState(['hebrew_bible', 'lxx', 'nkjv_ot', 'niv_ot', 'acf_ot', 'nvi_ot']);
  // Hebrew Search Lemma
  const [hebLemmaSearchText, setHebLemmaSearchText] = useState('');
  const [hebLemmaSelectedValue, setHebLemmaSelectedValue] = useState(null);
  const [hebLemmaSearchTranslationsData, setHebLemmaSearchTranslationsData] = useState({});
  const [hebLemmaSearchTranslationsLoading, setHebLemmaSearchTranslationsLoading] = useState(false);
  const [hebLemmaSearchSelectedTranslations, setHebLemmaSearchSelectedTranslations] = useState(['hebrew_bible', 'lxx', 'nkjv_ot', 'niv_ot', 'acf_ot', 'nvi_ot']);
  // Hebrew Verse Grid
  const [hebGridColumns, setHebGridColumns] = useState([]);
  const [hebGridSelectedColumns, setHebGridSelectedColumns] = useState([]);
  const [hebGridColumnPanelOpen, setHebGridColumnPanelOpen] = useState(false);
  // Hebrew Strong Frequency — pre-aggregated to avoid blocking the main thread
  const [hebStrongGrouped, setHebStrongGrouped] = useState(null); // Map<strong, {lemma,gloss,classBuckets}>
  const [hebFilterValues, setHebFilterValues] = useState({ classes: [], stems: [], poses: [], persons: [], genders: [], numbers: [], states: [], types: [] });
  const [hebSelectedClassFilter, setHebSelectedClassFilter] = useState('');
  const [hebSelectedStemFilter, setHebSelectedStemFilter] = useState('');
  const [hebSelectedPosFilter, setHebSelectedPosFilter] = useState('');
  const [hebSelectedPersonFilter, setHebSelectedPersonFilter] = useState('');
  const [hebSelectedGenderFilter, setHebSelectedGenderFilter] = useState('');
  const [hebSelectedNumberFilter, setHebSelectedNumberFilter] = useState('');
  const [hebSelectedStateFilter, setHebSelectedStateFilter] = useState('');
  const [hebSelectedTypeFilter, setHebSelectedTypeFilter] = useState('');
  const [hebFrequencyStrongNumber, setHebFrequencyStrongNumber] = useState(null);
  const [hebFrequencyEnglish, setHebFrequencyEnglish] = useState(null);
  const [hebFreqOccColumnPanelOpen, setHebFreqOccColumnPanelOpen] = useState(false);
  // Hebrew Interlinear word detail modal
  const [hebInterlinearSelectedWord, setHebInterlinearSelectedWord] = useState(null);
  const [hebInterlinearWordColumnPanelOpen, setHebInterlinearWordColumnPanelOpen] = useState(false);
  // MT <-> ACF verse alignment maps (built from hebrew_bible.csv)
  const [hebMtToAcfMap, setHebMtToAcfMap] = useState({});         // "book_mtCh_mtVs" -> {acf_chapter, acf_verse}
  const [hebAcfToMtChapters, setHebAcfToMtChapters] = useState({}); // "book_acfCh" -> [mtCh, ...]

  // ==================== PAGINATION STATE ====================
  const [viewerPage, setViewerPage] = useState(0);
  const [strongFreqPage, setStrongFreqPage] = useState(0);
  const [freqOccPage, setFreqOccPage] = useState(0);
  const [lxxGridPage, setLxxGridPage] = useState(0);
  const [lxxStrongFreqPage, setLxxStrongFreqPage] = useState(0);
  const [lxxFreqOccPage, setLxxFreqOccPage] = useState(0);
  const [hebGridPage, setHebGridPage] = useState(0);
  const [hebStrongFreqPage, setHebStrongFreqPage] = useState(0);
  const [hebFreqOccPage, setHebFreqOccPage] = useState(0);

  // Lazy-computed metadata (avoids blocking during initial load)
  const availableClasses = useMemo(() => data.length ? [...new Set(data.map(r => r.class))].filter(Boolean).sort() : [], [data]);
  const availableGenders = useMemo(() => data.length ? [...new Set(data.map(r => r.gender))].filter(Boolean).sort() : [], [data]);
  const availableCases = useMemo(() => data.length ? [...new Set(data.map(r => r.case))].filter(Boolean).sort() : [], [data]);
  const availableNumbers = useMemo(() => data.length ? [...new Set(data.map(r => r.number))].filter(Boolean).sort() : [], [data]);
  const availableTenses = useMemo(() => data.length ? [...new Set(data.map(r => r.tense))].filter(Boolean).sort() : [], [data]);
  const availableVoices = useMemo(() => data.length ? [...new Set(data.map(r => r.voice))].filter(Boolean).sort() : [], [data]);
  const availableMoods = useMemo(() => data.length ? [...new Set(data.map(r => r.mood))].filter(Boolean).sort() : [], [data]);
  const availablePersons = useMemo(() => data.length ? [...new Set(data.map(r => r.person))].filter(Boolean).sort() : [], [data]);

  // Load and parse TSV file with streaming download + chunked parsing
  useEffect(() => {
    const controller = new AbortController();
    const loadTSV = async () => {
      try {
        // Fetch version for cache busting
        let version = Date.now();
        try {
          const versionRes = await fetch('/tsv-version.txt', { signal: controller.signal });
          if (versionRes.ok) {
            version = await versionRes.text();
          }
        } catch (e) {
          if (e.name === 'AbortError') return;
          console.log('Version file not found, using timestamp');
        }
        
        const response = await fetch(`/macula-greek-Nestle1904_extended.tsv?v=${version}`, { signal: controller.signal });
        if (!response.ok) {
          throw new Error('Failed to load TSV file');
        }

        // Streaming download with progress
        const contentLength = response.headers.get('Content-Length');
        const totalBytes = contentLength ? parseInt(contentLength, 10) : null;
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let receivedLength = 0;
        const chunks = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          receivedLength += value.length;
          if (totalBytes) {
            setLoadingProgress({ phase: 'downloading', percent: Math.round((receivedLength / totalBytes) * 100) });
          }
        }

        // Concatenate chunks into text
        setLoadingProgress({ phase: 'parsing', percent: 0 });
        const allChunks = new Uint8Array(receivedLength);
        let position = 0;
        for (const chunk of chunks) {
          allChunks.set(chunk, position);
          position += chunk.length;
        }
        const text = decoder.decode(allChunks);
        const lines = text.trim().split('\n');
        
        if (lines.length === 0) {
          throw new Error('TSV file is empty');
        }

        // Parse header
        const headers = lines[0].split('\t').map(h => h.trim());
        setAllColumns(headers);
        const defaultColumns = ['book', 'chapter', 'verse', 'gloss', 'text', 'lemma', 'strong', 'portuguese', 'frequency'];
        const selected = defaultColumns.filter(col => headers.includes(col));
        setSelectedColumns(selected);

        // Parse data rows in chunks, yielding to keep UI responsive
        const totalLines = lines.length - 1;
        const CHUNK_SIZE = 20000;
        const rows = new Array(totalLines);
        const bookOrderMap = new Map();

        for (let start = 0; start < totalLines; start += CHUNK_SIZE) {
          const end = Math.min(start + CHUNK_SIZE, totalLines);
          for (let i = start; i < end; i++) {
            const values = lines[i + 1].split('\t');
            const row = {};
            for (let h = 0; h < headers.length; h++) {
              row[headers[h]] = values[h] || '';
            }
            rows[i] = row;
            if (row.book && row.order && !bookOrderMap.has(row.book)) {
              bookOrderMap.set(row.book, parseInt(row.order));
            }
          }
          setLoadingProgress({ phase: 'parsing', percent: Math.round((end / totalLines) * 100) });
          // Yield to event loop so the browser can repaint the progress bar
          await new Promise(r => setTimeout(r, 0));
        }

        setData(rows);
        
        const books = [...bookOrderMap.keys()].sort((a, b) => bookOrderMap.get(a) - bookOrderMap.get(b));
        setAvailableBooks(books);
        
        setLoading(false);
      } catch (err) {
        if (err.name === 'AbortError') return; // Ignore aborted fetches (React StrictMode unmount)
        setError(err.message);
        setLoading(false);
      }
    };

    loadTSV();
    return () => controller.abort(); // Cleanup on unmount
  }, []);

  // Update available chapters when book changes
  useEffect(() => {
    if (data.length > 0 && selectedBook) {
      const chapters = [...new Set(
        data
          .filter(row => row.book === selectedBook)
          .map(row => row.chapter)
      )].filter(Boolean).sort((a, b) => parseInt(a) - parseInt(b));
      setAvailableChapters(chapters);
      
      // If current chapter doesn't exist in new book, reset to first chapter
      if (!chapters.includes(selectedChapter) && chapters.length > 0) {
        setSelectedChapter(chapters[0]);
      }
    }
  }, [data, selectedBook]);

  // Update available chapters for Bibles viewer when biblesBook changes
  useEffect(() => {
    if (data.length > 0 && biblesBook) {
      const chapters = [...new Set(
        data
          .filter(row => row.book === biblesBook)
          .map(row => row.chapter)
      )].filter(Boolean).sort((a, b) => parseInt(a) - parseInt(b));
      setBiblesAvailableChapters(chapters);
      if (!chapters.includes(biblesChapter) && chapters.length > 0) {
        setBiblesChapter(chapters[0]);
      }
    }
  }, [data, biblesBook]);

  // Update available chapters for Interlinear viewer when interlinearBook changes
  useEffect(() => {
    if (data.length > 0 && interlinearBook) {
      const chapters = [...new Set(
        data
          .filter(row => row.book === interlinearBook)
          .map(row => row.chapter)
      )].filter(Boolean).sort((a, b) => parseInt(a) - parseInt(b));
      setInterlinearAvailableChapters(chapters);
      if (!chapters.includes(interlinearChapter) && chapters.length > 0) {
        setInterlinearChapter(chapters[0]);
      }
    }
  }, [data, interlinearBook]);

  // Load all translations for the Bibles viewer
  const loadBiblesTranslations = async () => {
    setBiblesLoading(true);
    try {
      const newData = { ...biblesData };
      for (const t of TRANSLATIONS) {
        if (!newData[t.key]) {
          if (t.key === 'greek_nt') {
            const lookup = {};
            data.forEach(row => {
              if (row.book && row.chapter && row.verse && row.text) {
                const vk = `${row.book}_${row.chapter}_${row.verse}`;
                if (!lookup[vk]) lookup[vk] = [];
                lookup[vk].push(row.text);
              }
            });
            Object.keys(lookup).forEach(vk => {
              lookup[vk] = lookup[vk].join(' ');
            });
            newData[t.key] = lookup;
          } else {
            newData[t.key] = await loadTranslation(t.key);
          }
        }
      }
      setBiblesData(newData);
    } catch (err) {
      console.error('Error loading Bibles translations:', err);
    }
    setBiblesLoading(false);
  };

  // Load Bibles data when menu activates
  useEffect(() => {
    if (activeMenu === 'bibles' && Object.keys(biblesData).length === 0 && data.length > 0) {
      loadBiblesTranslations();
    }
  }, [activeMenu, data]);

  // Compute verses for the current Bibles book/chapter
  const biblesVerses = useMemo(() => {
    if (Object.keys(biblesData).length === 0) return [];
    // Gather all verse numbers from all loaded translations for this book+chapter
    const verseSet = new Set();
    for (const tKey of Object.keys(biblesData)) {
      const lookup = biblesData[tKey];
      if (!lookup) continue;
      for (const key of Object.keys(lookup)) {
        const parts = key.split('_');
        if (parts[0] === biblesBook && parts[1] === biblesChapter) {
          verseSet.add(parts[2]);
        }
      }
    }
    return [...verseSet].sort((a, b) => parseInt(a) - parseInt(b));
  }, [biblesData, biblesBook, biblesChapter]);

  // Toggle Bibles translation selection
  const toggleBiblesTranslation = (key) => {
    setBiblesSelectedTranslations(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Compute interlinear data: group words by verse for the selected book/chapter
  const interlinearVerses = useMemo(() => {
    if (data.length === 0) return [];
    const chapterRows = data.filter(
      row => row.book === interlinearBook && row.chapter === interlinearChapter
    );
    // Group by verse number, preserving word order
    const verseMap = new Map();
    chapterRows.forEach(row => {
      const v = row.verse;
      if (!v) return;
      if (!verseMap.has(v)) verseMap.set(v, []);
      verseMap.get(v).push({
        text: row.text || '',
        lemma: row.lemma || '',
        portuguese_acf: row.portuguese_acf || '',
        strong: row.strong || '',
        morph: row.morph || '',
        gloss: row.gloss || '',
      });
    });
    // Sort verses numerically and return as array
    return [...verseMap.entries()]
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
      .map(([verseNum, words]) => ({ verseNum, words }));
  }, [data, interlinearBook, interlinearChapter]);

  // ==================== LXX DATA LOADING ====================
  const LXX_BIBLE_TRANSLATIONS = [
    { key: 'lxx', name: 'LXX', language: 'Greek' },
    { key: 'hebrew_bible', name: 'Hebrew', language: 'Hebrew' },
    { key: 'nkjv_ot', name: 'NKJV', language: 'English' },
    { key: 'niv_ot', name: 'NIV', language: 'English' },
    { key: 'acf_ot', name: 'ACF', language: 'Portuguese' },
    { key: 'nvi_ot', name: 'NVI', language: 'Portuguese' },
  ];

  const LXX_SEARCH_TRANSLATIONS = [
    { key: 'lxx', name: 'LXX', language: 'Greek' },
    { key: 'hebrew_bible', name: 'Hebrew', language: 'Hebrew' },
    { key: 'nkjv_ot', name: 'NKJV', language: 'English' },
    { key: 'niv_ot', name: 'NIV', language: 'English' },
    { key: 'acf_ot', name: 'ACF', language: 'Portuguese' },
    { key: 'nvi_ot', name: 'NVI', language: 'Portuguese' },
  ];

  // Fast CSV line splitter — handles quoted fields (e.g., gloss "10,000 dollars;talent")
  const splitCsvLine = (line) => {
    if (!line.includes('"')) return line.split(',');
    const fields = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQuotes) {
        if (c === '"') { if (line[i + 1] === '"') { current += '"'; i++; } else inQuotes = false; }
        else current += c;
      } else {
        if (c === '"') inQuotes = true;
        else if (c === ',') { fields.push(current); current = ''; }
        else if (c !== '\r') current += c;
      }
    }
    fields.push(current);
    return fields;
  };

  // Parse a large flat file (CSV or TSV) in async 10 k-row chunks so the main
  // thread stays responsive.  Returns { headers: string[], rows: object[] }.
  const parseFileAsync = async (text, delimiter) => {
    const lines = text.split('\n');
    const splitLine = delimiter === '\t' ? (line) => line.split('\t') : splitCsvLine;
    const headers = splitLine(lines[0]).map(h => h.trim().toLowerCase());
    const totalLines = lines.length - 1;
    const CHUNK = 10000;
    const rows = [];
    for (let start = 1; start <= totalLines; start += CHUNK) {
      const end = Math.min(start + CHUNK, totalLines + 1);
      for (let i = start; i < end; i++) {
        if (!lines[i] || !lines[i].trim()) continue;
        const values = splitLine(lines[i]);
        const row = {};
        for (let h = 0; h < headers.length; h++) {
          row[headers[h]] = (values[h] || '').trim();
        }
        rows.push(row);
      }
      await new Promise(r => setTimeout(r, 0)); // yield to event loop
    }
    return { headers, rows };
  };

  // Load LXX data when any LXX menu is activated
  const loadLxxData = async () => {
    if (lxxDataLoaded || lxxDataLoading) return;
    setLxxDataLoading(true);
    try {
      // Load LXX by verse (7.9 MB — chunked async parse)
      const verseRes = await fetch('/LXX_Rahlfs_by_verse.csv');
      const verseText = await verseRes.text();
      const { rows: parsedVerses } = await parseFileAsync(verseText, ',');
      setLxxVerseData(parsedVerses);

      // Load LXX by word (56 MB — chunked async parse keeps UI responsive)
      const wordRes = await fetch('/LXX_Rahlfs_by_word_hebrew.csv');
      const wordText = await wordRes.text();
      const { headers: wordHeaders, rows: parsedWords } = await parseFileAsync(wordText, ',');
      setLxxWordData(parsedWords);

      // Pre-aggregate Strong Frequency data in chunked passes
      const fv = { classes: new Set(), genders: new Set(), cases: new Set(), numbers: new Set(), tenses: new Set(), voices: new Set(), moods: new Set(), persons: new Set() };
      const grouped = {};
      const AGG_CHUNK = 20000;
      for (let start = 0; start < parsedWords.length; start += AGG_CHUNK) {
        const end = Math.min(start + AGG_CHUNK, parsedWords.length);
        for (let i = start; i < end; i++) {
          const row = parsedWords[i];
          const s = row.strong;
          if (row.class) fv.classes.add(row.class);
          if (row.gender) fv.genders.add(row.gender);
          if (row.case) fv.cases.add(row.case);
          if (row.number) fv.numbers.add(row.number);
          if (row.tense) fv.tenses.add(row.tense);
          if (row.voice) fv.voices.add(row.voice);
          if (row.mood) fv.moods.add(row.mood);
          if (row.person) fv.persons.add(row.person);
          if (!s) continue;
          if (!grouped[s]) {
            grouped[s] = { lemma: row.lemma || '', gloss: row.gloss || '', hebrew_strong: row.hebrew_strong || '', classBuckets: {} };
          }
          const cls = row.class || '';
          if (!grouped[s].classBuckets[cls]) grouped[s].classBuckets[cls] = [];
          grouped[s].classBuckets[cls].push({
            gender: row.gender || '', case: row.case || '', number: row.number || '',
            tense: row.tense || '', voice: row.voice || '', mood: row.mood || '', person: row.person || ''
          });
        }
        await new Promise(r => setTimeout(r, 0));
      }
      setLxxFilterValues({
        classes: [...fv.classes].sort(), genders: [...fv.genders].sort(),
        cases: [...fv.cases].sort(), numbers: [...fv.numbers].sort(),
        tenses: [...fv.tenses].sort(), voices: [...fv.voices].sort(),
        moods: [...fv.moods].sort(), persons: [...fv.persons].sort()
      });
      setLxxStrongGrouped(grouped);

      // Set grid columns from word data
      setLxxGridColumns(wordHeaders);
      const defaultGridCols = ['book', 'chapter', 'verse', 'greek_text', 'strong', 'gloss', 'morphology', 'lemma', 'frequency', 'hebrew_strong'];
      setLxxGridSelectedColumns(defaultGridCols.filter(c => wordHeaders.includes(c)));

      // Extract unique OT books in order
      const bookOrderMap = new Map();
      parsedVerses.forEach(row => {
        if (row.book && row.book_order && !bookOrderMap.has(row.book)) {
          bookOrderMap.set(row.book, parseInt(row.book_order));
        }
      });
      const otBooks = [...bookOrderMap.keys()].sort((a, b) => bookOrderMap.get(a) - bookOrderMap.get(b));
      setLxxAvailableBooks(otBooks);

      setLxxDataLoaded(true);
    } catch (err) {
      console.error('Error loading LXX data:', err);
    }
    setLxxDataLoading(false);
  };

  // Trigger LXX data load when any LXX menu is activated
  useEffect(() => {
    if (['lxxBible', 'lxxInterlinear', 'lxxSearchStrong', 'lxxSearchLemma', 'lxxVerseGrid', 'lxxStrongFrequency', 'lxxFrequencyOccurrences', 'lxxVerseTranslations'].includes(activeMenu) && !lxxDataLoaded) {
      loadLxxData();
    }
  }, [activeMenu, lxxDataLoaded]);

  // Update LXX available chapters when book changes (use acf_chapter for alignment)
  useEffect(() => {
    if (lxxVerseData.length > 0 && lxxSelectedBook) {
      const chapters = [...new Set(
        lxxVerseData
          .filter(row => row.book === lxxSelectedBook && row.acf_chapter)
          .map(row => row.acf_chapter)
      )].filter(Boolean).sort((a, b) => parseInt(a) - parseInt(b));
      setLxxAvailableChapters(chapters);
      if (!chapters.includes(lxxSelectedChapter) && chapters.length > 0) {
        setLxxSelectedChapter(chapters[0]);
      }
    }
  }, [lxxVerseData, lxxSelectedBook]);

  // Load LXX OT translations for Bible viewer
  const loadLxxBiblesTranslations = async () => {
    setLxxBiblesLoading(true);
    try {
      const newData = { ...lxxOtTranslations };
      // Build LXX verse lookup from lxxVerseData (keyed by acf_chapter/acf_verse for alignment)
      if (!newData['lxx']) {
        const lookup = {};
        lxxVerseData.forEach(row => {
          if (row.book && row.acf_chapter && row.acf_verse && row.verse_text) {
            const vk = `${row.book}_${row.acf_chapter}_${row.acf_verse}`;
            lookup[vk] = row.verse_text;
          }
        });
        newData['lxx'] = lookup;
      }
      // Load other OT translations
      for (const key of ['acf_ot', 'hebrew_bible', 'nkjv_ot', 'niv_ot', 'nvi_ot']) {
        if (!newData[key]) {
          newData[key] = await loadOtTranslation(key);
        }
      }
      setLxxOtTranslations(newData);
    } catch (err) {
      console.error('Error loading LXX Bible translations:', err);
    }
    setLxxBiblesLoading(false);
  };

  // Load an OT translation CSV file and build lookup map
  // Uses acf_chapter/acf_verse columns when available for alignment with ACF numbering
  const loadOtTranslation = async (translationKey) => {
    const response = await fetch(`/${translationKey}.csv`);
    if (!response.ok) throw new Error(`Failed to load ${translationKey}.csv`);
    const text = await response.text();
    const rows = parseCSV(text);
    if (rows.length === 0) return {};
    const headers = rows[0].map(h => h.trim().toLowerCase());
    const bookIdx = headers.indexOf('book');
    const acfChapterIdx = headers.indexOf('acf_chapter');
    const acfVerseIdx = headers.indexOf('acf_verse');
    const chapterIdx = acfChapterIdx >= 0 ? acfChapterIdx : headers.indexOf('chapter');
    const verseIdx = acfVerseIdx >= 0 ? acfVerseIdx : headers.indexOf('verse');
    const textIdx = headers.indexOf('verse_text');
    const lookup = {};
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (row.length > textIdx && textIdx >= 0) {
        const ch = (row[chapterIdx] || '').trim();
        const vs = (row[verseIdx] || '').trim();
        if (!ch || !vs) continue;
        const lk = `${(row[bookIdx] || '').trim()}_${ch}_${vs}`;
        lookup[lk] = row[textIdx];
      }
    }
    return lookup;
  };

  // Load LXX Bible translations when menu activates
  useEffect(() => {
    if (activeMenu === 'lxxBible' && lxxDataLoaded && Object.keys(lxxOtTranslations).length === 0) {
      loadLxxBiblesTranslations();
    }
  }, [activeMenu, lxxDataLoaded]);

  // Toggle LXX Bible translation selection
  const toggleLxxBiblesTranslation = (key) => {
    setLxxBiblesSelectedTranslations(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Compute LXX Bible verses for current book/chapter
  const lxxBiblesVerses = useMemo(() => {
    if (Object.keys(lxxOtTranslations).length === 0) return [];
    const verseSet = new Set();
    for (const tKey of Object.keys(lxxOtTranslations)) {
      const lookup = lxxOtTranslations[tKey];
      if (!lookup) continue;
      for (const key of Object.keys(lookup)) {
        const parts = key.split('_');
        if (parts[0] === lxxSelectedBook && parts[1] === lxxSelectedChapter) {
          verseSet.add(parts[2]);
        }
      }
    }
    return [...verseSet].sort((a, b) => parseInt(a) - parseInt(b));
  }, [lxxOtTranslations, lxxSelectedBook, lxxSelectedChapter]);

  // Compute LXX Interlinear data: group words by verse
  // Map from native LXX key (book_chapter_verse) → { acf_chapter, acf_verse }
  const lxxNativeToAcfMap = useMemo(() => {
    const map = new Map();
    lxxVerseData.forEach(row => {
      if (row.book && row.chapter && row.verse && row.acf_chapter && row.acf_verse) {
        map.set(`${row.book}_${row.chapter}_${row.verse}`, { acf_chapter: row.acf_chapter, acf_verse: row.acf_verse });
      }
    });
    return map;
  }, [lxxVerseData]);

  const lxxInterlinearVerses = useMemo(() => {
    if (lxxWordData.length === 0) return [];
    const chapterRows = lxxWordData.filter(
      row => row.book === lxxSelectedBook && row.chapter === lxxSelectedChapter
    );
    const verseMap = new Map();
    chapterRows.forEach(row => {
      const v = row.verse;
      if (!v) return;
      if (!verseMap.has(v)) verseMap.set(v, []);
      verseMap.get(v).push({
        text: row.greek_text || '',
        lemma: row.lemma || '',
        gloss: row.gloss || '',
        morphology: row.morphology || '',
        strong: row.strong || '',
        hebrew_strong: row.hebrew_strong || '',
        _fullRow: row,
      });
    });
    return [...verseMap.entries()]
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
      .map(([verseNum, words]) => {
        const nativeKey = `${lxxSelectedBook}_${lxxSelectedChapter}_${verseNum}`;
        const acf = lxxNativeToAcfMap.get(nativeKey);
        return { verseNum, words, acfChapter: acf ? acf.acf_chapter : '', acfVerse: acf ? acf.acf_verse : '' };
      });
  }, [lxxWordData, lxxSelectedBook, lxxSelectedChapter, lxxNativeToAcfMap]);

  // Unique LXX Strong numbers for search suggestions
  const lxxUniqueStrongs = useMemo(() => {
    return [...new Set(lxxWordData.map(row => row.strong).filter(Boolean))].sort();
  }, [lxxWordData]);

  const lxxStrongToLemmaMap = useMemo(() => {
    const map = new Map();
    lxxWordData.forEach(row => {
      if (row.strong && row.lemma && !map.has(row.strong)) {
        map.set(row.strong, row.lemma);
      }
    });
    return map;
  }, [lxxWordData]);

  // Normalize Greek text: remove accents and smooth breathing, but PRESERVE rough breathing (U+0314)
  // This way, plain vowels match any breathing, but ἁ specifically matches rough-breathing words.
  const normalizeGreek = (text) => {
    if (!text) return '';
    // NFD decomposes characters; remove all combining marks EXCEPT U+0314 (rough breathing / dasia)
    return text.normalize('NFD').replace(/[\u0300-\u0313\u0315-\u036f]/g, '').toLowerCase();
  };

  // Strip Hebrew niqqud (vowel points) and cantillation marks for normalized comparison
  const normalizeHebrew = (text) => {
    if (!text) return '';
    // Remove Hebrew combining characters: niqqud U+05B0–U+05C7, cantillation U+0591–U+05AF
    return text.normalize('NFD').replace(/[\u0591-\u05C7]/g, '');
  };

  // Unique LXX lemmas for lemma search suggestions
  const lxxUniqueLemmas = useMemo(() => {
    return [...new Set(lxxWordData.map(row => row.lemma).filter(Boolean))].sort();
  }, [lxxWordData]);

  // Map lemma -> first gloss found
  const lxxLemmaToGlossMap = useMemo(() => {
    const map = new Map();
    lxxWordData.forEach(row => {
      if (row.lemma && row.gloss && !map.has(row.lemma)) map.set(row.lemma, row.gloss);
    });
    return map;
  }, [lxxWordData]);

  // LXX Search Lemma suggestions (with diacritic normalization)
  const lxxLemmaSearchSuggestions = useMemo(() => {
    if (!lxxLemmaSearchText) return [];
    const normalizedSearch = normalizeGreek(lxxLemmaSearchText);
    return lxxUniqueLemmas.filter(val => normalizeGreek(val).includes(normalizedSearch)).slice(0, 50);
  }, [lxxLemmaSearchText, lxxUniqueLemmas]);

  // LXX Search Lemma: find unique verses containing the selected lemma
  const lxxLemmaSearchResults = useMemo(() => {
    if (!lxxLemmaSelectedValue) return [];
    const verseKeys = new Set();
    lxxWordData.forEach(row => {
      if (row.lemma === lxxLemmaSelectedValue) {
        verseKeys.add(`${row.book}_${row.chapter}_${row.verse}`);
      }
    });
    return [...verseKeys].map(vk => {
      const [book, chapter, verse] = vk.split('_');
      return { book, chapter, verse, key: vk };
    }).sort((a, b) => {
      const bookOrderMap = new Map();
      lxxVerseData.forEach(row => {
        if (!bookOrderMap.has(row.book)) bookOrderMap.set(row.book, parseInt(row.book_order) || 0);
      });
      const bookDiff = (bookOrderMap.get(a.book) || 0) - (bookOrderMap.get(b.book) || 0);
      if (bookDiff !== 0) return bookDiff;
      const chDiff = parseInt(a.chapter) - parseInt(b.chapter);
      if (chDiff !== 0) return chDiff;
      return parseInt(a.verse) - parseInt(b.verse);
    });
  }, [lxxWordData, lxxVerseData, lxxLemmaSelectedValue]);

  // LXX Search Strong suggestions
  const lxxSearchSuggestions = useMemo(() => {
    if (!lxxSearchText) return [];
    const lowerSearch = lxxSearchText.toLowerCase();
    return lxxUniqueStrongs.filter(val => val.toLowerCase().includes(lowerSearch)).slice(0, 50);
  }, [lxxSearchText, lxxUniqueStrongs]);

  // LXX Search Strong: find verses containing the selected strong number
  const lxxSearchResults = useMemo(() => {
    if (!lxxSearchSelectedValue) return [];
    // Get unique verse keys where this strong appears
    const verseKeys = new Set();
    lxxWordData.forEach(row => {
      if (row.strong === lxxSearchSelectedValue) {
        verseKeys.add(`${row.book}_${row.chapter}_${row.verse}`);
      }
    });
    return [...verseKeys].map(vk => {
      const [book, chapter, verse] = vk.split('_');
      return { book, chapter, verse, key: vk };
    }).sort((a, b) => {
      const bookOrderMap = new Map();
      lxxVerseData.forEach(row => {
        if (!bookOrderMap.has(row.book)) bookOrderMap.set(row.book, parseInt(row.book_order) || 0);
      });
      const bookDiff = (bookOrderMap.get(a.book) || 0) - (bookOrderMap.get(b.book) || 0);
      if (bookDiff !== 0) return bookDiff;
      const chDiff = parseInt(a.chapter) - parseInt(b.chapter);
      if (chDiff !== 0) return chDiff;
      return parseInt(a.verse) - parseInt(b.verse);
    });
  }, [lxxWordData, lxxVerseData, lxxSearchSelectedValue]);

  // Load search translations when LXX search strong is selected
  const loadLxxSearchTranslations = async () => {
    if (lxxSearchTranslationsLoading) return;
    setLxxSearchTranslationsLoading(true);
    try {
      const newData = { ...lxxSearchTranslationsData };
      // Build LXX lookup
      if (!newData['lxx'] && lxxVerseData.length > 0) {
        const lookup = {};
        lxxVerseData.forEach(row => {
          if (row.book && row.chapter && row.verse && row.verse_text) {
            lookup[`${row.book}_${row.chapter}_${row.verse}`] = row.verse_text;
          }
        });
        newData['lxx'] = lookup;
      }
      for (const key of ['acf_ot', 'hebrew_bible', 'nkjv_ot', 'niv_ot', 'nvi_ot']) {
        if (!newData[key]) {
          newData[key] = await loadOtTranslation(key);
        }
      }
      setLxxSearchTranslationsData(newData);
    } catch (err) {
      console.error('Error loading LXX search translations:', err);
    }
    setLxxSearchTranslationsLoading(false);
  };

  // Load search translations when a strong or lemma is selected
  useEffect(() => {
    if (activeMenu === 'lxxSearchStrong' && lxxSearchSelectedValue && Object.keys(lxxSearchTranslationsData).length === 0) {
      loadLxxSearchTranslations();
    }
    if (activeMenu === 'lxxSearchLemma' && lxxLemmaSelectedValue && Object.keys(lxxSearchTranslationsData).length === 0) {
      loadLxxSearchTranslations();
    }
  }, [activeMenu, lxxSearchSelectedValue, lxxLemmaSelectedValue]);

  // LXX Search Strong handlers
  const handleLxxSearchClear = () => {
    setLxxSearchText('');
    setLxxSearchSelectedValue(null);
  };

  const toggleLxxSearchTranslation = (key) => {
    setLxxSearchSelectedTranslations(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleLxxSelectSuggestion = (value) => {
    setLxxSearchText(value);
    setLxxSearchSelectedValue(value);
  };

  // LXX Search Lemma handlers
  const handleLxxLemmaSearchClear = () => {
    setLxxLemmaSearchText('');
    setLxxLemmaSelectedValue(null);
  };

  const handleLxxLemmaBackspace = () => {
    setLxxLemmaSearchText(prev => prev.slice(0, -1));
    setLxxLemmaSelectedValue(null);
  };

  const handleLxxLemmaSelectSuggestion = (value) => {
    setLxxLemmaSearchText(value);
    setLxxLemmaSelectedValue(value);
  };

  const handleLxxLemmaGreekKey = (char) => {
    setLxxLemmaSearchText(prev => prev + char);
    setLxxLemmaSelectedValue(null);
  };

  // LXX Verse Grid: filtered data for current book/chapter
  const lxxNativeAvailableChapters = useMemo(() => {
    if (!lxxWordData.length || !lxxSelectedBook) return [];
    return [...new Set(
      lxxWordData.filter(r => r.book === lxxSelectedBook).map(r => r.chapter)
    )].filter(Boolean).sort((a, b) => parseInt(a) - parseInt(b));
  }, [lxxWordData, lxxSelectedBook]);

  const lxxGridDisplayChapters = lxxGridNumberingMode === 'lxx' ? lxxNativeAvailableChapters : lxxAvailableChapters;

  const lxxGridFilteredData = useMemo(() => {
    if (lxxGridNumberingMode === 'lxx') {
      return lxxWordData.filter(row =>
        row.book === lxxSelectedBook && row.chapter === lxxGridChapter
      );
    } else {
      // ACF mode: find all native LXX chapters that correspond to this ACF chapter
      const nativeChapters = new Set(
        lxxVerseData
          .filter(r => r.book === lxxSelectedBook && r.acf_chapter === lxxGridChapter)
          .map(r => r.chapter)
      );
      return lxxWordData.filter(row =>
        row.book === lxxSelectedBook && nativeChapters.has(row.chapter)
      );
    }
  }, [lxxWordData, lxxVerseData, lxxSelectedBook, lxxGridChapter, lxxGridNumberingMode]);

  // Toggle LXX grid column
  const toggleLxxGridColumn = (column) => {
    setLxxGridSelectedColumns(prev =>
      prev.includes(column) ? prev.filter(c => c !== column) : [...prev, column]
    );
  };

  // Unique verses in the LXX that contain the selected lemma (for View Verse Translations drill-down)
  const lxxVerseTranslationVerses = useMemo(() => {
    if (!lxxVerseTranslationsLemma || !lxxWordData.length) return [];
    const verseKeys = new Set();
    lxxWordData.forEach(row => {
      if (row.lemma === lxxVerseTranslationsLemma) {
        verseKeys.add(`${row.book}_${row.chapter}_${row.verse}`);
      }
    });
    const bookOrderMap = new Map();
    lxxVerseData.forEach(row => {
      if (row.book && !bookOrderMap.has(row.book)) bookOrderMap.set(row.book, parseInt(row.book_order) || 0);
    });
    return [...verseKeys].map(vk => {
      const [book, chapter, verse] = vk.split('_');
      return { book, chapter, verse, key: vk };
    }).sort((a, b) => {
      const bookDiff = (bookOrderMap.get(a.book) || 0) - (bookOrderMap.get(b.book) || 0);
      if (bookDiff !== 0) return bookDiff;
      const chDiff = parseInt(a.chapter) - parseInt(b.chapter);
      if (chDiff !== 0) return chDiff;
      return parseInt(a.verse) - parseInt(b.verse);
    });
  }, [lxxWordData, lxxVerseData, lxxVerseTranslationsLemma]);

  const toggleLxxVerseTranslation = (key) => {
    setLxxVerseTranslationsSelectedTranslations(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Sync lxxGridChapter when book or numbering mode changes
  useEffect(() => {
    const chaps = lxxGridNumberingMode === 'lxx' ? lxxNativeAvailableChapters : lxxAvailableChapters;
    if (chaps.length > 0 && !chaps.includes(lxxGridChapter)) {
      setLxxGridChapter(chaps[0]);
    }
  }, [lxxSelectedBook, lxxGridNumberingMode, lxxNativeAvailableChapters, lxxAvailableChapters]);

  // LXX Strong Frequency data — operates on pre-aggregated lxxStrongGrouped (~10k-20k entries)
  // instead of lxxWordData (587k rows), so filter changes are fast and non-blocking
  const lxxStrongFrequencyData = useMemo(() => {
    if (!lxxStrongGrouped) return [];
    const hasFilter = lxxSelectedClassFilter || lxxSelectedGenderFilter || lxxSelectedCaseFilter ||
                      lxxSelectedNumberFilter || lxxSelectedTenseFilter || lxxSelectedVoiceFilter ||
                      lxxSelectedMoodFilter || lxxSelectedPersonFilter;
    const setToStr = arr => [...arr].sort().join(', ');
    const result = [];

    for (const [strong, info] of Object.entries(lxxStrongGrouped)) {
      let matchedRows;
      if (lxxSelectedClassFilter) {
        // Only look at the bucket for the selected class
        const bucket = info.classBuckets[lxxSelectedClassFilter] || [];
        matchedRows = bucket.filter(r => {
          if (lxxSelectedGenderFilter && r.gender !== lxxSelectedGenderFilter) return false;
          if (lxxSelectedCaseFilter && r.case !== lxxSelectedCaseFilter) return false;
          if (lxxSelectedNumberFilter && r.number !== lxxSelectedNumberFilter) return false;
          if (lxxSelectedTenseFilter && r.tense !== lxxSelectedTenseFilter) return false;
          if (lxxSelectedVoiceFilter && r.voice !== lxxSelectedVoiceFilter) return false;
          if (lxxSelectedMoodFilter && r.mood !== lxxSelectedMoodFilter) return false;
          if (lxxSelectedPersonFilter && r.person !== lxxSelectedPersonFilter) return false;
          return true;
        });
      } else {
        // No class filter — check all buckets
        matchedRows = [];
        for (const rows of Object.values(info.classBuckets)) {
          for (const r of rows) {
            if (lxxSelectedGenderFilter && r.gender !== lxxSelectedGenderFilter) continue;
            if (lxxSelectedCaseFilter && r.case !== lxxSelectedCaseFilter) continue;
            if (lxxSelectedNumberFilter && r.number !== lxxSelectedNumberFilter) continue;
            if (lxxSelectedTenseFilter && r.tense !== lxxSelectedTenseFilter) continue;
            if (lxxSelectedVoiceFilter && r.voice !== lxxSelectedVoiceFilter) continue;
            if (lxxSelectedMoodFilter && r.mood !== lxxSelectedMoodFilter) continue;
            if (lxxSelectedPersonFilter && r.person !== lxxSelectedPersonFilter) continue;
            matchedRows.push(r);
          }
        }
      }

      if (hasFilter && matchedRows.length === 0) continue;

      // Collect unique field values from matched rows
      const classes = lxxSelectedClassFilter
        ? new Set([lxxSelectedClassFilter])
        : new Set(Object.keys(info.classBuckets).filter(Boolean));
      const genders = new Set(), cases = new Set(), numbers = new Set();
      const tenses = new Set(), voices = new Set(), moods = new Set(), persons = new Set();
      const useRows = hasFilter ? matchedRows : Object.values(info.classBuckets).flat();
      useRows.forEach(r => {
        if (r.gender) genders.add(r.gender);
        if (r.case) cases.add(r.case);
        if (r.number) numbers.add(r.number);
        if (r.tense) tenses.add(r.tense);
        if (r.voice) voices.add(r.voice);
        if (r.mood) moods.add(r.mood);
        if (r.person) persons.add(r.person);
      });

      result.push({
        strong,
        frequency: hasFilter ? matchedRows.length : Object.values(info.classBuckets).reduce((s, b) => s + b.length, 0),
        class: setToStr([...classes]),
        gender: setToStr([...genders]),
        case: setToStr([...cases]),
        number: setToStr([...numbers]),
        tense: setToStr([...tenses]),
        voice: setToStr([...voices]),
        mood: setToStr([...moods]),
        person: setToStr([...persons]),
        lemma: info.lemma,
        gloss: info.gloss,
        hebrew_strong: info.hebrew_strong
      });
    }

    result.sort((a, b) => b.frequency - a.frequency);
    return result;
  }, [lxxStrongGrouped, lxxSelectedClassFilter, lxxSelectedGenderFilter, lxxSelectedCaseFilter, lxxSelectedNumberFilter, lxxSelectedTenseFilter, lxxSelectedVoiceFilter, lxxSelectedMoodFilter, lxxSelectedPersonFilter]);

  // LXX Frequency Occurrences (drill-down)
  const lxxFrequencyOccurrences = useMemo(() => {
    if (!lxxFrequencyStrongNumber) return [];
    return lxxWordData.filter(row => {
      if (row.strong !== lxxFrequencyStrongNumber) return false;
      if (lxxSelectedClassFilter && row.class !== lxxSelectedClassFilter) return false;
      if (lxxSelectedGenderFilter && row.gender !== lxxSelectedGenderFilter) return false;
      if (lxxSelectedCaseFilter && row.case !== lxxSelectedCaseFilter) return false;
      if (lxxSelectedNumberFilter && row.number !== lxxSelectedNumberFilter) return false;
      if (lxxSelectedTenseFilter && row.tense !== lxxSelectedTenseFilter) return false;
      if (lxxSelectedVoiceFilter && row.voice !== lxxSelectedVoiceFilter) return false;
      if (lxxSelectedMoodFilter && row.mood !== lxxSelectedMoodFilter) return false;
      if (lxxSelectedPersonFilter && row.person !== lxxSelectedPersonFilter) return false;
      return true;
    });
  }, [lxxWordData, lxxFrequencyStrongNumber, lxxSelectedClassFilter, lxxSelectedGenderFilter, lxxSelectedCaseFilter, lxxSelectedNumberFilter, lxxSelectedTenseFilter, lxxSelectedVoiceFilter, lxxSelectedMoodFilter, lxxSelectedPersonFilter]);

  // Handle "See Occurrences" from LXX Strong Frequency page
  const handleLxxSeeOccurrences = (strongNumber) => {
    setLxxFrequencyStrongNumber(strongNumber);
    setActiveMenu('lxxFrequencyOccurrences');
  };

  // Handle "See Occurrences" from LXX Verse Grid (by strong number)
  const handleLxxGridSeeOccurrences = (strongNumber) => {
    setLxxFrequencyStrongNumber(strongNumber);
    setActiveMenu('lxxFrequencyOccurrences');
  };

  // Handle "View Verse Translations" from LXX Verse Grid (by lemma)
  const handleLxxViewVerseTranslations = (lemma) => {
    setLxxVerseTranslationsLemma(lemma);
    if (Object.keys(lxxSearchTranslationsData).length === 0) {
      loadLxxSearchTranslations();
    }
    setActiveMenu('lxxVerseTranslations');
  };

  // ==================== HEBREW DATA LOADING ====================
  const HEB_BIBLE_TRANSLATIONS = [
    { key: 'hebrew_bible', name: 'Hebrew', language: 'Hebrew' },
    { key: 'lxx', name: 'LXX', language: 'Greek' },
    { key: 'nkjv_ot', name: 'NKJV', language: 'English' },
    { key: 'niv_ot', name: 'NIV', language: 'English' },
    { key: 'acf_ot', name: 'ACF', language: 'Portuguese' },
    { key: 'nvi_ot', name: 'NVI', language: 'Portuguese' },
  ];

  const HEB_SEARCH_TRANSLATIONS = [
    { key: 'hebrew_bible', name: 'Hebrew', language: 'Hebrew' },
    { key: 'lxx', name: 'LXX', language: 'Greek' },
    { key: 'nkjv_ot', name: 'NKJV', language: 'English' },
    { key: 'niv_ot', name: 'NIV', language: 'English' },
    { key: 'acf_ot', name: 'ACF', language: 'Portuguese' },
    { key: 'nvi_ot', name: 'NVI', language: 'Portuguese' },
  ];

  // Load Hebrew data when any Hebrew menu is activated
  const loadHebData = async () => {
    if (hebDataLoaded || hebDataLoading) return;
    setHebDataLoading(true);
    try {
      // Load macula-hebrew.tsv (49 MB — chunked async parse keeps UI responsive)
      const wordRes = await fetch('/macula-hebrew.tsv');
      const wordText = await wordRes.text();
      const { headers: wordHeaders, rows: parsedWords } = await parseFileAsync(wordText, '\t');
      setHebWordData(parsedWords);

      // Pre-aggregate Hebrew Strong Frequency data in chunked passes
      const hfv = { classes: new Set(), stems: new Set(), poses: new Set(), persons: new Set(), genders: new Set(), numbers: new Set(), states: new Set(), types: new Set() };
      const hgrouped = {};
      const AGG_CHUNK = 20000;
      for (let start = 0; start < parsedWords.length; start += AGG_CHUNK) {
        const end = Math.min(start + AGG_CHUNK, parsedWords.length);
        for (let i = start; i < end; i++) {
          const row = parsedWords[i];
          const s = row.strongnumberx;
          if (row.class) hfv.classes.add(row.class);
          if (row.stem) hfv.stems.add(row.stem);
          if (row.pos) hfv.poses.add(row.pos);
          if (row.person) hfv.persons.add(row.person);
          if (row.gender) hfv.genders.add(row.gender);
          if (row.number) hfv.numbers.add(row.number);
          if (row.state) hfv.states.add(row.state);
          if (row.type) hfv.types.add(row.type);
          if (!s) continue;
          if (!hgrouped[s]) {
            hgrouped[s] = { lemma: row.stronglemma || '', gloss: row.english || '', classBuckets: {} };
          }
          const cls = row.class || '';
          if (!hgrouped[s].classBuckets[cls]) hgrouped[s].classBuckets[cls] = [];
          hgrouped[s].classBuckets[cls].push({
            stem: row.stem || '', pos: row.pos || '', person: row.person || '',
            gender: row.gender || '', number: row.number || '', state: row.state || '', type: row.type || ''
          });
        }
        await new Promise(r => setTimeout(r, 0));
      }
      setHebFilterValues({
        classes: [...hfv.classes].sort(), stems: [...hfv.stems].sort(),
        poses: [...hfv.poses].sort(), persons: [...hfv.persons].sort(),
        genders: [...hfv.genders].sort(), numbers: [...hfv.numbers].sort(),
        states: [...hfv.states].sort(), types: [...hfv.types].sort()
      });
      setHebStrongGrouped(hgrouped);

      // Extract unique OT books in order of appearance
      const bookOrderMap = new Map();
      let bookOrder = 0;
      parsedWords.forEach(row => {
        if (row.book && !bookOrderMap.has(row.book)) {
          bookOrderMap.set(row.book, bookOrder++);
        }
      });
      const otBooks = [...bookOrderMap.keys()].sort((a, b) => bookOrderMap.get(a) - bookOrderMap.get(b));
      setHebAvailableBooks(otBooks);

      // Set grid columns from word data
      setHebGridColumns(wordHeaders);
      const defaultGridCols = ['book', 'chapter', 'verse', 'text', 'strongnumberx', 'gloss', 'morph', 'lemma', 'class', 'person', 'gender', 'number'];
      setHebGridSelectedColumns(defaultGridCols.filter(c => wordHeaders.includes(c)));

      // Load hebrew_bible.csv to build MT → ACF verse alignment maps
      const hebBibleRes = await fetch('/hebrew_bible.csv');
      const hebBibleText = await hebBibleRes.text();
      const hebBibleRows = parseCSV(hebBibleText);
      if (hebBibleRows.length > 0) {
        const hbHeaders = hebBibleRows[0].map(h => h.trim().toLowerCase());
        const hbBookIdx  = hbHeaders.indexOf('book');
        const hbChapIdx  = hbHeaders.indexOf('chapter');
        const hbVsIdx    = hbHeaders.indexOf('verse');
        const hbAcfChIdx = hbHeaders.indexOf('acf_chapter');
        const hbAcfVsIdx = hbHeaders.indexOf('acf_verse');
        const mtToAcf = {};
        const acfToMtChs = {}; // "book_acfCh" -> Set<mtCh>
        for (let i = 1; i < hebBibleRows.length; i++) {
          const r = hebBibleRows[i];
          const book  = (r[hbBookIdx]  || '').trim();
          const mtCh  = (r[hbChapIdx]  || '').trim();
          const mtVs  = (r[hbVsIdx]    || '').trim();
          const acfCh = (r[hbAcfChIdx] || '').trim();
          const acfVs = (r[hbAcfVsIdx] || '').trim();
          if (book && mtCh && mtVs) {
            mtToAcf[`${book}_${mtCh}_${mtVs}`] = { acf_chapter: acfCh, acf_verse: acfVs };
          }
          if (book && acfCh) {
            const mk = `${book}_${acfCh}`;
            if (!acfToMtChs[mk]) acfToMtChs[mk] = new Set();
            acfToMtChs[mk].add(mtCh);
          }
        }
        const acfToMtChsResolved = {};
        for (const k of Object.keys(acfToMtChs)) {
          acfToMtChsResolved[k] = [...acfToMtChs[k]];
        }
        setHebMtToAcfMap(mtToAcf);
        setHebAcfToMtChapters(acfToMtChsResolved);
      }

      setHebDataLoaded(true);
    } catch (err) {
      console.error('Error loading Hebrew data:', err);
    }
    setHebDataLoading(false);
  };

  // Trigger Hebrew data load when any Hebrew menu is activated
  useEffect(() => {
    if (['hebrewMT', 'hebrewInterlinear', 'hebrewSearchStrong', 'hebrewSearchLemma', 'hebrewVerseGrid', 'hebrewStrongFrequency', 'hebrewFrequencyOccurrences'].includes(activeMenu) && !hebDataLoaded) {
      loadHebData();
    }
  }, [activeMenu, hebDataLoaded]);

  // Update Hebrew available chapters when book changes (use ACF chapters for alignment)
  useEffect(() => {
    if (Object.keys(hebAcfToMtChapters).length > 0 && hebSelectedBook) {
      const prefix = `${hebSelectedBook}_`;
      const chapters = Object.keys(hebAcfToMtChapters)
        .filter(k => k.startsWith(prefix))
        .map(k => k.slice(prefix.length))
        .sort((a, b) => parseInt(a) - parseInt(b));
      setHebAvailableChapters(chapters);
      if (!chapters.includes(hebSelectedChapter) && chapters.length > 0) {
        setHebSelectedChapter(chapters[0]);
      }
    }
  }, [hebAcfToMtChapters, hebSelectedBook]);

  // Load Hebrew Bible translations for Bible viewer
  const loadHebBiblesTranslations = async () => {
    setHebBiblesLoading(true);
    try {
      const newData = { ...hebOtTranslations };
      if (!newData['hebrew_bible']) {
        newData['hebrew_bible'] = await loadOtTranslation('hebrew_bible');
      }
      // Build LXX lookup from LXX_Rahlfs_by_verse.csv
      if (!newData['lxx']) {
        if (lxxVerseData.length > 0) {
          const lookup = {};
          lxxVerseData.forEach(row => {
            if (row.book && row.acf_chapter && row.acf_verse && row.verse_text) {
              lookup[`${row.book}_${row.acf_chapter}_${row.acf_verse}`] = row.verse_text;
            }
          });
          newData['lxx'] = lookup;
        } else {
          const verseRes = await fetch('/LXX_Rahlfs_by_verse.csv');
          const verseText = await verseRes.text();
          const verseRows = parseCSV(verseText);
          const verseHeaders = verseRows[0].map(h => h.trim().toLowerCase());
          const lookup = {};
          for (let i = 1; i < verseRows.length; i++) {
            const row = {};
            verseHeaders.forEach((h, idx) => { row[h] = (verseRows[i][idx] || '').trim(); });
            if (row.book && row.acf_chapter && row.acf_verse && row.verse_text) {
              lookup[`${row.book}_${row.acf_chapter}_${row.acf_verse}`] = row.verse_text;
            }
          }
          newData['lxx'] = lookup;
        }
      }
      for (const key of ['acf_ot', 'nkjv_ot', 'niv_ot', 'nvi_ot']) {
        if (!newData[key]) {
          newData[key] = await loadOtTranslation(key);
        }
      }
      setHebOtTranslations(newData);
    } catch (err) {
      console.error('Error loading Hebrew Bible translations:', err);
    }
    setHebBiblesLoading(false);
  };

  // Load Hebrew Bible translations when menu activates
  useEffect(() => {
    if (activeMenu === 'hebrewMT' && hebDataLoaded && Object.keys(hebOtTranslations).length === 0) {
      loadHebBiblesTranslations();
    }
  }, [activeMenu, hebDataLoaded]);

  // Toggle Hebrew Bible translation selection
  const toggleHebBiblesTranslation = (key) => {
    setHebBiblesSelectedTranslations(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Compute Hebrew Bible verses for current book/chapter
  const hebBiblesVerses = useMemo(() => {
    if (Object.keys(hebOtTranslations).length === 0) return [];
    const verseSet = new Set();
    for (const tKey of Object.keys(hebOtTranslations)) {
      const lookup = hebOtTranslations[tKey];
      if (!lookup) continue;
      for (const key of Object.keys(lookup)) {
        const parts = key.split('_');
        if (parts[0] === hebSelectedBook && parts[1] === hebSelectedChapter) {
          verseSet.add(parts[2]);
        }
      }
    }
    return [...verseSet].sort((a, b) => parseInt(a) - parseInt(b));
  }, [hebOtTranslations, hebSelectedBook, hebSelectedChapter]);

  // Compute Hebrew Interlinear data: group words by verse
  const hebInterlinearVerses = useMemo(() => {
    if (hebWordData.length === 0 || Object.keys(hebMtToAcfMap).length === 0) return [];
    const mtChapters = new Set(hebAcfToMtChapters[`${hebSelectedBook}_${hebSelectedChapter}`] || []);
    const chapterRows = hebWordData.filter(
      row => row.book === hebSelectedBook && mtChapters.has(row.chapter)
    );
    const verseMap = new Map();
    chapterRows.forEach(row => {
      const mapping = hebMtToAcfMap[`${row.book}_${row.chapter}_${row.verse}`];
      const acfVerse = mapping ? mapping.acf_verse : null;
      if (!acfVerse) return; // skip title/superscription verses with no ACF equivalent
      if (!verseMap.has(acfVerse)) verseMap.set(acfVerse, []);
      verseMap.get(acfVerse).push({
        text: row.text || '',
        transliteration: row.transliteration || '',
        lemma: row.lemma || '',
        gloss: row.english || '',
        english: row.english || '',
        morph: row.morph || '',
        strong: row.strongnumberx || '',
        frequency: row.frequency || '',
        _fullRow: row,
      });
    });
    return [...verseMap.entries()]
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
      .map(([verseNum, words]) => ({ verseNum, words }));
  }, [hebWordData, hebMtToAcfMap, hebAcfToMtChapters, hebSelectedBook, hebSelectedChapter]);

  // Unique Hebrew Strong numbers for search suggestions
  const hebUniqueStrongs = useMemo(() => {
    return [...new Set(hebWordData.map(row => row.strongnumberx).filter(Boolean))].sort();
  }, [hebWordData]);

  const hebStrongToLemmaMap = useMemo(() => {
    const map = new Map();
    hebWordData.forEach(row => {
      if (row.strongnumberx && row.lemma && !map.has(row.strongnumberx)) {
        map.set(row.strongnumberx, row.lemma);
      }
    });
    return map;
  }, [hebWordData]);

  const hebStrongToGlossMap = useMemo(() => {
    const map = new Map();
    hebWordData.forEach(row => {
      if (row.strongnumberx && row.english && !map.has(row.strongnumberx)) {
        map.set(row.strongnumberx, row.english);
      }
    });
    return map;
  }, [hebWordData]);

  // Hebrew unique lemmas for lemma search suggestions
  const hebUniqueLemmas = useMemo(() => {
    return [...new Set(hebWordData.map(row => row.lemma).filter(Boolean))].sort();
  }, [hebWordData]);

  // Map lemma -> first gloss found
  const hebLemmaToEnglishMap = useMemo(() => {
    const map = new Map();
    hebWordData.forEach(row => {
      if (row.lemma && row.english && !map.has(row.lemma)) map.set(row.lemma, row.english);
    });
    return map;
  }, [hebWordData]);

  // Hebrew Search Lemma suggestions (with niqqud normalization)
  const hebLemmaSearchSuggestions = useMemo(() => {
    if (!hebLemmaSearchText) return [];
    const normalizedSearch = normalizeHebrew(hebLemmaSearchText);
    return hebUniqueLemmas.filter(val => normalizeHebrew(val).includes(normalizedSearch)).slice(0, 50);
  }, [hebLemmaSearchText, hebUniqueLemmas]);

  // Hebrew Search Lemma: find unique verses containing the selected lemma (ACF-aligned)
  const hebLemmaSearchResults = useMemo(() => {
    if (!hebLemmaSelectedValue || Object.keys(hebMtToAcfMap).length === 0) return [];
    const acfKeyMap = new Map();
    hebWordData.forEach(row => {
      if (row.lemma === hebLemmaSelectedValue) {
        const mapping = hebMtToAcfMap[`${row.book}_${row.chapter}_${row.verse}`];
        if (mapping && mapping.acf_chapter && mapping.acf_verse) {
          const acfKey = `${row.book}_${mapping.acf_chapter}_${mapping.acf_verse}`;
          if (!acfKeyMap.has(acfKey)) {
            acfKeyMap.set(acfKey, { book: row.book, chapter: mapping.acf_chapter, verse: mapping.acf_verse });
          }
        }
      }
    });
    const bookOrderMap = new Map();
    hebWordData.forEach(row => {
      if (!bookOrderMap.has(row.book)) bookOrderMap.set(row.book, hebAvailableBooks.indexOf(row.book));
    });
    return [...acfKeyMap.entries()].map(([key, ref]) => ({ ...ref, key })).sort((a, b) => {
      const bookDiff = (bookOrderMap.get(a.book) || 0) - (bookOrderMap.get(b.book) || 0);
      if (bookDiff !== 0) return bookDiff;
      const chDiff = parseInt(a.chapter) - parseInt(b.chapter);
      if (chDiff !== 0) return chDiff;
      return parseInt(a.verse) - parseInt(b.verse);
    });
  }, [hebWordData, hebMtToAcfMap, hebLemmaSelectedValue, hebAvailableBooks]);

  // Hebrew Search Strong suggestions
  const hebSearchSuggestions = useMemo(() => {
    if (!hebSearchText) return [];
    const lowerSearch = hebSearchText.toLowerCase();
    return hebUniqueStrongs.filter(val => val.toLowerCase().includes(lowerSearch)).slice(0, 50);
  }, [hebSearchText, hebUniqueStrongs]);

  // Hebrew Search Strong: find verses containing the selected strong number
  // Keys are ACF-aligned so translation lookups match
  const hebSearchResults = useMemo(() => {
    if (!hebSearchSelectedValue || Object.keys(hebMtToAcfMap).length === 0) return [];
    const acfKeyMap = new Map(); // acfKey -> {book, chapter (acf), verse (acf)}
    hebWordData.forEach(row => {
      if (row.strongnumberx === hebSearchSelectedValue) {
        const mapping = hebMtToAcfMap[`${row.book}_${row.chapter}_${row.verse}`];
        if (mapping && mapping.acf_chapter && mapping.acf_verse) {
          const acfKey = `${row.book}_${mapping.acf_chapter}_${mapping.acf_verse}`;
          if (!acfKeyMap.has(acfKey)) {
            acfKeyMap.set(acfKey, { book: row.book, chapter: mapping.acf_chapter, verse: mapping.acf_verse });
          }
        }
      }
    });
    const bookOrderMap = new Map();
    hebWordData.forEach(row => {
      if (!bookOrderMap.has(row.book)) bookOrderMap.set(row.book, hebAvailableBooks.indexOf(row.book));
    });
    return [...acfKeyMap.entries()].map(([key, ref]) => ({ ...ref, key })).sort((a, b) => {
      const bookDiff = (bookOrderMap.get(a.book) || 0) - (bookOrderMap.get(b.book) || 0);
      if (bookDiff !== 0) return bookDiff;
      const chDiff = parseInt(a.chapter) - parseInt(b.chapter);
      if (chDiff !== 0) return chDiff;
      return parseInt(a.verse) - parseInt(b.verse);
    });
  }, [hebWordData, hebMtToAcfMap, hebSearchSelectedValue, hebAvailableBooks]);

  // Load search translations when Hebrew search strong is selected
  const loadHebSearchTranslations = async () => {
    if (hebSearchTranslationsLoading) return;
    setHebSearchTranslationsLoading(true);
    try {
      const newData = { ...hebSearchTranslationsData };
      if (!newData['hebrew_bible']) {
        newData['hebrew_bible'] = await loadOtTranslation('hebrew_bible');
      }
      // Build LXX lookup keyed by acf_chapter/acf_verse for alignment with ACF numbering
      if (!newData['lxx']) {
        if (lxxVerseData.length > 0) {
          const lookup = {};
          lxxVerseData.forEach(row => {
            if (row.book && row.acf_chapter && row.acf_verse && row.verse_text) {
              lookup[`${row.book}_${row.acf_chapter}_${row.acf_verse}`] = row.verse_text;
            }
          });
          newData['lxx'] = lookup;
        } else {
          const verseRes = await fetch('/LXX_Rahlfs_by_verse.csv');
          const verseText = await verseRes.text();
          const verseRows = parseCSV(verseText);
          const verseHeaders = verseRows[0].map(h => h.trim().toLowerCase());
          const lookup = {};
          for (let i = 1; i < verseRows.length; i++) {
            const row = {};
            verseHeaders.forEach((h, idx) => { row[h] = (verseRows[i][idx] || '').trim(); });
            if (row.book && row.acf_chapter && row.acf_verse && row.verse_text) {
              lookup[`${row.book}_${row.acf_chapter}_${row.acf_verse}`] = row.verse_text;
            }
          }
          newData['lxx'] = lookup;
        }
      }
      for (const key of ['acf_ot', 'nkjv_ot', 'niv_ot', 'nvi_ot']) {
        if (!newData[key]) {
          newData[key] = await loadOtTranslation(key);
        }
      }
      setHebSearchTranslationsData(newData);
    } catch (err) {
      console.error('Error loading Hebrew search translations:', err);
    }
    setHebSearchTranslationsLoading(false);
  };

  // Load search translations when a strong is selected
  useEffect(() => {
    if (activeMenu === 'hebrewSearchStrong' && hebSearchSelectedValue && Object.keys(hebSearchTranslationsData).length === 0) {
      loadHebSearchTranslations();
    }
  }, [activeMenu, hebSearchSelectedValue]);

  // Hebrew Search Strong handlers
  const handleHebSearchClear = () => {
    setHebSearchText('');
    setHebSearchSelectedValue(null);
  };

  const toggleHebSearchTranslation = (key) => {
    setHebSearchSelectedTranslations(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleHebSelectSuggestion = (value) => {
    setHebSearchText(value);
    setHebSearchSelectedValue(value);
  };

  // Hebrew Search Lemma handlers
  const handleHebLemmaSearchClear = () => {
    setHebLemmaSearchText('');
    setHebLemmaSelectedValue(null);
  };

  const handleHebLemmaBackspace = () => {
    setHebLemmaSearchText(prev => prev.slice(0, -1));
    setHebLemmaSelectedValue(null);
  };

  const handleHebLemmaSelectSuggestion = (value) => {
    setHebLemmaSearchText(value);
    setHebLemmaSelectedValue(value);
  };

  const handleHebLemmaKey = (char) => {
    setHebLemmaSearchText(prev => prev + char);
    setHebLemmaSelectedValue(null);
  };

  const toggleHebLemmaSearchTranslation = (key) => {
    setHebLemmaSearchSelectedTranslations(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Load translations for Hebrew Lemma search
  const loadHebLemmaSearchTranslations = async () => {
    if (hebLemmaSearchTranslationsLoading) return;
    setHebLemmaSearchTranslationsLoading(true);
    try {
      const newData = { ...hebLemmaSearchTranslationsData };
      if (!newData['hebrew_bible']) {
        newData['hebrew_bible'] = await loadOtTranslation('hebrew_bible');
      }
      if (!newData['lxx']) {
        if (lxxVerseData.length > 0) {
          const lookup = {};
          lxxVerseData.forEach(row => {
            if (row.book && row.acf_chapter && row.acf_verse && row.verse_text) {
              lookup[`${row.book}_${row.acf_chapter}_${row.acf_verse}`] = row.verse_text;
            }
          });
          newData['lxx'] = lookup;
        } else {
          const verseRes = await fetch('/LXX_Rahlfs_by_verse.csv');
          const verseText = await verseRes.text();
          const verseRows = parseCSV(verseText);
          const verseHeaders = verseRows[0].map(h => h.trim().toLowerCase());
          const lookup = {};
          for (let i = 1; i < verseRows.length; i++) {
            const row = {};
            verseHeaders.forEach((h, idx) => { row[h] = (verseRows[i][idx] || '').trim(); });
            if (row.book && row.acf_chapter && row.acf_verse && row.verse_text) {
              lookup[`${row.book}_${row.acf_chapter}_${row.acf_verse}`] = row.verse_text;
            }
          }
          newData['lxx'] = lookup;
        }
      }
      for (const key of ['acf_ot', 'nkjv_ot', 'niv_ot', 'nvi_ot']) {
        if (!newData[key]) {
          newData[key] = await loadOtTranslation(key);
        }
      }
      setHebLemmaSearchTranslationsData(newData);
    } catch (err) {
      console.error('Error loading Hebrew lemma search translations:', err);
    }
    setHebLemmaSearchTranslationsLoading(false);
  };

  // Load translations when Hebrew lemma is selected
  useEffect(() => {
    if (activeMenu === 'hebrewSearchLemma' && hebLemmaSelectedValue && Object.keys(hebLemmaSearchTranslationsData).length === 0) {
      loadHebLemmaSearchTranslations();
    }
  }, [activeMenu, hebLemmaSelectedValue]);

  // Hebrew Verse Grid: filtered data for current book/chapter (using MT chapters mapped from selected ACF chapter)
  const hebGridFilteredData = useMemo(() => {
    const mtChapters = new Set(hebAcfToMtChapters[`${hebSelectedBook}_${hebSelectedChapter}`] || []);
    return hebWordData.filter(row =>
      row.book === hebSelectedBook && mtChapters.has(row.chapter)
    );
  }, [hebWordData, hebAcfToMtChapters, hebSelectedBook, hebSelectedChapter]);

  // Toggle Hebrew grid column
  const toggleHebGridColumn = (column) => {
    setHebGridSelectedColumns(prev =>
      prev.includes(column) ? prev.filter(c => c !== column) : [...prev, column]
    );
  };

  // Hebrew Strong Frequency data — operates on pre-aggregated hebStrongGrouped
  // instead of hebWordData to keep filter changes fast and non-blocking
  const hebStrongFrequencyData = useMemo(() => {
    if (!hebStrongGrouped) return [];
    const hasFilter = hebSelectedClassFilter || hebSelectedStemFilter || hebSelectedPosFilter ||
                      hebSelectedPersonFilter || hebSelectedGenderFilter || hebSelectedNumberFilter ||
                      hebSelectedStateFilter || hebSelectedTypeFilter;
    const setToStr = arr => [...arr].sort().join(', ');
    const result = [];

    for (const [strong, info] of Object.entries(hebStrongGrouped)) {
      let matchedRows;
      if (hebSelectedClassFilter) {
        const bucket = info.classBuckets[hebSelectedClassFilter] || [];
        matchedRows = bucket.filter(r => {
          if (hebSelectedStemFilter && r.stem !== hebSelectedStemFilter) return false;
          if (hebSelectedPosFilter && r.pos !== hebSelectedPosFilter) return false;
          if (hebSelectedPersonFilter && r.person !== hebSelectedPersonFilter) return false;
          if (hebSelectedGenderFilter && r.gender !== hebSelectedGenderFilter) return false;
          if (hebSelectedNumberFilter && r.number !== hebSelectedNumberFilter) return false;
          if (hebSelectedStateFilter && r.state !== hebSelectedStateFilter) return false;
          if (hebSelectedTypeFilter && r.type !== hebSelectedTypeFilter) return false;
          return true;
        });
      } else {
        matchedRows = [];
        for (const rows of Object.values(info.classBuckets)) {
          for (const r of rows) {
            if (hebSelectedStemFilter && r.stem !== hebSelectedStemFilter) continue;
            if (hebSelectedPosFilter && r.pos !== hebSelectedPosFilter) continue;
            if (hebSelectedPersonFilter && r.person !== hebSelectedPersonFilter) continue;
            if (hebSelectedGenderFilter && r.gender !== hebSelectedGenderFilter) continue;
            if (hebSelectedNumberFilter && r.number !== hebSelectedNumberFilter) continue;
            if (hebSelectedStateFilter && r.state !== hebSelectedStateFilter) continue;
            if (hebSelectedTypeFilter && r.type !== hebSelectedTypeFilter) continue;
            matchedRows.push(r);
          }
        }
      }

      if (hasFilter && matchedRows.length === 0) continue;

      const classes = hebSelectedClassFilter
        ? new Set([hebSelectedClassFilter])
        : new Set(Object.keys(info.classBuckets).filter(Boolean));
      const stems = new Set(), poses = new Set(), persons = new Set();
      const genders = new Set(), numbers = new Set(), states = new Set(), types = new Set();
      const useRows = hasFilter ? matchedRows : Object.values(info.classBuckets).flat();
      useRows.forEach(r => {
        if (r.stem) stems.add(r.stem);
        if (r.pos) poses.add(r.pos);
        if (r.person) persons.add(r.person);
        if (r.gender) genders.add(r.gender);
        if (r.number) numbers.add(r.number);
        if (r.state) states.add(r.state);
        if (r.type) types.add(r.type);
      });

      result.push({
        strong,
        frequency: hasFilter ? matchedRows.length : Object.values(info.classBuckets).reduce((s, b) => s + b.length, 0),
        class: setToStr([...classes]),
        stem: setToStr([...stems]),
        pos: setToStr([...poses]),
        person: setToStr([...persons]),
        gender: setToStr([...genders]),
        number: setToStr([...numbers]),
        state: setToStr([...states]),
        type: setToStr([...types]),
        lemma: info.lemma,
        gloss: info.gloss,
      });
    }

    result.sort((a, b) => b.frequency - a.frequency);
    return result;
  }, [hebStrongGrouped, hebSelectedClassFilter, hebSelectedStemFilter, hebSelectedPosFilter,
      hebSelectedPersonFilter, hebSelectedGenderFilter, hebSelectedNumberFilter,
      hebSelectedStateFilter, hebSelectedTypeFilter]);

  // Hebrew Frequency Occurrences (drill-down)
  const hebFrequencyOccurrences = useMemo(() => {
    if (!hebFrequencyStrongNumber) return [];
    return hebWordData.filter(row => {
      if (row.strongnumberx !== hebFrequencyStrongNumber) return false;
      if (hebSelectedClassFilter && row.class !== hebSelectedClassFilter) return false;
      if (hebSelectedStemFilter && row.stem !== hebSelectedStemFilter) return false;
      if (hebSelectedPosFilter && row.pos !== hebSelectedPosFilter) return false;
      if (hebSelectedPersonFilter && row.person !== hebSelectedPersonFilter) return false;
      if (hebSelectedGenderFilter && row.gender !== hebSelectedGenderFilter) return false;
      if (hebSelectedNumberFilter && row.number !== hebSelectedNumberFilter) return false;
      if (hebSelectedStateFilter && row.state !== hebSelectedStateFilter) return false;
      if (hebSelectedTypeFilter && row.type !== hebSelectedTypeFilter) return false;
      return true;
    });
  }, [hebWordData, hebFrequencyStrongNumber, hebSelectedClassFilter, hebSelectedStemFilter,
      hebSelectedPosFilter, hebSelectedPersonFilter, hebSelectedGenderFilter,
      hebSelectedNumberFilter, hebSelectedStateFilter, hebSelectedTypeFilter]);

  // Handle "See Occurrences" from Hebrew Strong Frequency page
  const handleHebSeeOccurrences = (strongNumber, english) => {
    setHebFrequencyStrongNumber(strongNumber);
    setHebFrequencyEnglish(english || null);
    setActiveMenu('hebrewFrequencyOccurrences');
  };

  // Filter data by selected book and chapter
  const filteredData = useMemo(() => {
    return data.filter(row => 
      row.book === selectedBook && row.chapter === selectedChapter
    );
  }, [data, selectedBook, selectedChapter]);

  useEffect(() => {
    if (selectedRow && !filteredData.includes(selectedRow)) {
      setSelectedRow(null);
    }
  }, [filteredData, selectedRow]);

  useEffect(() => {
    if (activeMenu !== 'viewer' || filteredData.length === 0) {
      return;
    }

    viewerTableContainerRef.current?.focus({ preventScroll: true });
  }, [activeMenu, filteredData]);

  // Filter data by Strong number (for Strong Occurrences panel)
  const strongOccurrences = useMemo(() => {
    if (!selectedRow || !selectedRow.strong) return [];
    return data.filter(row => row.strong === selectedRow.strong);
  }, [data, selectedRow]);

  // Filter data by Strong number (for Frequency Occurrences page)
  // Apply the same class/gender/case/number filters from the Strong Frequency page
  const frequencyOccurrences = useMemo(() => {
    if (!frequencyStrongNumber) return [];
    return data.filter(row => {
      if (row.strong !== frequencyStrongNumber) return false;
      if (selectedClassFilter && row.class !== selectedClassFilter) return false;
      if (selectedGenderFilter && row.gender !== selectedGenderFilter) return false;
      if (selectedCaseFilter && row.case !== selectedCaseFilter) return false;
      if (selectedNumberFilter && row.number !== selectedNumberFilter) return false;
      if (selectedTenseFilter && row.tense !== selectedTenseFilter) return false;
      if (selectedVoiceFilter && row.voice !== selectedVoiceFilter) return false;
      if (selectedMoodFilter && row.mood !== selectedMoodFilter) return false;
      if (selectedPersonFilter && row.person !== selectedPersonFilter) return false;
      return true;
    });
  }, [data, frequencyStrongNumber, selectedClassFilter, selectedGenderFilter, selectedCaseFilter, selectedNumberFilter, selectedTenseFilter, selectedVoiceFilter, selectedMoodFilter, selectedPersonFilter]);

  // Handle "See Occurrences" button click from Strong Frequency page
  const handleSeeOccurrences = (strongNumber) => {
    setFrequencyStrongNumber(strongNumber);
    setActiveMenu('frequencyOccurrences');
  };

  // Greek keyboard characters
  // Row 1-2: plain letters; Row 3: rough breathing (aspirated) vowels + ῥ; Row 4-5: uppercase
  const greekKeyboard = [
    ['α','β','γ','δ','ε','ζ','η','θ','ι','κ','λ','μ'],
    ['ν','ξ','ο','π','ρ','σ','ς','τ','υ','φ','χ','ψ','ω'],
    ['ἁ','ἑ','ἡ','ἱ','ὁ','ὑ','ὡ','ῥ'],
    ['Α','Β','Γ','Δ','Ε','Ζ','Η','Θ','Ι','Κ','Λ','Μ'],
    ['Ν','Ξ','Ο','Π','Ρ','Σ','Τ','Υ','Φ','Χ','Ψ','Ω'],
  ];

  // Hebrew consonant keyboard (22 letters + final forms)
  const hebrewKeyboard = [
    ['א','ב','ג','ד','ה','ו','ז','ח','ט','י','כ','ך'],
    ['ל','מ','ם','נ','ן','ס','ע','פ','ף','צ','ץ','ק'],
    ['ר','ש','ת'],
  ];

  // Unique values for search suggestions
  const uniqueLemmas = useMemo(() => {
    return [...new Set(data.map(row => row.lemma).filter(Boolean))].sort();
  }, [data]);

  const uniqueGlosses = useMemo(() => {
    return [...new Set(data.map(row => row.gloss).filter(Boolean))].sort();
  }, [data]);

  const uniqueStrongs = useMemo(() => {
    return [...new Set(data.map(row => row.strong).filter(Boolean))].sort();
  }, [data]);

  const strongToLemmaMap = useMemo(() => {
    const map = new Map();
    data.forEach(row => {
      if (row.strong && row.lemma && !map.has(row.strong)) {
        map.set(row.strong, row.lemma);
      }
    });
    return map;
  }, [data]);

  const uniquePortuguese = useMemo(() => {
    return [...new Set(data.map(row => row.portuguese).filter(Boolean))].sort();
  }, [data]);

  const lemmaToPortugueseMap = useMemo(() => {
    const map = new Map();
    data.forEach(row => {
      if (row.lemma && row.portuguese && !map.has(row.lemma)) {
        map.set(row.lemma, row.portuguese);
      }
    });
    return map;
  }, [data]);

  // Build a map: gloss -> { lemmasSet: Set, count: number }
  const glossInfoMap = useMemo(() => {
    const map = new Map();
    data.forEach(row => {
      if (!row.gloss) return;
      if (!map.has(row.gloss)) {
        map.set(row.gloss, { lemmasSet: new Set(), count: 0 });
      }
      const entry = map.get(row.gloss);
      entry.count++;
      if (row.lemma) entry.lemmasSet.add(row.lemma);
    });
    return map;
  }, [data]);

  // Build a map: portuguese -> { lemmas: string[], count: number }
  const portugueseInfoMap = useMemo(() => {
    const map = new Map();
    data.forEach(row => {
      if (!row.portuguese) return;
      if (!map.has(row.portuguese)) {
        map.set(row.portuguese, { lemmasSet: new Set(), count: 0 });
      }
      const entry = map.get(row.portuguese);
      entry.count++;
      if (row.lemma) entry.lemmasSet.add(row.lemma);
    });
    return map;
  }, [data]);

  // For a selected Portuguese word, list distinct (strong, lemma, count) tuples
  const portugueseLemmaStrongsList = useMemo(() => {
    if (!searchSelectedValue || activeMenu !== 'searchPortuguese') return [];
    const map = new Map(); // strong -> { lemma, count }
    data.forEach(row => {
      if (row.portuguese === searchSelectedValue && row.strong) {
        if (!map.has(row.strong)) {
          map.set(row.strong, { lemma: row.lemma || '', count: 0 });
        }
        map.get(row.strong).count++;
      }
    });
    return [...map.entries()]
      .map(([strong, info]) => ({ strong, lemma: info.lemma, count: info.count }))
      .sort((a, b) => b.count - a.count);
  }, [data, searchSelectedValue, activeMenu]);

  // For Portuguese search-as-you-type: one item per distinct (strong, lemma) across all matching portuguese values
  const portugueseDirectSuggestions = useMemo(() => {
    if (!searchText || activeMenu !== 'searchPortuguese') return [];
    const lowerSearch = searchText.toLowerCase();
    const map = new Map(); // strong -> { lemma, portuguese, count }
    data.forEach(row => {
      if (row.portuguese && row.portuguese.toLowerCase().includes(lowerSearch) && row.strong) {
        if (!map.has(row.strong)) {
          map.set(row.strong, { lemma: row.lemma || '', portuguese: row.portuguese, count: 0 });
        }
        map.get(row.strong).count++;
      }
    });
    return [...map.entries()]
      .map(([strong, info]) => ({ strong, lemma: info.lemma, portuguese: info.portuguese, count: info.count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 60);
  }, [data, searchText, activeMenu]);

  // Filtered search suggestions
  const searchSuggestions = useMemo(() => {
    if (!searchText) return [];
    let searchField, uniqueValues;
    if (activeMenu === 'searchLemma') {
      searchField = 'lemma';
      uniqueValues = uniqueLemmas;
    } else if (activeMenu === 'searchGloss') {
      searchField = 'gloss';
      uniqueValues = uniqueGlosses;
    } else if (activeMenu === 'searchPortuguese') {
      searchField = 'portuguese';
      uniqueValues = uniquePortuguese;
    } else {
      searchField = 'strong';
      uniqueValues = uniqueStrongs;
    }
    
    if (searchField === 'lemma') {
      // For lemma: normalize both search text and values (ignore diacritics)
      const normalizedSearch = normalizeGreek(searchText);
      return uniqueValues.filter(val => normalizeGreek(val).includes(normalizedSearch)).slice(0, 50);
    } else {
      // For gloss/strong: simple case-insensitive match
      const lowerSearch = searchText.toLowerCase();
      return uniqueValues.filter(val => val.toLowerCase().includes(lowerSearch)).slice(0, 50);
    }
  }, [searchText, activeMenu, uniqueLemmas, uniqueGlosses, uniqueStrongs, uniquePortuguese]);

  // Resolve Lemma/Gloss/Portuguese to Strong numbers
  const searchResolvedStrongs = useMemo(() => {
    if (!searchSelectedValue) return new Set();
    if (activeMenu === 'searchStrong') return new Set([searchSelectedValue]);
    let searchField;
    if (activeMenu === 'searchLemma') searchField = 'lemma';
    else if (activeMenu === 'searchGloss') searchField = 'gloss';
    else if (activeMenu === 'searchPortuguese') searchField = 'portuguese';
    else return new Set();
    return new Set(
      data.filter(row => row[searchField] === searchSelectedValue)
          .map(row => row.strong)
          .filter(Boolean)
    );
  }, [data, searchSelectedValue, activeMenu]);

  // Search results: find all rows matching the resolved Strong numbers
  const searchResults = useMemo(() => {
    if (searchResolvedStrongs.size === 0) return [];
    return data.filter(row => searchResolvedStrongs.has(row.strong));
  }, [data, searchResolvedStrongs]);

  // Handle search key press on Greek keyboard
  const handleGreekKey = (char) => {
    setSearchText(prev => prev + char);
    setSearchSelectedValue(null);
  };

  const handleSearchBackspace = () => {
    setSearchText(prev => prev.slice(0, -1));
    setSearchSelectedValue(null);
  };

  const handleSearchClear = () => {
    setSearchText('');
    setSearchSelectedValue(null);
    setSearchPortugueseSelectedStrong(null);
  };

  const handleSelectSuggestion = (value) => {
    setSearchText(value);
    setSearchSelectedValue(value);
  };

  // Translation viewer configuration
  const TRANSLATIONS = [
    { key: 'greek_nt', name: 'Greek NT', language: 'Greek' },
    { key: 'nkjv_nt', name: 'NKJV', language: 'English' },
    { key: 'niv_nt', name: 'NIV', language: 'English' },
    { key: 'acf_nt', name: 'ACF', language: 'Portuguese' },
    { key: 'nvi_nt', name: 'NVI', language: 'Portuguese' },
  ];

  // Parse CSV handling quoted fields and multiline values
  const parseCSV = (text) => {
    const rows = [];
    let currentRow = [];
    let currentField = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (inQuotes) {
        if (char === '"') {
          if (text[i + 1] === '"') {
            currentField += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          currentField += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          currentRow.push(currentField);
          currentField = '';
        } else if (char === '\n') {
          currentRow.push(currentField);
          if (currentRow.length > 1) rows.push(currentRow);
          currentRow = [];
          currentField = '';
        } else if (char !== '\r') {
          currentField += char;
        }
      }
    }
    if (currentField || currentRow.length > 0) {
      currentRow.push(currentField);
      if (currentRow.length > 1) rows.push(currentRow);
    }
    return rows;
  };

  // Load a translation CSV/TSV file and build lookup map
  const loadTranslation = async (translationKey) => {
    // Determine file extension based on translation key
    const fileExtension = translationKey === 'greek_nt' ? 'tsv' : 'csv';
    const response = await fetch(`/${translationKey}.${fileExtension}`);
    if (!response.ok) throw new Error(`Failed to load ${translationKey}.${fileExtension}`);
    const text = await response.text();
    
    // Parse based on file type
    let rows;
    if (fileExtension === 'tsv') {
      // Simple TSV parsing (tab-separated)
      rows = text.trim().split('\n').map(line => line.split('\t'));
    } else {
      // Use CSV parser for CSV files
      rows = parseCSV(text);
    }
    
    if (rows.length === 0) return {};
    const headers = rows[0].map(h => h.trim().toLowerCase());
    const bookIdx = headers.indexOf('book');
    const chapterIdx = headers.indexOf('chapter');
    const verseIdx = headers.indexOf('verse') !== -1 ? headers.indexOf('verse') : headers.indexOf('verse_number');
    const textIdx = headers.indexOf('verse_text');
    const lookup = {};
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (row.length > textIdx) {
        const lk = `${row[bookIdx].trim()}_${row[chapterIdx].trim()}_${row[verseIdx].trim()}`;
        lookup[lk] = row[textIdx];
      }
    }
    return lookup;
  };

  // Handle "View Verse Translations" button click
  const handleViewTranslations = async (searchValue, searchField, returnMenu) => {
    setTranslationsLoading(true);
    setTranslationSearchValue(searchValue);
    setTranslationSearchField(searchField);
    if (returnMenu) setTranslationReturnMenu(returnMenu);
    
    // Resolve to Strong numbers for all search types
    let resolvedStrongs;
    if (searchField === 'strong') {
      resolvedStrongs = new Set([searchValue]);
    } else {
      resolvedStrongs = new Set(
        data.filter(row => row[searchField] === searchValue)
            .map(row => row.strong)
            .filter(Boolean)
      );
    }
    setTranslationFilterStrongs(resolvedStrongs);
    
    // Find lemma and portuguese info from the first matching strong
    const firstStrong = [...resolvedStrongs][0];
    if (firstStrong) {
      const matchingRow = data.find(row => row.strong === firstStrong);
      setTranslationSearchLemma(matchingRow ? matchingRow.lemma : '');
      setTranslationSearchPortuguese(matchingRow ? matchingRow.portuguese : '');
    } else {
      setTranslationSearchLemma('');
      setTranslationSearchPortuguese('');
    }
    
    try {
      const newData = { ...translationData };
      for (const t of TRANSLATIONS) {
        if (!newData[t.key]) {
          if (t.key === 'greek_nt') {
            // Build Greek NT lookup from already-loaded data (avoids extra file fetch)
            const lookup = {};
            data.forEach(row => {
              if (row.book && row.chapter && row.verse && row.text) {
                const vk = `${row.book}_${row.chapter}_${row.verse}`;
                if (!lookup[vk]) lookup[vk] = [];
                lookup[vk].push(row.text);
              }
            });
            // Join all Greek words per verse into a single string
            Object.keys(lookup).forEach(vk => {
              lookup[vk] = lookup[vk].join(' ');
            });
            newData[t.key] = lookup;
          } else {
            newData[t.key] = await loadTranslation(t.key);
          }
        }
      }
      setTranslationData(newData);
      setActiveMenu('translationViewer');
    } catch (err) {
      console.error('Error loading translations:', err);
    }
    setTranslationsLoading(false);
  };

  // Toggle translation selection
  const toggleTranslation = (key) => {
    setSelectedTranslations(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Build a lookup: verseKey -> Set of portuguese_acf words for the current Strong numbers
  const acfHighlightMap = useMemo(() => {
    if (!translationFilterStrongs || translationFilterStrongs.size === 0) return {};
    const map = {};
    data.forEach(row => {
      if (translationFilterStrongs.has(row.strong) && row.portuguese_acf) {
        const vk = `${row.book}_${row.chapter}_${row.verse}`;
        if (!map[vk]) map[vk] = new Set();
        map[vk].add(row.portuguese_acf);
      }
    });
    return map;
  }, [data, translationFilterStrongs]);

  // Build a lookup: verseKey -> Set of Greek 'text' words for the current Strong numbers
  const greekHighlightMap = useMemo(() => {
    if (!translationFilterStrongs || translationFilterStrongs.size === 0) return {};
    const map = {};
    data.forEach(row => {
      if (translationFilterStrongs.has(row.strong) && row.text) {
        const vk = `${row.book}_${row.chapter}_${row.verse}`;
        if (!map[vk]) map[vk] = new Set();
        map[vk].add(row.text);
      }
    });
    return map;
  }, [data, translationFilterStrongs]);

  // Highlight portuguese_acf words in ACF verse text
  const highlightAcfText = (text, verseKey) => {
    const words = acfHighlightMap[verseKey];
    if (!words || words.size === 0) return text;
    // Build a regex that matches any of the portuguese_acf words (case-insensitive, word boundaries)
    const escaped = [...words].map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
    const parts = text.split(pattern);
    if (parts.length === 1) return text;
    return parts.map((part, i) => {
      if (words.has(part) || [...words].some(w => w.toLowerCase() === part.toLowerCase())) {
        return <span key={i} className="acf-highlight">{part}</span>;
      }
      return part;
    });
  };

  // Highlight Greek 'text' words in Greek NT verse text
  const highlightGreekText = (text, verseKey) => {
    const words = greekHighlightMap[verseKey];
    if (!words || words.size === 0) return text;
    // Build a regex that matches any of the Greek text words (case-sensitive for Greek)
    const escaped = [...words].map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(`(${escaped.join('|')})`);
    const parts = text.split(pattern);
    if (parts.length === 1) return text;
    return parts.map((part, i) => {
      if (words.has(part)) {
        return <span key={i} className="greek-highlight">{part}</span>;
      }
      return part;
    });
  };

  // Compute unique verses for translation viewer (resolved via Strong numbers)
  const translationVerses = useMemo(() => {
    let matchingRows;
    if (translationFilterStrongs && translationFilterStrongs.size > 0) {
      matchingRows = data.filter(row => translationFilterStrongs.has(row.strong));
    } else if (translationSearchValue && translationSearchField) {
      matchingRows = data.filter(row => row[translationSearchField] === translationSearchValue);
    } else {
      return [];
    }
    const seen = new Set();
    const uniqueVerses = [];
    matchingRows.forEach(row => {
      const verseKey = `${row.book}_${row.chapter}_${row.verse}`;
      if (!seen.has(verseKey)) {
        seen.add(verseKey);
        uniqueVerses.push({ book: row.book, chapter: row.chapter, verse: row.verse, key: verseKey });
      }
    });
    return uniqueVerses;
  }, [data, translationFilterStrongs, translationSearchValue, translationSearchField]);

  // Calculate Strong Frequency data
  const strongFrequencyData = useMemo(() => {
    // Filter data by class, gender, case, number and verb-specific fields if filters are selected
    let filtered = data;
    if (selectedClassFilter) filtered = filtered.filter(row => row.class === selectedClassFilter);
    if (selectedGenderFilter) filtered = filtered.filter(row => row.gender === selectedGenderFilter);
    if (selectedCaseFilter) filtered = filtered.filter(row => row.case === selectedCaseFilter);
    if (selectedNumberFilter) filtered = filtered.filter(row => row.number === selectedNumberFilter);
    if (selectedTenseFilter) filtered = filtered.filter(row => row.tense === selectedTenseFilter);
    if (selectedVoiceFilter) filtered = filtered.filter(row => row.voice === selectedVoiceFilter);
    if (selectedMoodFilter) filtered = filtered.filter(row => row.mood === selectedMoodFilter);
    if (selectedPersonFilter) filtered = filtered.filter(row => row.person === selectedPersonFilter);

    // Group by strong number and count occurrences
    // Collect all unique values for each attribute per strong number
    const strongCounts = {};
    const strongInfo = {};

    filtered.forEach(row => {
      if (row.strong) {
        if (!strongCounts[row.strong]) {
          strongCounts[row.strong] = 0;
          strongInfo[row.strong] = {
            classes: new Set(),
            genders: new Set(),
            cases: new Set(),
            numbers: new Set(),
            tenses: new Set(),
            voices: new Set(),
            moods: new Set(),
            persons: new Set(),
            lemma: row.lemma || '',
            gloss: row.gloss || '',
            portuguese: row.portuguese || ''
          };
        }
        strongCounts[row.strong]++;
        const info = strongInfo[row.strong];
        if (row.class) info.classes.add(row.class);
        if (row.gender) info.genders.add(row.gender);
        if (row.case) info.cases.add(row.case);
        if (row.number) info.numbers.add(row.number);
        if (row.tense) info.tenses.add(row.tense);
        if (row.voice) info.voices.add(row.voice);
        if (row.mood) info.moods.add(row.mood);
        if (row.person) info.persons.add(row.person);
      }
    });

    // Convert to array and sort by frequency descending
    // For each attribute, show the single value if uniform, or join with comma if mixed
    const setToString = (s) => [...s].sort().join(', ');
    const result = Object.keys(strongCounts).map(strong => ({
      strong,
      frequency: strongCounts[strong],
      class: setToString(strongInfo[strong].classes),
      gender: setToString(strongInfo[strong].genders),
      case: setToString(strongInfo[strong].cases),
      number: setToString(strongInfo[strong].numbers),
      tense: setToString(strongInfo[strong].tenses),
      voice: setToString(strongInfo[strong].voices),
      mood: setToString(strongInfo[strong].moods),
      person: setToString(strongInfo[strong].persons),
      lemma: strongInfo[strong].lemma,
      gloss: strongInfo[strong].gloss,
      portuguese: strongInfo[strong].portuguese
    }));

    result.sort((a, b) => b.frequency - a.frequency);

    return result;
  }, [data, selectedClassFilter, selectedGenderFilter, selectedCaseFilter, selectedNumberFilter, selectedTenseFilter, selectedVoiceFilter, selectedMoodFilter, selectedPersonFilter]);

  // ==================== RESET PAGINATION ON DATA CHANGE ====================
  useEffect(() => { setViewerPage(0); }, [selectedBook, selectedChapter, selectedClassFilter, selectedGenderFilter, selectedCaseFilter, selectedNumberFilter]);
  useEffect(() => { setStrongFreqPage(0); }, [strongFrequencyData]);
  useEffect(() => { setFreqOccPage(0); }, [frequencyOccurrences]);
  useEffect(() => { setLxxGridPage(0); }, [lxxGridFilteredData]);
  useEffect(() => { setLxxStrongFreqPage(0); }, [lxxStrongFrequencyData]);
  useEffect(() => { setLxxFreqOccPage(0); }, [lxxFrequencyOccurrences]);
  useEffect(() => { setHebGridPage(0); }, [hebGridFilteredData]);
  useEffect(() => { setHebStrongFreqPage(0); }, [hebStrongFrequencyData]);
  useEffect(() => { setHebFreqOccPage(0); }, [hebFrequencyOccurrences]);

  // Handle row selection in Data Viewer
  const handleRowClick = (row) => {
    if (selectedRow && selectedRow.ref === row.ref) {
      // Clicking the same row deselects it
      setSelectedRow(null);
    } else {
      setSelectedRow(row);
    }
  };

  const getViewerCurrentIndex = () => {
    if (selectedRow) {
      const selectedIndex = filteredData.indexOf(selectedRow);
      if (selectedIndex >= 0) {
        return selectedIndex;
      }
    }

    const container = viewerTableContainerRef.current;
    const firstRow = container?.querySelector('tbody tr');
    if (!container || !firstRow) {
      return -1;
    }

    const rowHeight = Math.max(1, firstRow.getBoundingClientRect().height);
    return Math.min(filteredData.length - 1, Math.max(0, Math.floor(container.scrollTop / rowHeight)));
  };

  const moveViewerSelectionByPage = (direction) => {
    if (filteredData.length === 0) {
      return;
    }

    const pageSize = getViewerPageSize();
    const currentIndex = getViewerCurrentIndex();
    const baseIndex = currentIndex >= 0 ? currentIndex : direction > 0 ? -1 : filteredData.length;
    const nextIndex = Math.min(
      filteredData.length - 1,
      Math.max(0, baseIndex + direction * pageSize)
    );

    setSelectedRow(filteredData[nextIndex]);
  };

  const focusTableContainer = (event) => {
    if (event.target.closest('button, input, select, textarea, a')) {
      return;
    }
    event.currentTarget.focus();
  };

  const handlePagedTableKeyDown = (event) => {
    if (event.key !== 'PageDown' && event.key !== 'PageUp') {
      return;
    }

    event.preventDefault();
    event.currentTarget.focus({ preventScroll: true });
    const direction = event.key === 'PageDown' ? 1 : -1;
    const scrollAmount = Math.max(120, event.currentTarget.clientHeight - 48);
    event.currentTarget.scrollBy({ top: direction * scrollAmount, behavior: 'auto' });
  };

  const getViewerPageSize = () => {
    const container = viewerTableContainerRef.current;
    if (!container) {
      return 1;
    }

    const firstRow = container.querySelector('tbody tr');
    if (!firstRow) {
      return 1;
    }

    const headerHeight = container.querySelector('thead')?.getBoundingClientRect().height || 0;
    const rowHeight = Math.max(1, firstRow.getBoundingClientRect().height);
    const usableHeight = Math.max(rowHeight, container.clientHeight - headerHeight);
    return Math.max(1, Math.floor(usableHeight / rowHeight));
  };

  const handleViewerTableKeyDown = (event) => {
    if (event.key !== 'PageDown' && event.key !== 'PageUp') {
      return;
    }

    event.preventDefault();
    event.currentTarget.focus({ preventScroll: true });
    moveViewerSelectionByPage(event.key === 'PageDown' ? 1 : -1);
  };

  useEffect(() => {
    if (activeMenu !== 'viewer') {
      return undefined;
    }

    const handleWindowKeyDown = (event) => {
      if (event.key !== 'PageDown' && event.key !== 'PageUp') {
        return;
      }

      const interactiveElement = event.target instanceof Element
        ? event.target.closest('input, select, textarea, button, a, [contenteditable="true"]')
        : null;
      if (interactiveElement) {
        return;
      }

      event.preventDefault();
      moveViewerSelectionByPage(event.key === 'PageDown' ? 1 : -1);
      viewerTableContainerRef.current?.focus({ preventScroll: true });
    };

    window.addEventListener('keydown', handleWindowKeyDown);
    return () => window.removeEventListener('keydown', handleWindowKeyDown);
  }, [activeMenu, filteredData, selectedRow]);

  useEffect(() => {
    if (activeMenu !== 'viewer' || !selectedRow) {
      return;
    }

    const container = viewerTableContainerRef.current;
    if (!container) {
      return;
    }

    const selectedIndex = filteredData.indexOf(selectedRow);
    if (selectedIndex < 0) {
      return;
    }

    const rowElement = container.querySelector(`tbody tr[data-row-index="${selectedIndex}"]`);
    rowElement?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    container.focus({ preventScroll: true });
  }, [activeMenu, filteredData, selectedRow]);

  // Toggle column selection
  const toggleColumn = (column) => {
    setSelectedColumns(prev => {
      if (prev.includes(column)) {
        return prev.filter(col => col !== column);
      } else {
        return [...prev, column];
      }
    });
  };

  // Select all columns
  const selectAll = () => {
    setSelectedColumns([...allColumns]);
  };

  // Deselect all columns
  const deselectAll = () => {
    setSelectedColumns([]);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-content">
          <div className="loading-title">Loading Greek NT data...</div>
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: `${loadingProgress.percent}%` }} />
          </div>
          <div className="loading-detail">
            {loadingProgress.phase === 'downloading'
              ? `Downloading... ${loadingProgress.percent}%`
              : `Parsing rows... ${loadingProgress.percent}%`}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1>Greek NT Viewer</h1>
        <div className="header-row">
          <nav className="menu-nav">
            <button 
              className={activeMenu === 'viewer' ? 'active' : ''} 
              onClick={() => setActiveMenu('viewer')}
            >
              📖 NT Word Grid
            </button>
            <button 
              className={activeMenu === 'bibles' ? 'active' : ''} 
              onClick={() => setActiveMenu('bibles')}
            >
              📜 NT Bibles
            </button>
            <button 
              className={activeMenu === 'interlinear' ? 'active' : ''} 
              onClick={() => setActiveMenu('interlinear')}
            >
              📝 NT Interlinear
            </button>
            <button 
              className={activeMenu === 'strongFrequency' ? 'active' : ''} 
              onClick={() => setActiveMenu('strongFrequency')}
            >
              📊 NT Strong Frequency
            </button>
            <div className="search-dropdown">
              <button 
                className={activeMenu === 'searchLemma' || activeMenu === 'searchGloss' || activeMenu === 'searchStrong' || activeMenu === 'searchPortuguese' ? 'active' : ''} 
                onClick={() => setSearchMenuOpen(prev => !prev)}
              >
                🔍 NT Search ▾
              </button>
              {searchMenuOpen && (
                <div className="search-dropdown-menu">
                  <button onClick={() => { setActiveMenu('searchLemma'); setSearchMenuOpen(false); setSearchText(''); setSearchSelectedValue(null); }}>
                    Lemma
                  </button>
                  <button onClick={() => { setActiveMenu('searchGloss'); setSearchMenuOpen(false); setSearchText(''); setSearchSelectedValue(null); }}>
                    Gloss
                  </button>
                  <button onClick={() => { setActiveMenu('searchStrong'); setSearchMenuOpen(false); setSearchText(''); setSearchSelectedValue(null); }}>
                    Strong
                  </button>
                  <button onClick={() => { setActiveMenu('searchPortuguese'); setSearchMenuOpen(false); setSearchText(''); setSearchSelectedValue(null); setSearchPortugueseSelectedStrong(null); }}>
                    Portuguese
                  </button>
                </div>
              )}
            </div>
            <div className="search-dropdown">
              <button 
                className={activeMenu === 'lxxBible' || activeMenu === 'lxxInterlinear' || activeMenu === 'lxxSearchStrong' || activeMenu === 'lxxSearchLemma' || activeMenu === 'lxxVerseGrid' || activeMenu === 'lxxStrongFrequency' || activeMenu === 'lxxFrequencyOccurrences' || activeMenu === 'lxxVerseTranslations' ? 'active' : ''} 
                onClick={() => { setLxxMenuOpen(prev => !prev); loadLxxData(); }}
              >
                📜 LXX ▾
              </button>
              {lxxMenuOpen && (
                <div className="search-dropdown-menu">
                  <button onClick={() => { setActiveMenu('lxxBible'); setLxxMenuOpen(false); }}>
                    LXX Bible
                  </button>
                  <button onClick={() => { setActiveMenu('lxxInterlinear'); setLxxMenuOpen(false); }}>
                    Interlinear
                  </button>
                  <button onClick={() => { setActiveMenu('lxxSearchStrong'); setLxxMenuOpen(false); setLxxSearchText(''); setLxxSearchSelectedValue(null); }}>
                    Search LXX Strong
                  </button>
                  <button onClick={() => { setActiveMenu('lxxSearchLemma'); setLxxMenuOpen(false); setLxxLemmaSearchText(''); setLxxLemmaSelectedValue(null); }}>
                    Search LXX lemma
                  </button>
                  <button onClick={() => { setActiveMenu('lxxVerseGrid'); setLxxMenuOpen(false); }}>
                    View LXX Verse Grid
                  </button>
                  <button onClick={() => { setActiveMenu('lxxStrongFrequency'); setLxxMenuOpen(false); }}>
                    📊 Strong Frequency
                  </button>
                </div>
              )}
            </div>
            <div className="search-dropdown">
              <button 
                className={activeMenu === 'hebrewMT' || activeMenu === 'hebrewInterlinear' || activeMenu === 'hebrewSearchStrong' || activeMenu === 'hebrewSearchLemma' || activeMenu === 'hebrewVerseGrid' || activeMenu === 'hebrewStrongFrequency' || activeMenu === 'hebrewFrequencyOccurrences' ? 'active' : ''} 
                onClick={() => { setHebMenuOpen(prev => !prev); loadHebData(); }}
              >
                📖 Hebrew ▾
              </button>
              {hebMenuOpen && (
                <div className="search-dropdown-menu">
                  <button onClick={() => { setActiveMenu('hebrewMT'); setHebMenuOpen(false); }}>
                    Hebrew MT
                  </button>
                  <button onClick={() => { setActiveMenu('hebrewInterlinear'); setHebMenuOpen(false); }}>
                    Interlinear
                  </button>
                  <button onClick={() => { setActiveMenu('hebrewSearchStrong'); setHebMenuOpen(false); setHebSearchText(''); setHebSearchSelectedValue(null); }}>
                    Search Hebrew Strong
                  </button>
                  <button onClick={() => { setActiveMenu('hebrewSearchLemma'); setHebMenuOpen(false); setHebLemmaSearchText(''); setHebLemmaSelectedValue(null); }}>
                    Search Hebrew Lemma
                  </button>
                  <button onClick={() => { setActiveMenu('hebrewVerseGrid'); setHebMenuOpen(false); }}>
                    View Hebrew Verse Grid
                  </button>
                  <button onClick={() => { setActiveMenu('hebrewStrongFrequency'); setHebMenuOpen(false); }}>
                    📊 Strong Frequency
                  </button>
                </div>
              )}
            </div>
            <button 
              className={activeMenu === 'about' ? 'active' : ''} 
              onClick={() => setActiveMenu('about')}
            >
              ℹ️ About Us
            </button>
          </nav>
        </div>
        {['lxxBible', 'lxxInterlinear', 'lxxSearchStrong', 'lxxVerseGrid'].includes(activeMenu) && (
          <div className="header-row lxx-book-chapter-row">
            <div className="filters">
              <label>
                OT Book:
                <select value={lxxSelectedBook} onChange={(e) => setLxxSelectedBook(e.target.value)}>
                  {lxxAvailableBooks.map(book => (
                    <option key={book} value={book}>{book}</option>
                  ))}
                </select>
              </label>
              {activeMenu === 'lxxVerseGrid' ? (
                <>
                  <label>
                    Numbering:
                    <select value={lxxGridNumberingMode} onChange={(e) => setLxxGridNumberingMode(e.target.value)}>
                      <option value="lxx">LXX Native</option>
                      <option value="acf">ACF</option>
                    </select>
                  </label>
                  <label>
                    Chapter:
                    <select value={lxxGridChapter} onChange={(e) => setLxxGridChapter(e.target.value)}>
                      {lxxGridDisplayChapters.map(chapter => (
                        <option key={chapter} value={chapter}>{chapter}</option>
                      ))}
                    </select>
                  </label>
                </>
              ) : (
                <label>
                  Chapter:
                  <select value={lxxSelectedChapter} onChange={(e) => setLxxSelectedChapter(e.target.value)}>
                    {lxxAvailableChapters.map(chapter => (
                      <option key={chapter} value={chapter}>{chapter}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </div>
        )}
        {['hebrewMT', 'hebrewInterlinear', 'hebrewSearchStrong', 'hebrewVerseGrid'].includes(activeMenu) && (
          <div className="header-row lxx-book-chapter-row">
            <div className="filters">
              <label>
                OT Book:
                <select value={hebSelectedBook} onChange={(e) => setHebSelectedBook(e.target.value)}>
                  {hebAvailableBooks.map(book => (
                    <option key={book} value={book}>{HEB_BOOK_NAMES[book] || book}</option>
                  ))}
                </select>
              </label>
              <label>
                Chapter:
                <select value={hebSelectedChapter} onChange={(e) => setHebSelectedChapter(e.target.value)}>
                  {hebAvailableChapters.map(chapter => (
                    <option key={chapter} value={chapter}>{chapter}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}
      </header>

      <main className="app-main">
        {/* ==================== DATA VIEWER TAB ==================== */}
        {activeMenu === 'viewer' && (
          <div className="viewer-layout">
            {/* Book/Chapter navigation */}
            <div className="viewer-nav">
              <div className="filters">
                <label>
                  Book:
                  <select value={selectedBook} onChange={(e) => setSelectedBook(e.target.value)}>
                    {availableBooks.map(book => (
                      <option key={book} value={book}>{book}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Chapter:
                  <select value={selectedChapter} onChange={(e) => setSelectedChapter(e.target.value)}>
                    {availableChapters.map(chapter => (
                      <option key={chapter} value={chapter}>{chapter}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {/* Column selector toggle button */}
            <div className="viewer-toolbar">
              <button 
                className={`toolbar-btn ${columnPanelOpen ? 'active' : ''}`}
                onClick={() => setColumnPanelOpen(prev => !prev)}
              >
                ⚙️ {columnPanelOpen ? 'Hide' : 'Show'} Columns ({selectedColumns.length}/{allColumns.length})
              </button>
              {selectedRow && selectedRow.strong && (
                <>
                  <span className="toolbar-separator">|</span>
                  <span className="toolbar-selected-info">
                    Selected: <strong className="strong-number-badge">{selectedRow.strong}</strong>
                    {selectedRow.lemma && <span className="greek-text"> ({selectedRow.lemma})</span>}
                    {selectedRow.gloss && <span> — {selectedRow.gloss}</span>}
                  </span>
                  <button
                    className="toolbar-btn"
                    onClick={() => handleSeeOccurrences(selectedRow.strong)}
                  >
                    📋 See Occurrences
                  </button>
                  <button
                    className="toolbar-btn"
                    onClick={() => handleViewTranslations(selectedRow.strong, 'strong', 'viewer')}
                  >
                    📖 View Verse Translations
                  </button>
                </>
              )}
            </div>

            {/* Collapsible column selector */}
            {columnPanelOpen && (
              <div className="column-panel">
                <div className="column-panel-header">
                  <h3>Select Columns</h3>
                  <div className="column-panel-actions">
                    <button onClick={selectAll}>Select All</button>
                    <button onClick={deselectAll}>Deselect All</button>
                    <button className="close-panel-btn" onClick={() => setColumnPanelOpen(false)}>✕</button>
                  </div>
                </div>
                <div className="columns-list">
                  {allColumns.map(column => (
                    <label key={column} className="column-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(column)}
                        onChange={() => toggleColumn(column)}
                      />
                      <span>{column}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Main data table */}
            <div className="display-data">
              {selectedColumns.length === 0 ? (
                <div className="no-columns">
                  No columns selected. Click <strong>⚙️ Show Columns</strong> above to choose which columns to display.
                </div>
              ) : (
                <div
                  className="table-container keyboard-scrollable"
                  ref={viewerTableContainerRef}
                  tabIndex={0}
                  onKeyDown={handleViewerTableKeyDown}
                  onMouseDown={focusTableContainer}
                  aria-label="Data viewer grid. Use Page Up and Page Down to move by pages."
                >
                  <div className="data-info">
                    Showing {filteredData.length} rows × {selectedColumns.length} columns
                    <span className="info-badge">{selectedBook} {selectedChapter}</span>
                    {!selectedRow && <span className="hint">💡 Click a row to see translation and occurrence options for its Strong number</span>}
                  </div>
                  <PaginationControls currentPage={viewerPage} totalRows={filteredData.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setViewerPage} />
                  <table className="data-table">
                    <thead>
                      <tr>
                        {selectedColumns.map(column => (
                          <th key={column}>{column}</th>
                        ))}
                        <th className="action-col"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.slice(viewerPage * TABLE_PAGE_SIZE, (viewerPage + 1) * TABLE_PAGE_SIZE).map((row, index) => (
                        <tr 
                          key={viewerPage * TABLE_PAGE_SIZE + index} 
                          data-row-index={viewerPage * TABLE_PAGE_SIZE + index}
                          onClick={() => handleRowClick(row)}
                          className={selectedRow && selectedRow.ref === row.ref ? 'selected-row' : ''}
                          style={{ cursor: 'pointer' }}
                        >
                          {selectedColumns.map(column => (
                            <td key={column}>{row[column]}</td>
                          ))}
                          <td className="action-col">
                            {row.strong && (
                              <button
                                className="row-occurrence-btn"
                                onClick={(e) => { e.stopPropagation(); handleSeeOccurrences(row.strong); }}
                              >
                                📋 See Occurrences
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <PaginationControls currentPage={viewerPage} totalRows={filteredData.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setViewerPage} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== STRONG FREQUENCY TAB ==================== */}
        {activeMenu === 'strongFrequency' && (
          <div className="display-data">
            <div
              className="table-container keyboard-scrollable"
              tabIndex={0}
              onKeyDown={handlePagedTableKeyDown}
              onMouseDown={focusTableContainer}
              aria-label="Strong frequency grid. Use Page Up and Page Down to move by pages."
            >
              <div className="data-info">
                <label>
                  Filter by Class:
                  <select
                    value={selectedClassFilter}
                    onChange={(e) => {
                      setSelectedClassFilter(e.target.value);
                      // Reset verb-specific filters when class changes
                      setSelectedTenseFilter('');
                      setSelectedVoiceFilter('');
                      setSelectedMoodFilter('');
                      setSelectedPersonFilter('');
                    }}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Classes</option>
                    {availableClasses.map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by Gender:
                  <select
                    value={selectedGenderFilter}
                    onChange={(e) => setSelectedGenderFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Genders</option>
                    {availableGenders.map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by Case:
                  <select
                    value={selectedCaseFilter}
                    onChange={(e) => setSelectedCaseFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Cases</option>
                    {availableCases.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by Number:
                  <select
                    value={selectedNumberFilter}
                    onChange={(e) => setSelectedNumberFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Numbers</option>
                    {availableNumbers.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
                {selectedClassFilter === 'verb' && (
                  <>
                    <label style={{ marginLeft: '20px' }}>
                      Tense:
                      <select
                        value={selectedTenseFilter}
                        onChange={(e) => setSelectedTenseFilter(e.target.value)}
                        style={{ marginLeft: '10px' }}
                      >
                        <option value="">All Tenses</option>
                        {availableTenses.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ marginLeft: '20px' }}>
                      Voice:
                      <select
                        value={selectedVoiceFilter}
                        onChange={(e) => setSelectedVoiceFilter(e.target.value)}
                        style={{ marginLeft: '10px' }}
                      >
                        <option value="">All Voices</option>
                        {availableVoices.map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ marginLeft: '20px' }}>
                      Mood:
                      <select
                        value={selectedMoodFilter}
                        onChange={(e) => setSelectedMoodFilter(e.target.value)}
                        style={{ marginLeft: '10px' }}
                      >
                        <option value="">All Moods</option>
                        {availableMoods.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ marginLeft: '20px' }}>
                      Person:
                      <select
                        value={selectedPersonFilter}
                        onChange={(e) => setSelectedPersonFilter(e.target.value)}
                        style={{ marginLeft: '10px' }}
                      >
                        <option value="">All Persons</option>
                        {availablePersons.map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </label>
                  </>
                )}
                <span style={{ marginLeft: '20px' }}>
                  Showing {strongFrequencyData.length} unique Strong numbers
                  {selectedClassFilter && ` (class: ${selectedClassFilter})`}
                  {selectedGenderFilter && ` (gender: ${selectedGenderFilter})`}
                  {selectedCaseFilter && ` (case: ${selectedCaseFilter})`}
                  {selectedNumberFilter && ` (number: ${selectedNumberFilter})`}
                  {selectedTenseFilter && ` (tense: ${selectedTenseFilter})`}
                  {selectedVoiceFilter && ` (voice: ${selectedVoiceFilter})`}
                  {selectedMoodFilter && ` (mood: ${selectedMoodFilter})`}
                  {selectedPersonFilter && ` (person: ${selectedPersonFilter})`}
                </span>
              </div>
              <PaginationControls currentPage={strongFreqPage} totalRows={strongFrequencyData.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setStrongFreqPage} />
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Strong</th>
                    <th>Frequency</th>
                    <th>Class</th>
                    <th>Gender</th>
                    <th>Case</th>
                    <th>Number</th>
                    {selectedClassFilter === 'verb' && <>
                      <th>Tense</th>
                      <th>Voice</th>
                      <th>Mood</th>
                      <th>Person</th>
                    </>}
                    <th>Lemma</th>
                    <th>Gloss</th>
                    <th>Portuguese</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {strongFrequencyData.slice(strongFreqPage * TABLE_PAGE_SIZE, (strongFreqPage + 1) * TABLE_PAGE_SIZE).map((row, index) => (
                    <tr key={strongFreqPage * TABLE_PAGE_SIZE + index}>
                      <td>{strongFreqPage * TABLE_PAGE_SIZE + index + 1}</td>
                      <td>{row.strong}</td>
                      <td>{row.frequency}</td>
                      <td>{row.class}</td>
                      <td>{row.gender}</td>
                      <td>{row.case}</td>
                      <td>{row.number}</td>
                      {selectedClassFilter === 'verb' && <>
                        <td>{row.tense}</td>
                        <td>{row.voice}</td>
                        <td>{row.mood}</td>
                        <td>{row.person}</td>
                      </>}
                      <td>{row.lemma}</td>
                      <td>{row.gloss}</td>
                      <td>{row.portuguese}</td>
                      <td>
                        <button 
                          onClick={() => handleSeeOccurrences(row.strong)}
                          className="see-occurrences-btn"
                        >
                          See Occurrences
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls currentPage={strongFreqPage} totalRows={strongFrequencyData.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setStrongFreqPage} />
            </div>
          </div>
        )}

        {/* ==================== FREQUENCY OCCURRENCES (drill-down) ==================== */}
        {activeMenu === 'frequencyOccurrences' && (
          <div className="viewer-layout">
            {/* Column selector toggle button */}
            <div className="viewer-toolbar">
              <button 
                onClick={() => setActiveMenu('strongFrequency')}
                className="back-btn"
              >
                ← Back to Strong Frequency
              </button>
              <span className="toolbar-separator">|</span>
              <button 
                className={`toolbar-btn ${columnPanelOpen ? 'active' : ''}`}
                onClick={() => setColumnPanelOpen(prev => !prev)}
              >
                ⚙️ {columnPanelOpen ? 'Hide' : 'Show'} Columns ({selectedColumns.length}/{allColumns.length})
              </button>
              <span className="toolbar-separator">|</span>
              <strong>Strong Number: {frequencyStrongNumber}</strong> — Found {frequencyOccurrences.length} occurrences
              {(selectedClassFilter || selectedGenderFilter || selectedCaseFilter || selectedNumberFilter) ? ' (filtered' : ' in the entire New Testament'}
              {selectedClassFilter && ` class: ${selectedClassFilter}`}
              {selectedGenderFilter && ` gender: ${selectedGenderFilter}`}
              {selectedCaseFilter && ` case: ${selectedCaseFilter}`}
              {selectedNumberFilter && ` number: ${selectedNumberFilter}`}
              {(selectedClassFilter || selectedGenderFilter || selectedCaseFilter || selectedNumberFilter) && ')'}
              <button className="view-translations-btn" onClick={() => handleViewTranslations(frequencyStrongNumber, 'strong', 'frequencyOccurrences')}>📖 View Verse Translations</button>
            </div>

            {/* Collapsible column selector */}
            {columnPanelOpen && (
              <div className="column-panel">
                <div className="column-panel-header">
                  <h3>Select Columns</h3>
                  <div className="column-panel-actions">
                    <button onClick={selectAll}>Select All</button>
                    <button onClick={deselectAll}>Deselect All</button>
                    <button className="close-panel-btn" onClick={() => setColumnPanelOpen(false)}>✕</button>
                  </div>
                </div>
                <div className="columns-list">
                  {allColumns.map(column => (
                    <label key={column} className="column-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(column)}
                        onChange={() => toggleColumn(column)}
                      />
                      <span>{column}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Main data table */}
            <div className="display-data">
              {selectedColumns.length === 0 ? (
                <div className="no-columns">
                  No columns selected. Click <strong>⚙️ Show Columns</strong> above to choose which columns to display.
                </div>
              ) : (
                <div
                  className="table-container keyboard-scrollable"
                  tabIndex={0}
                  onKeyDown={handlePagedTableKeyDown}
                  onMouseDown={focusTableContainer}
                  aria-label="Strong occurrences grid. Use Page Up and Page Down to move by pages."
                >
                  <div className="data-info">
                    Showing {frequencyOccurrences.length} rows × {selectedColumns.length + 1} columns
                    <span className="info-badge">Strong: {frequencyStrongNumber}</span>
                  </div>
                  <PaginationControls currentPage={freqOccPage} totalRows={frequencyOccurrences.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setFreqOccPage} />
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        {selectedColumns.map(column => (
                          <th key={column}>{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {frequencyOccurrences.slice(freqOccPage * TABLE_PAGE_SIZE, (freqOccPage + 1) * TABLE_PAGE_SIZE).map((row, index) => (
                        <tr key={freqOccPage * TABLE_PAGE_SIZE + index}>
                          <td>{freqOccPage * TABLE_PAGE_SIZE + index + 1}</td>
                          {selectedColumns.map(column => (
                            <td key={column}>{row[column]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <PaginationControls currentPage={freqOccPage} totalRows={frequencyOccurrences.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setFreqOccPage} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== SEARCH LEMMA TAB ==================== */}
        {activeMenu === 'searchLemma' && (
          <div className="search-page">
            <h2>🔍 Search by Lemma</h2>
            <div className="search-input-area">
              <div className="search-input-row">
                <input 
                  type="text" 
                  className="search-input greek-text"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setSearchSelectedValue(null); }}
                  placeholder="Type or use the keyboard below..."
                  readOnly={false}
                />
                <button className="search-clear-btn" onClick={handleSearchClear}>Clear</button>
                <button className="search-clear-btn" onClick={handleSearchBackspace}>⌫</button>
              </div>
              <div className="greek-keyboard">
                {greekKeyboard.map((row, rowIdx) => (
                  <div key={rowIdx} className="greek-keyboard-row">
                    {row.map((char, charIdx) => (
                      <button 
                        key={charIdx} 
                        className="greek-key"
                        onClick={() => handleGreekKey(char)}
                      >
                        {char}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            {searchText && !searchSelectedValue && (
              <div className="search-suggestions">
                <h3>Matching Lemmas ({searchSuggestions.length}{searchSuggestions.length === 50 ? '+' : ''})</h3>
                <div className="suggestions-list">
                  {searchSuggestions.map((val, idx) => (
                    <button key={idx} className="suggestion-item suggestion-item--lemma" onClick={() => handleSelectSuggestion(val)}>
                      <span className="suggestion-word greek-text">{val}</span>
                      {lemmaToPortugueseMap.get(val) && (
                        <span className="suggestion-portuguese">{lemmaToPortugueseMap.get(val)}</span>
                      )}
                    </button>
                  ))}
                  {searchSuggestions.length === 0 && <div className="no-suggestions">No matching lemmas found.</div>}
                </div>
              </div>
            )}
            {searchSelectedValue && (
              <div className="display-data search-results">
                <div className="viewer-toolbar">
                  <button 
                    className={`toolbar-btn ${columnPanelOpen ? 'active' : ''}`}
                    onClick={() => setColumnPanelOpen(prev => !prev)}
                  >
                    ⚙️ {columnPanelOpen ? 'Hide' : 'Show'} Columns ({selectedColumns.length}/{allColumns.length})
                  </button>
                </div>
                {columnPanelOpen && (
                  <div className="column-panel">
                    <div className="column-panel-header">
                      <h3>Select Columns</h3>
                      <div className="column-panel-actions">
                        <button onClick={selectAll}>Select All</button>
                        <button onClick={deselectAll}>Deselect All</button>
                        <button className="close-panel-btn" onClick={() => setColumnPanelOpen(false)}>✕</button>
                      </div>
                    </div>
                    <div className="columns-list">
                      {allColumns.map(column => (
                        <label key={column} className="column-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedColumns.includes(column)}
                            onChange={() => toggleColumn(column)}
                          />
                          <span>{column}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {selectedColumns.length === 0 ? (
                  <div className="no-columns">
                    No columns selected. Click <strong>⚙️ Show Columns</strong> above to choose which columns to display.
                  </div>
                ) : (
                  <div
                    className="table-container keyboard-scrollable"
                    tabIndex={0}
                    onKeyDown={handlePagedTableKeyDown}
                    onMouseDown={focusTableContainer}
                    aria-label="Lemma search results grid. Use Page Up and Page Down to move by pages."
                  >
                    <div className="data-info">
                      <span className="info-badge greek-text">{searchSelectedValue}</span>
                      <span>Found {searchResults.length} occurrences in the entire NT</span>
                      {searchResolvedStrongs.size > 0 && <span className="info-badge">Strong: {[...searchResolvedStrongs].join(', ')}</span>}
                      <button className="back-btn" onClick={() => { setSearchSelectedValue(null); setSelectedSearchRow(null); }}>← Back to suggestions</button>
                      <button className="view-translations-btn" onClick={() => handleViewTranslations(searchSelectedValue, 'lemma')}>📖 View Verse Translations</button>
                    </div>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          {selectedColumns.map(column => (
                            <th key={column}>{column}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map((row, index) => (
                          <tr
                            key={index}
                            onClick={() => setSelectedSearchRow(row)}
                            className={selectedSearchRow === row ? 'selected-row' : ''}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>{index + 1}</td>
                            {selectedColumns.map(column => (
                              <td key={column}>{row[column]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ==================== SEARCH GLOSS TAB ==================== */}
        {activeMenu === 'searchGloss' && (
          <div className="search-page">
            <h2>🔍 Search by Gloss</h2>
            <div className="search-input-area">
              <div className="search-input-row">
                <input 
                  type="text" 
                  className="search-input"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setSearchSelectedValue(null); }}
                  placeholder="Type an English gloss..."
                  autoFocus
                />
                <button className="search-clear-btn" onClick={handleSearchClear}>Clear</button>
              </div>
            </div>
            {searchText && !searchSelectedValue && (
              <div className="search-suggestions">
                <h3>Matching Glosses ({searchSuggestions.length}{searchSuggestions.length === 50 ? '+' : ''})</h3>
                <div className="suggestions-list">
                  {searchSuggestions.map((val, idx) => {
                    const info = glossInfoMap.get(val);
                    const lemmas = info ? [...info.lemmasSet].join(', ') : '';
                    const count = info ? info.count : 0;
                    return (
                      <button key={idx} className="suggestion-item" onClick={() => handleSelectSuggestion(val)}>
                        <span className="suggestion-word">{val}</span>
                        {lemmas && <span className="suggestion-strong greek-text">{lemmas}</span>}
                        <span className="suggestion-count">{count}</span>
                      </button>
                    );
                  })}
                  {searchSuggestions.length === 0 && <div className="no-suggestions">No matching glosses found.</div>}
                </div>
              </div>
            )}
            {searchSelectedValue && (
              <div className="display-data search-results">
                <div className="viewer-toolbar">
                  <button 
                    className={`toolbar-btn ${columnPanelOpen ? 'active' : ''}`}
                    onClick={() => setColumnPanelOpen(prev => !prev)}
                  >
                    ⚙️ {columnPanelOpen ? 'Hide' : 'Show'} Columns ({selectedColumns.length}/{allColumns.length})
                  </button>
                </div>
                {columnPanelOpen && (
                  <div className="column-panel">
                    <div className="column-panel-header">
                      <h3>Select Columns</h3>
                      <div className="column-panel-actions">
                        <button onClick={selectAll}>Select All</button>
                        <button onClick={deselectAll}>Deselect All</button>
                        <button className="close-panel-btn" onClick={() => setColumnPanelOpen(false)}>✕</button>
                      </div>
                    </div>
                    <div className="columns-list">
                      {allColumns.map(column => (
                        <label key={column} className="column-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedColumns.includes(column)}
                            onChange={() => toggleColumn(column)}
                          />
                          <span>{column}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {selectedColumns.length === 0 ? (
                  <div className="no-columns">
                    No columns selected. Click <strong>⚙️ Show Columns</strong> above to choose which columns to display.
                  </div>
                ) : (
                  <div
                    className="table-container keyboard-scrollable"
                    tabIndex={0}
                    onKeyDown={handlePagedTableKeyDown}
                    onMouseDown={focusTableContainer}
                    aria-label="Gloss search results grid. Use Page Up and Page Down to move by pages."
                  >
                    <div className="data-info">
                      <span className="info-badge">{searchSelectedValue}</span>
                      <span>Found {searchResults.length} occurrences in the entire NT</span>
                      {searchResolvedStrongs.size > 0 && <span className="info-badge">Strong: {[...searchResolvedStrongs].join(', ')}</span>}
                      <button className="back-btn" onClick={() => setSearchSelectedValue(null)}>← Back to suggestions</button>
                      <button className="view-translations-btn" onClick={() => handleViewTranslations(searchSelectedValue, 'gloss')}>📖 View Verse Translations</button>
                    </div>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          {selectedColumns.map(column => (
                            <th key={column}>{column}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map((row, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            {selectedColumns.map(column => (
                              <td key={column}>{row[column]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ==================== SEARCH STRONG TAB ==================== */}
        {activeMenu === 'searchStrong' && (
          <div className="search-page">
            <h2>🔍 Search by Strong Number</h2>
            <div className="search-input-area">
              <div className="search-input-row">
                <input 
                  type="text" 
                  className="search-input"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setSearchSelectedValue(null); }}
                  placeholder="Type a Strong number (e.g. G3056)..."
                  autoFocus
                />
                <button className="search-clear-btn" onClick={handleSearchClear}>Clear</button>
              </div>
            </div>
            {searchText && !searchSelectedValue && (
              <div className="search-suggestions">
                <h3>Matching Strong Numbers ({searchSuggestions.length}{searchSuggestions.length === 50 ? '+' : ''})</h3>
                <div className="suggestions-list">
                  {searchSuggestions.map((val, idx) => (
                    <button key={idx} className="suggestion-item" onClick={() => handleSelectSuggestion(val)}>
                      {val}{strongToLemmaMap.get(val) ? <span className="suggestion-lemma"> — {strongToLemmaMap.get(val)}</span> : ''}
                    </button>
                  ))}
                  {searchSuggestions.length === 0 && <div className="no-suggestions">No matching Strong numbers found.</div>}
                </div>
              </div>
            )}
            {searchSelectedValue && (
              <div className="display-data search-results">
                <div className="viewer-toolbar">
                  <button 
                    className={`toolbar-btn ${columnPanelOpen ? 'active' : ''}`}
                    onClick={() => setColumnPanelOpen(prev => !prev)}
                  >
                    ⚙️ {columnPanelOpen ? 'Hide' : 'Show'} Columns ({selectedColumns.length}/{allColumns.length})
                  </button>
                </div>
                {columnPanelOpen && (
                  <div className="column-panel">
                    <div className="column-panel-header">
                      <h3>Select Columns</h3>
                      <div className="column-panel-actions">
                        <button onClick={selectAll}>Select All</button>
                        <button onClick={deselectAll}>Deselect All</button>
                        <button className="close-panel-btn" onClick={() => setColumnPanelOpen(false)}>✕</button>
                      </div>
                    </div>
                    <div className="columns-list">
                      {allColumns.map(column => (
                        <label key={column} className="column-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedColumns.includes(column)}
                            onChange={() => toggleColumn(column)}
                          />
                          <span>{column}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {selectedColumns.length === 0 ? (
                  <div className="no-columns">
                    No columns selected. Click <strong>⚙️ Show Columns</strong> above to choose which columns to display.
                  </div>
                ) : (
                  <div
                    className="table-container keyboard-scrollable"
                    tabIndex={0}
                    onKeyDown={handlePagedTableKeyDown}
                    onMouseDown={focusTableContainer}
                    aria-label="Strong search results grid. Use Page Up and Page Down to move by pages."
                  >
                    <div className="data-info">
                      <span className="info-badge">{searchSelectedValue}</span>
                      <span>Found {searchResults.length} occurrences in the entire NT</span>
                      <button className="back-btn" onClick={() => setSearchSelectedValue(null)}>← Back to suggestions</button>
                      <button className="view-translations-btn" onClick={() => handleViewTranslations(searchSelectedValue, 'strong')}>📖 View Verse Translations</button>
                    </div>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          {selectedColumns.map(column => (
                            <th key={column}>{column}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map((row, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            {selectedColumns.map(column => (
                              <td key={column}>{row[column]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {/* ==================== SEARCH PORTUGUESE TAB ==================== */}
        {activeMenu === 'searchPortuguese' && (
          <div className="search-page">
            <h2>🔍 Search by Portuguese</h2>
            <div className="search-input-area">
              <div className="search-input-row">
                <input 
                  type="text" 
                  className="search-input"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setSearchSelectedValue(null); setSearchPortugueseSelectedStrong(null); }}
                  placeholder="Type a Portuguese word..."
                  autoFocus
                />
                <button className="search-clear-btn" onClick={handleSearchClear}>Clear</button>
              </div>
            </div>
            {/* Step 1: one button per distinct Greek lemma/strong that translates to the typed word */}
            {searchText && !searchPortugueseSelectedStrong && (
              <div className="search-suggestions">
                <h3>Greek words translated as "{searchText}" ({portugueseDirectSuggestions.length}{portugueseDirectSuggestions.length === 60 ? '+' : ''})</h3>
                <div className="suggestions-list">
                  {portugueseDirectSuggestions.map((item, idx) => (
                    <button key={idx} className="suggestion-item suggestion-item--lemma" onClick={() => setSearchPortugueseSelectedStrong(item.strong)}>
                      <span className="suggestion-word greek-text">{item.lemma}</span>
                      <span className="suggestion-strong">{item.portuguese}</span>
                      <span className="suggestion-count">{item.count}</span>
                    </button>
                  ))}
                  {portugueseDirectSuggestions.length === 0 && <div className="no-suggestions">No Greek words found for this Portuguese term.</div>}
                </div>
              </div>
            )}
            {/* Step 2: show results for the selected strong */}
            {searchPortugueseSelectedStrong && (() => {
              const ptResults = data.filter(row => row.strong === searchPortugueseSelectedStrong);
              const ptLemma = ptResults.length > 0 ? ptResults[0].lemma : '';
              return (
                <div className="display-data search-results">
                  <div className="viewer-toolbar">
                    <button 
                      className={`toolbar-btn ${columnPanelOpen ? 'active' : ''}`}
                      onClick={() => setColumnPanelOpen(prev => !prev)}
                    >
                      ⚙️ {columnPanelOpen ? 'Hide' : 'Show'} Columns ({selectedColumns.length}/{allColumns.length})
                    </button>
                  </div>
                  {columnPanelOpen && (
                    <div className="column-panel">
                      <div className="column-panel-header">
                        <h3>Select Columns</h3>
                        <div className="column-panel-actions">
                          <button onClick={selectAll}>Select All</button>
                          <button onClick={deselectAll}>Deselect All</button>
                          <button className="close-panel-btn" onClick={() => setColumnPanelOpen(false)}>✕</button>
                        </div>
                      </div>
                      <div className="columns-list">
                        {allColumns.map(column => (
                          <label key={column} className="column-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedColumns.includes(column)}
                              onChange={() => toggleColumn(column)}
                            />
                            <span>{column}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedColumns.length === 0 ? (
                    <div className="no-columns">
                      No columns selected. Click <strong>⚙️ Show Columns</strong> above to choose which columns to display.
                    </div>
                  ) : (
                    <div
                      className="table-container keyboard-scrollable"
                      tabIndex={0}
                      onKeyDown={handlePagedTableKeyDown}
                      onMouseDown={focusTableContainer}
                      aria-label="Portuguese search results grid. Use Page Up and Page Down to move by pages."
                    >
                      <div className="data-info">
                        {ptLemma && <span className="info-badge greek-text">{ptLemma}</span>}
                        <span className="info-badge">{searchPortugueseSelectedStrong}</span>
                        <span>Found {ptResults.length} occurrences in the entire NT</span>
                        <button className="back-btn" onClick={() => setSearchPortugueseSelectedStrong(null)}>← Back to lemmas</button>
                        <button className="view-translations-btn" onClick={() => handleViewTranslations(searchPortugueseSelectedStrong, 'strong')}>📖 View Verse Translations</button>
                      </div>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            {selectedColumns.map(column => (
                              <th key={column}>{column}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {ptResults.map((row, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              {selectedColumns.map(column => (
                                <td key={column}>{row[column]}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* ==================== TRANSLATION VIEWER ==================== */}
        {activeMenu === 'translationViewer' && (
          <div className="translation-viewer">
            <div className="translation-header">
              <button className="back-btn" onClick={() => {
                if (translationReturnMenu) {
                  setActiveMenu(translationReturnMenu);
                  setTranslationReturnMenu('');
                } else if (translationSearchField === 'strong') setActiveMenu('searchStrong');
                else if (translationSearchField === 'lemma') setActiveMenu('searchLemma');
                else if (translationSearchField === 'gloss') setActiveMenu('searchGloss');
                else if (translationSearchField === 'portuguese') setActiveMenu('searchPortuguese');
                else setActiveMenu('viewer');
              }}>← Back{translationReturnMenu === 'viewer' ? ' to Data Viewer' : translationReturnMenu === 'frequencyOccurrences' ? ' to Occurrences' : ' to Search Results'}</button>
              <h2>📖 Verse Translations</h2>
              <p className="translation-subtitle">
                Showing {translationVerses.length} verses containing <span className="info-badge">{translationSearchValue}</span>
                {translationSearchField === 'strong' && translationSearchLemma && (
                  <span className="lemma-display greek-text"> ({translationSearchLemma})</span>
                )}
                {translationSearchField === 'strong' && translationSearchPortuguese && (
                  <span className="portuguese-display"> — {translationSearchPortuguese}</span>
                )}
                {translationSearchField === 'portuguese' && translationSearchLemma && (
                  <span className="lemma-display greek-text"> ({translationSearchLemma})</span>
                )}
              </p>
            </div>

            <div className="translation-selector">
              <h3>Select Translations:</h3>
              <div className="translation-checkboxes">
                {TRANSLATIONS.map(t => (
                  <label key={t.key} className="translation-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedTranslations.includes(t.key)}
                      onChange={() => toggleTranslation(t.key)}
                    />
                    <span className="translation-name">{t.name}</span>
                    <span className="translation-lang">({t.language})</span>
                  </label>
                ))}
              </div>
            </div>

            {translationsLoading ? (
              <div className="loading">Loading translations...</div>
            ) : selectedTranslations.length === 0 ? (
              <div className="no-columns" style={{ background: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                No translations selected. Check one or more translations above.
              </div>
            ) : (
              <div className="translation-verses">
                {translationVerses.map((verse, idx) => (
                  <div key={verse.key} className="verse-card">
                    <div className="verse-reference">
                      <span className="verse-num">{idx + 1}.</span>
                      <span className="verse-ref-text">{verse.book} {verse.chapter}:{verse.verse}</span>
                    </div>
                    <div className="verse-translations">
                      {selectedTranslations
                        .sort((a, b) => {
                          const indexA = TRANSLATIONS.findIndex(t => t.key === a);
                          const indexB = TRANSLATIONS.findIndex(t => t.key === b);
                          return indexA - indexB;
                        })
                        .map(tKey => {
                        const trans = TRANSLATIONS.find(t => t.key === tKey);
                        const text = translationData[tKey] && translationData[tKey][verse.key];
                        return (
                          <div key={tKey} className="verse-translation-item">
                            <span className={`translation-label label-${trans.key}`}>
                              {trans.name}
                            </span>
                            <p className="verse-text-content">
                              {text
                                ? (tKey === 'acf_nt' ? highlightAcfText(text, verse.key)
                                  : tKey === 'greek_nt' ? highlightGreekText(text, verse.key)
                                  : text)
                                : '(verse not found in this translation)'}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {translationVerses.length === 0 && (
                  <div className="no-columns">No verses found.</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ==================== BIBLES VIEWER TAB ==================== */}
        {activeMenu === 'bibles' && (
          <div className="bibles-viewer">
            <div className="bibles-header">
              <h2>📜 Bibles — Chapter Viewer</h2>
              <p className="bibles-subtitle">Read the New Testament chapter by chapter in multiple translations</p>
            </div>

            <div className="bibles-controls">
              <div className="bibles-nav">
                <label className="bibles-select-label">
                  Book:
                  <select value={biblesBook} onChange={(e) => setBiblesBook(e.target.value)} className="bibles-select">
                    {availableBooks.map(book => (
                      <option key={book} value={book}>{book}</option>
                    ))}
                  </select>
                </label>
                <label className="bibles-select-label">
                  Chapter:
                  <select value={biblesChapter} onChange={(e) => setBiblesChapter(e.target.value)} className="bibles-select">
                    {biblesAvailableChapters.map(ch => (
                      <option key={ch} value={ch}>{ch}</option>
                    ))}
                  </select>
                </label>
                <span className="bibles-info-badge">{biblesBook} {biblesChapter}</span>
              </div>

              <div className="bibles-translation-selector">
                <span className="bibles-translations-label">Translations:</span>
                <div className="bibles-translation-chips">
                  {TRANSLATIONS.map(t => {
                    const isSelected = biblesSelectedTranslations.includes(t.key);
                    return (
                      <button
                        key={t.key}
                        className={`bibles-chip bibles-chip-${t.key} ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleBiblesTranslation(t.key)}
                      >
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {biblesLoading ? (
              <div className="loading">Loading translations...</div>
            ) : biblesSelectedTranslations.length === 0 ? (
              <div className="bibles-empty">No translations selected. Click one or more translations above to start reading.</div>
            ) : (
              <div className="bibles-chapter-content">
                {biblesVerses.map((verseNum) => {
                  const verseKey = `${biblesBook}_${biblesChapter}_${verseNum}`;
                  return (
                    <div key={verseKey} className="bibles-verse-block">
                      <div className="bibles-verse-number-bar">
                        <span className="bibles-verse-number">{verseNum}</span>
                        <span className="bibles-verse-ref">{biblesBook} {biblesChapter}:{verseNum}</span>
                      </div>
                      <div className="bibles-verse-translations">
                        {TRANSLATIONS
                          .filter(t => biblesSelectedTranslations.includes(t.key))
                          .map(t => {
                            const text = biblesData[t.key] && biblesData[t.key][verseKey];
                            return (
                              <div key={t.key} className={`bibles-verse-row bibles-row-${t.key}`}>
                                <span className={`bibles-label bibles-label-${t.key}`}>{t.name}</span>
                                <p className={`bibles-verse-text ${t.key === 'greek_nt' ? 'greek-text' : ''}`}>
                                  {text || <em className="bibles-not-found">(verse not found)</em>}
                                </p>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  );
                })}
                {biblesVerses.length === 0 && !biblesLoading && (
                  <div className="bibles-empty">No verses found for {biblesBook} chapter {biblesChapter}.</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ==================== INTERLINEAR TAB ==================== */}
        {activeMenu === 'interlinear' && (
          <div className="interlinear-viewer">
            <div className="interlinear-header">
              <h2>📝 Interlinear — Chapter Viewer</h2>
              <p className="interlinear-subtitle">Read the Greek New Testament word-by-word with parsing details</p>
            </div>

            <div className="interlinear-controls">
              <div className="interlinear-nav">
                <div className="filters">
                  <label>
                    Book:
                    <select value={interlinearBook} onChange={(e) => setInterlinearBook(e.target.value)}>
                      {availableBooks.map(book => (
                        <option key={book} value={book}>{book}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Chapter:
                    <select value={interlinearChapter} onChange={(e) => setInterlinearChapter(e.target.value)}>
                      {interlinearAvailableChapters.map(chapter => (
                        <option key={chapter} value={chapter}>{chapter}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <span className="interlinear-info-badge">{interlinearBook} {interlinearChapter}</span>
              </div>

              <div className="interlinear-toggle-row">
                <span className="interlinear-toggle-label">Show/Hide:</span>
                <button
                  className={`interlinear-toggle-chip ${interlinearShowLemma ? 'active' : ''}`}
                  onClick={() => setInterlinearShowLemma(prev => !prev)}
                >
                  Lemma
                </button>
                <button
                  className={`interlinear-toggle-chip ${interlinearShowPortuguese ? 'active' : ''}`}
                  onClick={() => setInterlinearShowPortuguese(prev => !prev)}
                >
                  Portuguese (ACF)
                </button>
                <button
                  className={`interlinear-toggle-chip ${interlinearShowStrong ? 'active' : ''}`}
                  onClick={() => setInterlinearShowStrong(prev => !prev)}
                >
                  Strong
                </button>
                <button
                  className={`interlinear-toggle-chip ${interlinearShowMorph ? 'active' : ''}`}
                  onClick={() => setInterlinearShowMorph(prev => !prev)}
                >
                  Morph
                </button>
                <button
                  className={`interlinear-toggle-chip ${interlinearShowGloss ? 'active' : ''}`}
                  onClick={() => setInterlinearShowGloss(prev => !prev)}
                >
                  Gloss
                </button>
              </div>
            </div>

            <div className="interlinear-chapter-content">
              {interlinearVerses.map(({ verseNum, words }) => (
                <div key={verseNum} className="interlinear-verse-block">
                  <div className="interlinear-verse-number-bar">
                    <span className="interlinear-verse-number">{verseNum}</span>
                    <span className="interlinear-verse-ref">{interlinearBook} {interlinearChapter}:{verseNum}</span>
                  </div>
                  <div className="interlinear-words">
                    {words.map((word, idx) => (
                      <div key={idx} className="interlinear-word-stack">
                        <span className="interlinear-greek">{word.text}</span>
                        {interlinearShowLemma && (
                          <span className="interlinear-lemma">{word.lemma || '—'}</span>
                        )}
                        {interlinearShowPortuguese && (
                          <span className="interlinear-portuguese">{word.portuguese_acf || '—'}</span>
                        )}
                        {interlinearShowStrong && (
                          <span className="interlinear-strong">{word.strong || '—'}</span>
                        )}
                        {interlinearShowMorph && (
                          <span className="interlinear-morph">{word.morph || '—'}</span>
                        )}
                        {interlinearShowGloss && (
                          <span className="interlinear-gloss">{word.gloss || '—'}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {interlinearVerses.length === 0 && (
                <div className="interlinear-empty">No verses found for {interlinearBook} chapter {interlinearChapter}.</div>
              )}
            </div>
          </div>
        )}

        {/* ==================== LXX BIBLE TAB ==================== */}
        {activeMenu === 'lxxBible' && (
          <div className="bibles-viewer">
            <div className="bibles-header">
              <h2>📜 LXX Bible — Chapter Viewer</h2>
              <p className="bibles-subtitle">Read the Septuagint (LXX) chapter by chapter in multiple translations</p>
            </div>

            {lxxDataLoading ? (
              <div className="loading">Loading LXX data...</div>
            ) : (
              <>
                <div className="bibles-controls">
                  <div className="bibles-nav">
                    <span className="bibles-info-badge">{lxxSelectedBook} {lxxSelectedChapter}</span>
                  </div>

                  <div className="bibles-translation-selector">
                    <span className="bibles-translations-label">Translations:</span>
                    <div className="bibles-translation-chips">
                      {LXX_BIBLE_TRANSLATIONS.map(t => {
                        const isSelected = lxxBiblesSelectedTranslations.includes(t.key);
                        return (
                          <button
                            key={t.key}
                            className={`bibles-chip bibles-chip-${t.key} ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleLxxBiblesTranslation(t.key)}
                          >
                            {t.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {lxxBiblesLoading ? (
                  <div className="loading">Loading translations...</div>
                ) : lxxBiblesSelectedTranslations.length === 0 ? (
                  <div className="bibles-empty">No translations selected. Click one or more translations above to start reading.</div>
                ) : (
                  <div className="bibles-chapter-content">
                    {lxxBiblesVerses.map((verseNum) => {
                      const verseKey = `${lxxSelectedBook}_${lxxSelectedChapter}_${verseNum}`;
                      return (
                        <div key={verseKey} className="bibles-verse-block">
                          <div className="bibles-verse-number-bar">
                            <span className="bibles-verse-number">{verseNum}</span>
                            <span className="bibles-verse-ref">{lxxSelectedBook} {lxxSelectedChapter}:{verseNum}</span>
                          </div>
                          <div className="bibles-verse-translations">
                            {LXX_BIBLE_TRANSLATIONS
                              .filter(t => lxxBiblesSelectedTranslations.includes(t.key))
                              .map(t => {
                                const text = lxxOtTranslations[t.key] && lxxOtTranslations[t.key][verseKey];
                                return (
                                  <div key={t.key} className={`bibles-verse-row bibles-row-${t.key}`}>
                                    <span className={`bibles-label bibles-label-${t.key}`}>{t.name}</span>
                                    <p className={`bibles-verse-text ${t.key === 'lxx' ? 'greek-text' : ''} ${t.key === 'hebrew_bible' ? 'hebrew-text' : ''}`}>
                                      {text || <em className="bibles-not-found">(verse not found)</em>}
                                    </p>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      );
                    })}
                    {lxxBiblesVerses.length === 0 && !lxxBiblesLoading && (
                      <div className="bibles-empty">No verses found for {lxxSelectedBook} chapter {lxxSelectedChapter}.</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ==================== LXX INTERLINEAR TAB ==================== */}
        {activeMenu === 'lxxInterlinear' && (
          <div className="interlinear-viewer">
            <div className="interlinear-header">
              <h2>📝 LXX Interlinear — Chapter Viewer</h2>
              <p className="interlinear-subtitle">Read the Septuagint word-by-word with parsing details</p>
            </div>

            {lxxDataLoading ? (
              <div className="loading">Loading LXX data...</div>
            ) : (
              <>
                <div className="interlinear-controls">
                  <div className="interlinear-nav">
                    <span className="interlinear-info-badge">{lxxSelectedBook} {lxxSelectedChapter}</span>
                  </div>

                  <div className="interlinear-toggle-row">
                    <span className="interlinear-toggle-label">Verse numbering:</span>
                    <button
                      className={`interlinear-toggle-chip ${lxxInterlinearVerseMode === 'lxx' ? 'active' : ''}`}
                      onClick={() => setLxxInterlinearVerseMode('lxx')}
                    >
                      LXX (native)
                    </button>
                    <button
                      className={`interlinear-toggle-chip ${lxxInterlinearVerseMode === 'acf' ? 'active' : ''}`}
                      onClick={() => setLxxInterlinearVerseMode('acf')}
                    >
                      ACF aligned
                    </button>
                  </div>

                  <div className="interlinear-toggle-row">
                    <span className="interlinear-toggle-label">Show/Hide:</span>
                    <button
                      className={`interlinear-toggle-chip ${lxxInterlinearShowLemma ? 'active' : ''}`}
                      onClick={() => setLxxInterlinearShowLemma(prev => !prev)}
                    >
                      Lemma
                    </button>
                    <button
                      className={`interlinear-toggle-chip ${lxxInterlinearShowGloss ? 'active' : ''}`}
                      onClick={() => setLxxInterlinearShowGloss(prev => !prev)}
                    >
                      Gloss
                    </button>
                    <button
                      className={`interlinear-toggle-chip ${lxxInterlinearShowMorph ? 'active' : ''}`}
                      onClick={() => setLxxInterlinearShowMorph(prev => !prev)}
                    >
                      Morphology
                    </button>
                    <button
                      className={`interlinear-toggle-chip ${lxxInterlinearShowStrong ? 'active' : ''}`}
                      onClick={() => setLxxInterlinearShowStrong(prev => !prev)}
                    >
                      Strong
                    </button>
                    <button
                      className={`interlinear-toggle-chip ${lxxInterlinearShowHebStrong ? 'active' : ''}`}
                      onClick={() => setLxxInterlinearShowHebStrong(prev => !prev)}
                    >
                      Hebrew Strong
                    </button>
                  </div>
                </div>

                <div className="interlinear-chapter-content">
                  {lxxInterlinearVerses.map(({ verseNum, words, acfChapter, acfVerse }) => {
                    const dispVerse   = lxxInterlinearVerseMode === 'acf' && acfVerse   ? acfVerse   : verseNum;
                    const dispChapter = lxxInterlinearVerseMode === 'acf' && acfChapter ? acfChapter : lxxSelectedChapter;
                    const verseLabel  = lxxInterlinearVerseMode === 'acf' && !acfVerse ? `${verseNum} (no ACF)` : dispVerse;
                    return (
                    <div key={verseNum} className="interlinear-verse-block">
                      <div className="interlinear-verse-number-bar">
                        <span className="interlinear-verse-number">{verseLabel}</span>
                        <span className="interlinear-verse-ref">{lxxSelectedBook} {dispChapter}:{verseLabel}</span>
                      </div>
                      <div className="interlinear-words">
                        {words.map((word, idx) => (
                          <div
                            key={idx}
                            className="interlinear-word-stack interlinear-word-clickable"
                            onClick={() => { setLxxInterlinearSelectedWord(word._fullRow); setLxxInterlinearWordColumnPanelOpen(false); setLxxInterlinearWordVerseMode('lxx'); }}
                            title="Click to view all word details"
                          >
                            <span className="interlinear-greek">{word.text}</span>
                            {lxxInterlinearShowLemma && (
                              <span className="interlinear-lemma">{word.lemma || '—'}</span>
                            )}
                            {lxxInterlinearShowGloss && (
                              <span className="interlinear-gloss">{word.gloss || '—'}</span>
                            )}
                            {lxxInterlinearShowMorph && (
                              <span className="interlinear-morph">{word.morphology || '—'}</span>
                            )}
                            {lxxInterlinearShowStrong && (
                              <span className="interlinear-strong">{word.strong || '—'}</span>
                            )}
                            {lxxInterlinearShowHebStrong && (
                              <span className="interlinear-hebrew-strong">{word.hebrew_strong || '—'}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    );
                  })}
                  {lxxInterlinearVerses.length === 0 && (
                    <div className="interlinear-empty">No verses found for {lxxSelectedBook} chapter {lxxSelectedChapter}.</div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ==================== LXX INTERLINEAR WORD DETAIL MODAL ==================== */}
        {lxxInterlinearSelectedWord && (() => {
          const strong = lxxInterlinearSelectedWord.strong || '';
          const strongRows = strong
            ? lxxWordData.filter(row => row.strong === strong)
            : [lxxInterlinearSelectedWord];
          // Enrich each row with acf_chapter/acf_verse from the alignment map
          const enrichedRows = strongRows.map(row => {
            const nativeKey = `${row.book}_${row.chapter}_${row.verse}`;
            const acf = lxxNativeToAcfMap.get(nativeKey);
            return { ...row, acf_chapter: acf ? acf.acf_chapter : '', acf_verse: acf ? acf.acf_verse : '' };
          });
          // Available column list for the modal (native cols + acf alignment cols)
          const modalColumns = lxxGridColumns.includes('acf_chapter')
            ? lxxGridColumns
            : [...lxxGridColumns, 'acf_chapter', 'acf_verse'];
          return (
          <div className="modal-overlay" onClick={() => setLxxInterlinearSelectedWord(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>
                  <span className="interlinear-greek" style={{ fontSize: '1.4rem' }}>
                    {lxxInterlinearSelectedWord.greek_text}
                  </span>
                  {lxxInterlinearSelectedWord.lemma && (
                    <span className="info-badge greek-text">{lxxInterlinearSelectedWord.lemma}</span>
                  )}
                  {lxxInterlinearSelectedWord.gloss && (
                    <span className="info-badge">{lxxInterlinearSelectedWord.gloss}</span>
                  )}
                  {strong && (
                    <span className="info-badge">{strong}</span>
                  )}
                  <span className="info-badge">{strongRows.length} occurrence{strongRows.length !== 1 ? 's' : ''} in LXX</span>
                </h3>
                <button className="modal-close-btn" onClick={() => setLxxInterlinearSelectedWord(null)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="viewer-toolbar" style={{ marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <button
                    className={`toolbar-btn ${lxxInterlinearWordColumnPanelOpen ? 'active' : ''}`}
                    onClick={() => setLxxInterlinearWordColumnPanelOpen(prev => !prev)}
                  >
                    ⚙️ {lxxInterlinearWordColumnPanelOpen ? 'Hide' : 'Show'} Columns ({lxxGridSelectedColumns.length}/{modalColumns.length})
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginLeft: '0.5rem' }}>
                    <span style={{ fontSize: '0.85rem', color: '#7f8c8d', fontWeight: 600 }}>Verse numbering:</span>
                    <button
                      className={`interlinear-toggle-chip ${lxxInterlinearWordVerseMode === 'lxx' ? 'active' : ''}`}
                      onClick={() => setLxxInterlinearWordVerseMode('lxx')}
                    >
                      LXX (native)
                    </button>
                    <button
                      className={`interlinear-toggle-chip ${lxxInterlinearWordVerseMode === 'acf' ? 'active' : ''}`}
                      onClick={() => setLxxInterlinearWordVerseMode('acf')}
                    >
                      ACF aligned
                    </button>
                  </div>
                  <button
                    className="view-translations-btn"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => {
                      setLxxInterlinearSelectedWord(null);
                      setLxxInterlinearWordColumnPanelOpen(false);
                      setLxxSearchText(strong);
                      setLxxSearchSelectedValue(strong);
                      setLxxMenuOpen(false);
                      setActiveMenu('lxxSearchStrong');
                    }}
                  >
                    📖 View Verse Translations
                  </button>
                </div>
                {lxxInterlinearWordColumnPanelOpen && (
                  <div className="column-panel">
                    <div className="column-panel-header">
                      <h3>Select Columns</h3>
                      <div className="column-panel-actions">
                        <button onClick={() => setLxxGridSelectedColumns([...modalColumns])}>Select All</button>
                        <button onClick={() => setLxxGridSelectedColumns([])}>Deselect All</button>
                        <button className="close-panel-btn" onClick={() => setLxxInterlinearWordColumnPanelOpen(false)}>✕</button>
                      </div>
                    </div>
                    <div className="columns-list">
                      {modalColumns.map(column => (
                        <label key={column} className="column-checkbox">
                          <input
                            type="checkbox"
                            checked={lxxGridSelectedColumns.includes(column)}
                            onChange={() => toggleLxxGridColumn(column)}
                          />
                          <span>{column}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {lxxGridSelectedColumns.length === 0 ? (
                  <div className="no-columns">
                    No columns selected. Click <strong>⚙️ Show Columns</strong> above to choose which columns to display.
                  </div>
                ) : (
                  <div className="modal-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          {lxxGridSelectedColumns.map(column => (
                            <th key={column}>
                              {column === 'chapter' ? (lxxInterlinearWordVerseMode === 'acf' ? 'chapter (ACF)' : 'chapter (LXX)') :
                               column === 'verse'   ? (lxxInterlinearWordVerseMode === 'acf' ? 'verse (ACF)'   : 'verse (LXX)')   : column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {enrichedRows.map((row, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            {lxxGridSelectedColumns.map(column => {
                              let val = row[column] || '';
                              if (lxxInterlinearWordVerseMode === 'acf') {
                                if (column === 'chapter') val = row.acf_chapter || '—';
                                if (column === 'verse')   val = row.acf_verse   || '—';
                              }
                              return <td key={column}>{val}</td>;
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
          );
        })()}

        {/* ==================== LXX SEARCH STRONG TAB ==================== */}
        {activeMenu === 'lxxSearchStrong' && (
          <div className="search-page">
            <h2>🔍 Search LXX by Strong Number</h2>

            {lxxDataLoading ? (
              <div className="loading">Loading LXX data...</div>
            ) : (
              <>
                <div className="search-input-area">
                  <div className="search-input-row">
                    <input 
                      type="text" 
                      className="search-input"
                      value={lxxSearchText}
                      onChange={(e) => { setLxxSearchText(e.target.value); setLxxSearchSelectedValue(null); }}
                      placeholder="Type a Strong number (e.g. 3056)..."
                      autoFocus
                    />
                    <button className="search-clear-btn" onClick={handleLxxSearchClear}>Clear</button>
                  </div>
                </div>
                {lxxSearchText && !lxxSearchSelectedValue && (
                  <div className="search-suggestions">
                    <h3>Matching Strong Numbers ({lxxSearchSuggestions.length}{lxxSearchSuggestions.length === 50 ? '+' : ''})</h3>
                    <div className="suggestions-list">
                      {lxxSearchSuggestions.map((val, idx) => (
                        <button key={idx} className="suggestion-item" onClick={() => handleLxxSelectSuggestion(val)}>
                          {val}{lxxStrongToLemmaMap.get(val) ? <span className="suggestion-lemma"> — {lxxStrongToLemmaMap.get(val)}</span> : ''}
                        </button>
                      ))}
                      {lxxSearchSuggestions.length === 0 && <div className="no-suggestions">No matching Strong numbers found in LXX.</div>}
                    </div>
                  </div>
                )}
                {lxxSearchSelectedValue && (
                  <div className="lxx-search-results">
                    <div className="data-info">
                      <span className="info-badge">{lxxSearchSelectedValue}</span>
                      {lxxStrongToLemmaMap.get(lxxSearchSelectedValue) && (
                        <span className="info-badge greek-text">{lxxStrongToLemmaMap.get(lxxSearchSelectedValue)}</span>
                      )}
                      {lxxStrongToLemmaMap.get(lxxSearchSelectedValue) && lxxLemmaToGlossMap.get(lxxStrongToLemmaMap.get(lxxSearchSelectedValue)) && (
                        <span className="info-badge">{lxxLemmaToGlossMap.get(lxxStrongToLemmaMap.get(lxxSearchSelectedValue))}</span>
                      )}
                      <span>Found in {lxxSearchResults.length} verses in the LXX</span>
                      <button className="back-btn" onClick={() => setLxxSearchSelectedValue(null)}>← Back to suggestions</button>
                    </div>

                    <div className="bibles-translation-selector">
                      <span className="bibles-translations-label">Translations:</span>
                      <div className="bibles-translation-chips">
                        {LXX_SEARCH_TRANSLATIONS.map(t => {
                          const isSelected = lxxSearchSelectedTranslations.includes(t.key);
                          return (
                            <button
                              key={t.key}
                              className={`bibles-chip bibles-chip-${t.key} ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleLxxSearchTranslation(t.key)}
                            >
                              {t.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {lxxSearchTranslationsLoading ? (
                      <div className="loading">Loading translations...</div>
                    ) : (
                      <div className="translation-verses">
                        {lxxSearchResults.map((verse, idx) => (
                          <div key={verse.key} className="verse-card">
                            <div className="verse-reference">
                              <span className="verse-num">{idx + 1}.</span>
                              <span className="verse-ref-text">{verse.book} {verse.chapter}:{verse.verse}</span>
                            </div>
                            <div className="verse-translations">
                              {LXX_SEARCH_TRANSLATIONS
                                .filter(t => lxxSearchSelectedTranslations.includes(t.key))
                                .map(t => {
                                const text = lxxSearchTranslationsData[t.key] && lxxSearchTranslationsData[t.key][verse.key];
                                return (
                                  <div key={t.key} className="verse-translation-item">
                                    <span className={`translation-label label-${t.key}`}>
                                      {t.name}
                                    </span>
                                    <p className={`verse-text-content ${t.key === 'lxx' ? 'greek-text' : ''} ${t.key === 'hebrew_bible' ? 'hebrew-text' : ''}`}>
                                      {text || '(verse not found in this translation)'}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        {lxxSearchResults.length === 0 && (
                          <div className="no-columns">No verses found containing this Strong number.</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ==================== LXX SEARCH LEMMA TAB ==================== */}
        {activeMenu === 'lxxSearchLemma' && (
          <div className="search-page">
            <h2>🔍 Search LXX by Lemma</h2>

            {lxxDataLoading ? (
              <div className="loading">Loading LXX data...</div>
            ) : (
              <>
                <div className="search-input-area">
                  <div className="search-input-row">
                    <input
                      type="text"
                      className="search-input greek-text"
                      value={lxxLemmaSearchText}
                      onChange={(e) => { setLxxLemmaSearchText(e.target.value); setLxxLemmaSelectedValue(null); }}
                      placeholder="Type or use the keyboard below..."
                      readOnly={false}
                    />
                    <button className="search-clear-btn" onClick={handleLxxLemmaSearchClear}>Clear</button>
                    <button className="search-clear-btn" onClick={handleLxxLemmaBackspace}>⌫</button>
                  </div>
                  <div className="greek-keyboard">
                    {greekKeyboard.map((row, rowIdx) => (
                      <div key={rowIdx} className="greek-keyboard-row">
                        {row.map((char, charIdx) => (
                          <button
                            key={charIdx}
                            className="greek-key"
                            onClick={() => handleLxxLemmaGreekKey(char)}
                          >
                            {char}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {lxxLemmaSearchText && !lxxLemmaSelectedValue && (
                  <div className="search-suggestions">
                    <h3>Matching Lemmas ({lxxLemmaSearchSuggestions.length}{lxxLemmaSearchSuggestions.length === 50 ? '+' : ''})</h3>
                    <div className="suggestions-list">
                      {lxxLemmaSearchSuggestions.map((val, idx) => (
                        <button key={idx} className="suggestion-item suggestion-item--lemma" onClick={() => handleLxxLemmaSelectSuggestion(val)}>
                          <span className="suggestion-word greek-text">{val}</span>
                          {lxxLemmaToGlossMap.get(val) && <span className="suggestion-gloss">{lxxLemmaToGlossMap.get(val)}</span>}
                        </button>
                      ))}
                      {lxxLemmaSearchSuggestions.length === 0 && <div className="no-suggestions">No matching lemmas found in the LXX.</div>}
                    </div>
                  </div>
                )}

                {lxxLemmaSelectedValue && (
                  <div className="lxx-search-results">
                    <div className="data-info">
                      <span className="info-badge greek-text">{lxxLemmaSelectedValue}</span>
                      {lxxLemmaToGlossMap.get(lxxLemmaSelectedValue) && (
                        <span className="info-badge">{lxxLemmaToGlossMap.get(lxxLemmaSelectedValue)}</span>
                      )}
                      <span>Found in {lxxLemmaSearchResults.length} verses in the LXX</span>
                      <button className="back-btn" onClick={() => { setLxxLemmaSelectedValue(null); }}>← Back to suggestions</button>
                    </div>

                    <div className="bibles-translation-selector">
                      <span className="bibles-translations-label">Translations:</span>
                      <div className="bibles-translation-chips">
                        {LXX_SEARCH_TRANSLATIONS.map(t => {
                          const isSelected = lxxSearchSelectedTranslations.includes(t.key);
                          return (
                            <button
                              key={t.key}
                              className={`bibles-chip bibles-chip-${t.key} ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleLxxSearchTranslation(t.key)}
                            >
                              {t.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {lxxSearchTranslationsLoading ? (
                      <div className="loading">Loading translations...</div>
                    ) : (
                      <div className="translation-verses">
                        {lxxLemmaSearchResults.map((verse, idx) => (
                          <div key={verse.key} className="verse-card">
                            <div className="verse-reference">
                              <span className="verse-num">{idx + 1}.</span>
                              <span className="verse-ref-text">{verse.book} {verse.chapter}:{verse.verse}</span>
                            </div>
                            <div className="verse-translations">
                              {LXX_SEARCH_TRANSLATIONS
                                .filter(t => lxxSearchSelectedTranslations.includes(t.key))
                                .map(t => {
                                  const text = lxxSearchTranslationsData[t.key] && lxxSearchTranslationsData[t.key][verse.key];
                                  return (
                                    <div key={t.key} className="verse-translation-item">
                                      <span className={`translation-label label-${t.key}`}>
                                        {t.name}
                                      </span>
                                      <p className={`verse-text-content ${t.key === 'lxx' ? 'greek-text' : ''} ${t.key === 'hebrew_bible' ? 'hebrew-text' : ''}`}>
                                        {text || '(verse not found in this translation)'}
                                      </p>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        ))}
                        {lxxLemmaSearchResults.length === 0 && (
                          <div className="no-columns">No verses found containing this lemma.</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ==================== LXX VERSE GRID TAB ==================== */}
        {activeMenu === 'lxxVerseGrid' && (
          <div className="viewer-layout">
            <div className="interlinear-header">
              <h2>📊 LXX Verse Grid</h2>
              <p className="interlinear-subtitle">View LXX word data in a grid format</p>
            </div>

            {lxxDataLoading ? (
              <div className="loading">Loading LXX data...</div>
            ) : (
              <>
                <div className="viewer-toolbar">
                  <button 
                    className={`toolbar-btn ${lxxGridColumnPanelOpen ? 'active' : ''}`}
                    onClick={() => setLxxGridColumnPanelOpen(prev => !prev)}
                  >
                    ⚙️ {lxxGridColumnPanelOpen ? 'Hide' : 'Show'} Columns ({lxxGridSelectedColumns.length}/{lxxGridColumns.length})
                  </button>
                  {lxxGridSelectedRow && (
                    <>
                      <span className="toolbar-separator">|</span>
                      <span className="toolbar-selected-info">
                        Selected: <strong className="strong-number-badge">{lxxGridSelectedRow.strong}</strong>
                        {lxxGridSelectedRow.lemma && <span className="greek-text"> ({lxxGridSelectedRow.lemma})</span>}
                        {lxxGridSelectedRow.gloss && <span> — {lxxGridSelectedRow.gloss}</span>}
                      </span>
                      {lxxGridSelectedRow.strong && (
                        <button
                          className="toolbar-btn"
                          onClick={() => handleLxxGridSeeOccurrences(lxxGridSelectedRow.strong)}
                        >
                          📋 See Occurrences
                        </button>
                      )}
                      {lxxGridSelectedRow.lemma && (
                        <button
                          className="toolbar-btn"
                          onClick={() => handleLxxViewVerseTranslations(lxxGridSelectedRow.lemma)}
                        >
                          📖 View Verse Translations
                        </button>
                      )}
                    </>
                  )}
                </div>

                {lxxGridColumnPanelOpen && (
                  <div className="column-panel">
                    <div className="column-panel-header">
                      <h3>Select Columns</h3>
                      <div className="column-panel-actions">
                        <button onClick={() => setLxxGridSelectedColumns([...lxxGridColumns])}>Select All</button>
                        <button onClick={() => setLxxGridSelectedColumns([])}>Deselect All</button>
                        <button className="close-panel-btn" onClick={() => setLxxGridColumnPanelOpen(false)}>✕</button>
                      </div>
                    </div>
                    <div className="columns-list">
                      {lxxGridColumns.map(column => (
                        <label key={column} className="column-checkbox">
                          <input
                            type="checkbox"
                            checked={lxxGridSelectedColumns.includes(column)}
                            onChange={() => toggleLxxGridColumn(column)}
                          />
                          <span>{column}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="display-data">
                  {lxxGridSelectedColumns.length === 0 ? (
                    <div className="no-columns">
                      No columns selected. Click <strong>⚙️ Show Columns</strong> above to choose which columns to display.
                    </div>
                  ) : (
                    <div
                      className="table-container keyboard-scrollable"
                      tabIndex={0}
                      onKeyDown={handlePagedTableKeyDown}
                      onMouseDown={focusTableContainer}
                      aria-label="LXX verse grid. Use Page Up and Page Down to move by pages."
                    >
                      <div className="data-info">
                        Showing {lxxGridFilteredData.length} rows × {lxxGridSelectedColumns.length} columns
                        <span className="info-badge">{lxxSelectedBook} {lxxGridChapter} ({lxxGridNumberingMode === 'lxx' ? 'LXX' : 'ACF'})</span>
                        {!lxxGridSelectedRow && <span className="hint">💡 Click a row to see occurrence and translation options</span>}
                      </div>
                      <PaginationControls currentPage={lxxGridPage} totalRows={lxxGridFilteredData.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setLxxGridPage} />
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            {lxxGridSelectedColumns.map(column => (
                              <th key={column}>{column}</th>
                            ))}
                            <th className="action-col"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {lxxGridFilteredData.slice(lxxGridPage * TABLE_PAGE_SIZE, (lxxGridPage + 1) * TABLE_PAGE_SIZE).map((row, index) => (
                            <tr
                              key={lxxGridPage * TABLE_PAGE_SIZE + index}
                              onClick={() => setLxxGridSelectedRow(prev => prev === row ? null : row)}
                              className={lxxGridSelectedRow === row ? 'selected-row' : ''}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>{lxxGridPage * TABLE_PAGE_SIZE + index + 1}</td>
                              {lxxGridSelectedColumns.map(column => (
                                <td key={column}>{row[column]}</td>
                              ))}
                              <td className="action-col">
                                {row.strong && (
                                  <button
                                    className="row-occurrence-btn"
                                    onClick={(e) => { e.stopPropagation(); handleLxxGridSeeOccurrences(row.strong); }}
                                  >
                                    📋 See Occurrences
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <PaginationControls currentPage={lxxGridPage} totalRows={lxxGridFilteredData.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setLxxGridPage} />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ==================== LXX STRONG FREQUENCY TAB ==================== */}
        {activeMenu === 'lxxStrongFrequency' && (
          <div className="display-data">
            {lxxDataLoading ? (
              <div className="loading">Loading LXX data...</div>
            ) : (
            <>
              <div className="data-info">
                <label>
                  Filter by Class:
                  <select
                    value={lxxSelectedClassFilter}
                    onChange={(e) => {
                      setLxxSelectedClassFilter(e.target.value);
                      setLxxSelectedTenseFilter('');
                      setLxxSelectedVoiceFilter('');
                      setLxxSelectedMoodFilter('');
                      setLxxSelectedPersonFilter('');
                    }}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Classes</option>
                    {lxxFilterValues.classes.map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by Gender:
                  <select
                    value={lxxSelectedGenderFilter}
                    onChange={(e) => setLxxSelectedGenderFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Genders</option>
                    {lxxFilterValues.genders.map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by Case:
                  <select
                    value={lxxSelectedCaseFilter}
                    onChange={(e) => setLxxSelectedCaseFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Cases</option>
                    {lxxFilterValues.cases.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by Number:
                  <select
                    value={lxxSelectedNumberFilter}
                    onChange={(e) => setLxxSelectedNumberFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Numbers</option>
                    {lxxFilterValues.numbers.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
                {lxxSelectedClassFilter === 'verb' && (
                  <>
                    <label style={{ marginLeft: '20px' }}>
                      Tense:
                      <select
                        value={lxxSelectedTenseFilter}
                        onChange={(e) => setLxxSelectedTenseFilter(e.target.value)}
                        style={{ marginLeft: '10px' }}
                      >
                        <option value="">All Tenses</option>
                        {lxxFilterValues.tenses.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ marginLeft: '20px' }}>
                      Voice:
                      <select
                        value={lxxSelectedVoiceFilter}
                        onChange={(e) => setLxxSelectedVoiceFilter(e.target.value)}
                        style={{ marginLeft: '10px' }}
                      >
                        <option value="">All Voices</option>
                        {lxxFilterValues.voices.map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ marginLeft: '20px' }}>
                      Mood:
                      <select
                        value={lxxSelectedMoodFilter}
                        onChange={(e) => setLxxSelectedMoodFilter(e.target.value)}
                        style={{ marginLeft: '10px' }}
                      >
                        <option value="">All Moods</option>
                        {lxxFilterValues.moods.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ marginLeft: '20px' }}>
                      Person:
                      <select
                        value={lxxSelectedPersonFilter}
                        onChange={(e) => setLxxSelectedPersonFilter(e.target.value)}
                        style={{ marginLeft: '10px' }}
                      >
                        <option value="">All Persons</option>
                        {lxxFilterValues.persons.map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </label>
                  </>
                )}
                <span style={{ marginLeft: '20px' }}>
                  Showing {lxxStrongFrequencyData.length} unique Strong numbers
                  {lxxSelectedClassFilter && ` (class: ${lxxSelectedClassFilter})`}
                  {lxxSelectedGenderFilter && ` (gender: ${lxxSelectedGenderFilter})`}
                  {lxxSelectedCaseFilter && ` (case: ${lxxSelectedCaseFilter})`}
                  {lxxSelectedNumberFilter && ` (number: ${lxxSelectedNumberFilter})`}
                  {lxxSelectedTenseFilter && ` (tense: ${lxxSelectedTenseFilter})`}
                  {lxxSelectedVoiceFilter && ` (voice: ${lxxSelectedVoiceFilter})`}
                  {lxxSelectedMoodFilter && ` (mood: ${lxxSelectedMoodFilter})`}
                  {lxxSelectedPersonFilter && ` (person: ${lxxSelectedPersonFilter})`}
                </span>
              </div>
            <div
              className="table-container keyboard-scrollable"
              tabIndex={0}
              onKeyDown={handlePagedTableKeyDown}
              onMouseDown={focusTableContainer}
              aria-label="LXX Strong frequency grid. Use Page Up and Page Down to move by pages."
            >
              <PaginationControls currentPage={lxxStrongFreqPage} totalRows={lxxStrongFrequencyData.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setLxxStrongFreqPage} />
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Strong</th>
                    <th>Frequency</th>
                    <th>Class</th>
                    <th>Gender</th>
                    <th>Case</th>
                    <th>Number</th>
                    {lxxSelectedClassFilter === 'verb' && <>
                      <th>Tense</th>
                      <th>Voice</th>
                      <th>Mood</th>
                      <th>Person</th>
                    </>}
                    <th>Lemma</th>
                    <th>Gloss</th>
                    <th>Hebrew Strong</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lxxStrongFrequencyData.slice(lxxStrongFreqPage * TABLE_PAGE_SIZE, (lxxStrongFreqPage + 1) * TABLE_PAGE_SIZE).map((row, index) => (
                    <tr key={lxxStrongFreqPage * TABLE_PAGE_SIZE + index}>
                      <td>{lxxStrongFreqPage * TABLE_PAGE_SIZE + index + 1}</td>
                      <td>{row.strong}</td>
                      <td>{row.frequency}</td>
                      <td>{row.class}</td>
                      <td>{row.gender}</td>
                      <td>{row.case}</td>
                      <td>{row.number}</td>
                      {lxxSelectedClassFilter === 'verb' && <>
                        <td>{row.tense}</td>
                        <td>{row.voice}</td>
                        <td>{row.mood}</td>
                        <td>{row.person}</td>
                      </>}
                      <td>{row.lemma}</td>
                      <td>{row.gloss}</td>
                      <td>{row.hebrew_strong}</td>
                      <td>
                        <button 
                          onClick={() => handleLxxSeeOccurrences(row.strong)}
                          className="see-occurrences-btn"
                        >
                          See Occurrences
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls currentPage={lxxStrongFreqPage} totalRows={lxxStrongFrequencyData.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setLxxStrongFreqPage} />
            </div>
            </>
            )}
          </div>
        )}

        {/* ==================== LXX FREQUENCY OCCURRENCES (drill-down) ==================== */}
        {activeMenu === 'lxxFrequencyOccurrences' && (
          <div className="viewer-layout">
            <div className="viewer-toolbar">
              <button 
                onClick={() => setActiveMenu('lxxStrongFrequency')}
                className="back-btn"
              >
                ← Back to LXX Strong Frequency
              </button>
              <span className="toolbar-separator">|</span>
              <button 
                className={`toolbar-btn ${lxxFreqOccColumnPanelOpen ? 'active' : ''}`}
                onClick={() => setLxxFreqOccColumnPanelOpen(prev => !prev)}
              >
                ⚙️ {lxxFreqOccColumnPanelOpen ? 'Hide' : 'Show'} Columns ({lxxGridSelectedColumns.length}/{lxxGridColumns.length})
              </button>
              <span className="toolbar-separator">|</span>
              <strong>Strong Number: {lxxFrequencyStrongNumber}</strong> — Found {lxxFrequencyOccurrences.length} occurrences
              {(lxxSelectedClassFilter || lxxSelectedGenderFilter || lxxSelectedCaseFilter || lxxSelectedNumberFilter) ? ' (filtered' : ' in the entire LXX'}
              {lxxSelectedClassFilter && ` class: ${lxxSelectedClassFilter}`}
              {lxxSelectedGenderFilter && ` gender: ${lxxSelectedGenderFilter}`}
              {lxxSelectedCaseFilter && ` case: ${lxxSelectedCaseFilter}`}
              {lxxSelectedNumberFilter && ` number: ${lxxSelectedNumberFilter}`}
              {lxxSelectedTenseFilter && ` tense: ${lxxSelectedTenseFilter}`}
              {lxxSelectedVoiceFilter && ` voice: ${lxxSelectedVoiceFilter}`}
              {lxxSelectedMoodFilter && ` mood: ${lxxSelectedMoodFilter}`}
              {lxxSelectedPersonFilter && ` person: ${lxxSelectedPersonFilter}`}
              {(lxxSelectedClassFilter || lxxSelectedGenderFilter || lxxSelectedCaseFilter || lxxSelectedNumberFilter) && ')'}
              {lxxFrequencyOccurrences[0]?.lemma && (
                <>
                  <span className="toolbar-separator">|</span>
                  <button
                    className="toolbar-btn"
                    onClick={() => handleLxxViewVerseTranslations(lxxFrequencyOccurrences[0].lemma)}
                  >
                    📖 View Verse Translations
                  </button>
                </>
              )}
            </div>

            {lxxFreqOccColumnPanelOpen && (
              <div className="column-panel">
                <div className="column-panel-header">
                  <h3>Select Columns</h3>
                  <div className="column-panel-actions">
                    <button onClick={() => setLxxGridSelectedColumns([...lxxGridColumns])}>Select All</button>
                    <button onClick={() => setLxxGridSelectedColumns([])}>Deselect All</button>
                    <button className="close-panel-btn" onClick={() => setLxxFreqOccColumnPanelOpen(false)}>✕</button>
                  </div>
                </div>
                <div className="columns-list">
                  {lxxGridColumns.map(column => (
                    <label key={column} className="column-checkbox">
                      <input
                        type="checkbox"
                        checked={lxxGridSelectedColumns.includes(column)}
                        onChange={() => toggleLxxGridColumn(column)}
                      />
                      <span>{column}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="display-data">
              {lxxGridSelectedColumns.length === 0 ? (
                <div className="no-columns">
                  No columns selected. Click <strong>⚙️ Show Columns</strong> above to choose which columns to display.
                </div>
              ) : (
                <div
                  className="table-container keyboard-scrollable"
                  tabIndex={0}
                  onKeyDown={handlePagedTableKeyDown}
                  onMouseDown={focusTableContainer}
                  aria-label="LXX Strong occurrences grid. Use Page Up and Page Down to move by pages."
                >
                  <div className="data-info">
                    Showing {lxxFrequencyOccurrences.length} rows × {lxxGridSelectedColumns.length + 1} columns
                    <span className="info-badge">Strong: {lxxFrequencyStrongNumber}</span>
                  </div>
                  <PaginationControls currentPage={lxxFreqOccPage} totalRows={lxxFrequencyOccurrences.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setLxxFreqOccPage} />
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        {lxxGridSelectedColumns.map(column => (
                          <th key={column}>{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lxxFrequencyOccurrences.slice(lxxFreqOccPage * TABLE_PAGE_SIZE, (lxxFreqOccPage + 1) * TABLE_PAGE_SIZE).map((row, index) => (
                        <tr key={lxxFreqOccPage * TABLE_PAGE_SIZE + index}>
                          <td>{lxxFreqOccPage * TABLE_PAGE_SIZE + index + 1}</td>
                          {lxxGridSelectedColumns.map(column => (
                            <td key={column}>{row[column]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <PaginationControls currentPage={lxxFreqOccPage} totalRows={lxxFrequencyOccurrences.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setLxxFreqOccPage} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== LXX VERSE TRANSLATIONS (drill-down from Verse Grid) ==================== */}
        {activeMenu === 'lxxVerseTranslations' && (
          <div className="translation-viewer">
            <div className="translation-header">
              <button className="back-btn" onClick={() => setActiveMenu('lxxVerseGrid')}>
                ← Back to LXX Verse Grid
              </button>
              <h2>📖 LXX Verse Translations</h2>
              <p className="translation-subtitle">
                Showing {lxxVerseTranslationVerses.length} verses containing lemma{' '}
                <span className="info-badge greek-text">{lxxVerseTranslationsLemma}</span>
                {lxxLemmaToGlossMap.get(lxxVerseTranslationsLemma) && (
                  <span className="info-badge">{lxxLemmaToGlossMap.get(lxxVerseTranslationsLemma)}</span>
                )}
              </p>
            </div>

            <div className="bibles-translation-selector" style={{ marginBottom: '1rem' }}>
              <span className="bibles-translations-label">Translations:</span>
              <div className="bibles-translation-chips">
                {LXX_SEARCH_TRANSLATIONS.map(t => {
                  const isSelected = lxxVerseTranslationsSelectedTranslations.includes(t.key);
                  return (
                    <button
                      key={t.key}
                      className={`bibles-chip bibles-chip-${t.key} ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleLxxVerseTranslation(t.key)}
                    >
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {lxxSearchTranslationsLoading ? (
              <div className="loading">Loading translations...</div>
            ) : lxxVerseTranslationsSelectedTranslations.length === 0 ? (
              <div className="no-columns" style={{ background: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                No translations selected. Click one or more translations above.
              </div>
            ) : (
              <div className="translation-verses">
                {lxxVerseTranslationVerses.map((verse, idx) => (
                  <div key={verse.key} className="verse-card">
                    <div className="verse-reference">
                      <span className="verse-num">{idx + 1}.</span>
                      <span className="verse-ref-text">{verse.book} {verse.chapter}:{verse.verse}</span>
                    </div>
                    <div className="verse-translations">
                      {LXX_SEARCH_TRANSLATIONS
                        .filter(t => lxxVerseTranslationsSelectedTranslations.includes(t.key))
                        .map(t => {
                          const text = lxxSearchTranslationsData[t.key] && lxxSearchTranslationsData[t.key][verse.key];
                          return (
                            <div key={t.key} className="verse-translation-item">
                              <span className={`translation-label label-${t.key}`}>{t.name}</span>
                              <p className={`verse-text-content ${t.key === 'lxx' ? 'greek-text' : ''} ${t.key === 'hebrew_bible' ? 'hebrew-text' : ''}`}>
                                {text || '(verse not found in this translation)'}
                              </p>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ))}
                {lxxVerseTranslationVerses.length === 0 && (
                  <div className="no-columns">No verses found containing this lemma.</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ==================== HEBREW MT TAB ==================== */}
        {activeMenu === 'hebrewMT' && (
          <div className="bibles-viewer">
            <div className="bibles-header">
              <h2>📖 Hebrew MT — Chapter Viewer</h2>
              <p className="bibles-subtitle">Read the Hebrew Masoretic Text chapter by chapter in multiple translations</p>
            </div>

            {hebDataLoading ? (
              <div className="loading">Loading Hebrew data...</div>
            ) : (
              <>
                <div className="bibles-controls">
                  <div className="bibles-nav">
                    <span className="bibles-info-badge">{hebSelectedBook} {hebSelectedChapter}</span>
                  </div>

                  <div className="bibles-translation-selector">
                    <span className="bibles-translations-label">Translations:</span>
                    <div className="bibles-translation-chips">
                      {HEB_BIBLE_TRANSLATIONS.map(t => {
                        const isSelected = hebBiblesSelectedTranslations.includes(t.key);
                        return (
                          <button
                            key={t.key}
                            className={`bibles-chip bibles-chip-${t.key} ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleHebBiblesTranslation(t.key)}
                          >
                            {t.name}
                          </button>
                        );
                      })}
                    </div>
                    <div className="interlinear-toggle-row" style={{ marginTop: '0.5rem' }}>
                      <span className="interlinear-toggle-label">Font:</span>
                      <button
                        className="interlinear-toggle-chip"
                        onClick={() => setHebMtTranslationFontSize(prev => Math.max(8, prev - 1))}
                        title="Decrease font size"
                      >
                        🔡−
                      </button>
                      <span style={{ fontSize: '0.8rem', alignSelf: 'center', color: '#aaa' }}>{hebMtTranslationFontSize}px</span>
                      <button
                        className="interlinear-toggle-chip"
                        onClick={() => setHebMtTranslationFontSize(prev => Math.min(32, prev + 1))}
                        title="Increase font size"
                      >
                        🔡+
                      </button>
                    </div>
                  </div>
                </div>

                {hebBiblesLoading ? (
                  <div className="loading">Loading translations...</div>
                ) : hebBiblesSelectedTranslations.length === 0 ? (
                  <div className="bibles-empty">No translations selected. Click one or more translations above to start reading.</div>
                ) : (
                  <div className="bibles-chapter-content">
                    {hebBiblesVerses.map((verseNum) => {
                      const verseKey = `${hebSelectedBook}_${hebSelectedChapter}_${verseNum}`;
                      return (
                        <div key={verseKey} className="bibles-verse-block">
                          <div className="bibles-verse-number-bar">
                            <span className="bibles-verse-number">{verseNum}</span>
                            <span className="bibles-verse-ref">{hebSelectedBook} {hebSelectedChapter}:{verseNum}</span>
                          </div>
                          <div className="bibles-verse-translations">
                            {HEB_BIBLE_TRANSLATIONS
                              .filter(t => hebBiblesSelectedTranslations.includes(t.key))
                              .map(t => {
                                const text = hebOtTranslations[t.key] && hebOtTranslations[t.key][verseKey];
                                return (
                                  <div key={t.key} className={`bibles-verse-row bibles-row-${t.key}`}>
                                    <span className={`bibles-label bibles-label-${t.key}`}>{t.name}</span>
                                    <p className={`bibles-verse-text ${t.key === 'hebrew_bible' ? 'hebrew-text' : ''} ${t.key === 'lxx' ? 'greek-text' : ''}`} style={{ fontSize: `${hebMtTranslationFontSize}px` }}>
                                      {text || <em className="bibles-not-found">(verse not found)</em>}
                                    </p>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      );
                    })}
                    {hebBiblesVerses.length === 0 && !hebBiblesLoading && (
                      <div className="bibles-empty">No verses found for {hebSelectedBook} chapter {hebSelectedChapter}.</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ==================== HEBREW INTERLINEAR TAB ==================== */}
        {activeMenu === 'hebrewInterlinear' && (
          <div className="interlinear-viewer">
            <div className="interlinear-header">
              <h2>📝 Hebrew Interlinear — Chapter Viewer</h2>
              <p className="interlinear-subtitle">Read the Hebrew MT word-by-word with parsing details</p>
            </div>

            {hebDataLoading ? (
              <div className="loading">Loading Hebrew data...</div>
            ) : (
              <>
                <div className="interlinear-controls">
                  <div className="interlinear-nav">
                    <span className="interlinear-info-badge">{hebSelectedBook} {hebSelectedChapter}</span>
                  </div>

                  <div className="interlinear-toggle-row">
                    <span className="interlinear-toggle-label">Show/Hide:</span>
                    <button
                      className={`interlinear-toggle-chip ${hebInterlinearShowLemma ? 'active' : ''}`}
                      onClick={() => setHebInterlinearShowLemma(prev => !prev)}
                    >
                      Lemma
                    </button>
                    <button
                      className={`interlinear-toggle-chip ${hebInterlinearShowTranslit ? 'active' : ''}`}
                      onClick={() => setHebInterlinearShowTranslit(prev => !prev)}
                    >
                      Transliteration
                    </button>
                    <button
                      className={`interlinear-toggle-chip ${hebInterlinearShowEnglish ? 'active' : ''}`}
                      onClick={() => setHebInterlinearShowEnglish(prev => !prev)}
                    >
                      English
                    </button>
                    <button
                      className={`interlinear-toggle-chip ${hebInterlinearShowMorph ? 'active' : ''}`}
                      onClick={() => setHebInterlinearShowMorph(prev => !prev)}
                    >
                      Morphology
                    </button>
                    <button
                      className={`interlinear-toggle-chip ${hebInterlinearShowStrong ? 'active' : ''}`}
                      onClick={() => setHebInterlinearShowStrong(prev => !prev)}
                    >
                      Strong
                    </button>
                    <button
                      className={`interlinear-toggle-chip ${hebInterlinearShowFrequency ? 'active' : ''}`}
                      onClick={() => setHebInterlinearShowFrequency(prev => !prev)}
                    >
                      Frequency
                    </button>
                    <span className="interlinear-toggle-label" style={{ marginLeft: '1rem' }}>Font:</span>
                    <button
                      className="interlinear-toggle-chip"
                      onClick={() => setHebInterlinearFontSize(prev => Math.max(8, prev - 1))}
                      title="Decrease font size"
                    >
                      🔡−
                    </button>
                    <span style={{ fontSize: '0.8rem', alignSelf: 'center', color: '#aaa' }}>{hebInterlinearFontSize}px</span>
                    <button
                      className="interlinear-toggle-chip"
                      onClick={() => setHebInterlinearFontSize(prev => Math.min(32, prev + 1))}
                      title="Increase font size"
                    >
                      🔡+
                    </button>
                  </div>
                </div>

                <div className="interlinear-chapter-content">
                  {hebInterlinearVerses.map(({ verseNum, words }) => (
                    <div key={verseNum} className="interlinear-verse-block">
                      <div className="interlinear-verse-number-bar">
                        <span className="interlinear-verse-number">{verseNum}</span>
                        <span className="interlinear-verse-ref">{hebSelectedBook} {hebSelectedChapter}:{verseNum}</span>
                      </div>
                      <div className="interlinear-words" style={{ direction: 'rtl' }}>
                        {words.map((word, idx) => (
                          <div
                            key={idx}
                            className="interlinear-word-stack interlinear-word-clickable"
                            style={{ fontSize: `${hebInterlinearFontSize}px` }}
                            onClick={() => { setHebInterlinearSelectedWord(word._fullRow); setHebInterlinearWordColumnPanelOpen(false); }}
                            title="Click to view all word details"
                          >
                            <span className="interlinear-hebrew" style={{ direction: 'rtl', unicodeBidi: 'bidi-override' }}>{word.text}</span>
                            {hebInterlinearShowTranslit && (
                              <span className="interlinear-translit">{word.transliteration || '—'}</span>
                            )}
                            {hebInterlinearShowLemma && (
                              <span className="interlinear-lemma" style={{ direction: 'rtl', unicodeBidi: 'bidi-override' }}>{word.lemma || '—'}</span>
                            )}
                            {hebInterlinearShowEnglish && (
                              <span className="interlinear-gloss">{word.english || '—'}</span>
                            )}
                            {hebInterlinearShowMorph && (
                              <span className="interlinear-morph">{word.morph || '—'}</span>
                            )}
                            {hebInterlinearShowStrong && (
                              <span className="interlinear-strong">{word.strong || '—'}</span>
                            )}
                            {hebInterlinearShowFrequency && (
                              <span className="interlinear-frequency">{word.frequency || '—'}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {hebInterlinearVerses.length === 0 && (
                    <div className="interlinear-empty">No verses found for {hebSelectedBook} chapter {hebSelectedChapter}.</div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ==================== HEBREW INTERLINEAR WORD DETAIL MODAL ==================== */}
        {hebInterlinearSelectedWord && (() => {
          const strong = hebInterlinearSelectedWord.strongnumberx || '';
          const strongRows = strong
            ? hebWordData.filter(row => row.strongnumberx === strong)
            : [hebInterlinearSelectedWord];
          return (
          <div className="modal-overlay" onClick={() => setHebInterlinearSelectedWord(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>
                  <span className="interlinear-hebrew" style={{ direction: 'rtl', unicodeBidi: 'bidi-override', fontSize: '1.4rem' }}>
                    {hebInterlinearSelectedWord.text}
                  </span>
                  {hebInterlinearSelectedWord.lemma && (
                    <span className="info-badge hebrew-text">{hebInterlinearSelectedWord.lemma}</span>
                  )}
                  {hebInterlinearSelectedWord.gloss && (
                    <span className="info-badge">{hebInterlinearSelectedWord.gloss}</span>
                  )}
                  {strong && (
                    <span className="info-badge">{strong}</span>
                  )}
                  <span className="info-badge">{strongRows.length} occurrence{strongRows.length !== 1 ? 's' : ''} in Hebrew MT</span>
                </h3>
                <button className="modal-close-btn" onClick={() => setHebInterlinearSelectedWord(null)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="viewer-toolbar" style={{ marginBottom: '0.75rem' }}>
                  <button
                    className={`toolbar-btn ${hebInterlinearWordColumnPanelOpen ? 'active' : ''}`}
                    onClick={() => setHebInterlinearWordColumnPanelOpen(prev => !prev)}
                  >
                    ⚙️ {hebInterlinearWordColumnPanelOpen ? 'Hide' : 'Show'} Columns ({hebGridSelectedColumns.length}/{hebGridColumns.length})
                  </button>
                  <button
                    className="view-translations-btn"
                    style={{ marginLeft: '0.75rem' }}
                    onClick={() => {
                      const s = hebInterlinearSelectedWord.strongnumberx || '';
                      setHebInterlinearSelectedWord(null);
                      setHebInterlinearWordColumnPanelOpen(false);
                      setHebSearchText(s);
                      setHebSearchSelectedValue(s);
                      setHebMenuOpen(false);
                      setActiveMenu('hebrewSearchStrong');
                    }}
                  >
                    📖 View Verse Translations
                  </button>
                </div>
                {hebInterlinearWordColumnPanelOpen && (
                  <div className="column-panel">
                    <div className="column-panel-header">
                      <h3>Select Columns</h3>
                      <div className="column-panel-actions">
                        <button onClick={() => setHebGridSelectedColumns([...hebGridColumns])}>Select All</button>
                        <button onClick={() => setHebGridSelectedColumns([])}>Deselect All</button>
                        <button className="close-panel-btn" onClick={() => setHebInterlinearWordColumnPanelOpen(false)}>✕</button>
                      </div>
                    </div>
                    <div className="columns-list">
                      {hebGridColumns.map(column => (
                        <label key={column} className="column-checkbox">
                          <input
                            type="checkbox"
                            checked={hebGridSelectedColumns.includes(column)}
                            onChange={() => toggleHebGridColumn(column)}
                          />
                          <span>{column}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {hebGridSelectedColumns.length === 0 ? (
                  <div className="no-columns">
                    No columns selected. Click <strong>⚙️ Show Columns</strong> above to choose which columns to display.
                  </div>
                ) : (
                  <div className="modal-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          {hebGridSelectedColumns.map(column => (
                            <th key={column}>{column}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {strongRows.map((row, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            {hebGridSelectedColumns.map(column => (
                              <td key={column}>{row[column] || ''}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

              </div>
            </div>
          </div>
          );
        })()}

        {/* ==================== HEBREW SEARCH STRONG TAB ==================== */}
        {activeMenu === 'hebrewSearchStrong' && (
          <div className="search-page">
            <h2>🔍 Search Hebrew by Strong Number</h2>

            {hebDataLoading ? (
              <div className="loading">Loading Hebrew data...</div>
            ) : (
              <>
                <div className="search-input-area">
                  <div className="search-input-row">
                    <input 
                      type="text" 
                      className="search-input"
                      value={hebSearchText}
                      onChange={(e) => { setHebSearchText(e.target.value); setHebSearchSelectedValue(null); }}
                      placeholder="Type a Strong number (e.g. 0430)..."
                      autoFocus
                    />
                    <button className="search-clear-btn" onClick={handleHebSearchClear}>Clear</button>
                  </div>
                </div>
                {hebSearchText && !hebSearchSelectedValue && (
                  <div className="search-suggestions">
                    <h3>Matching Strong Numbers ({hebSearchSuggestions.length}{hebSearchSuggestions.length === 50 ? '+' : ''})</h3>
                    <div className="suggestions-list">
                      {hebSearchSuggestions.map((val, idx) => (
                        <button key={idx} className="suggestion-item" onClick={() => handleHebSelectSuggestion(val)}>
                          {val}{hebStrongToLemmaMap.get(val) ? <span className="suggestion-lemma"> — {hebStrongToLemmaMap.get(val)}</span> : ''}{hebStrongToGlossMap.get(val) ? <span className="suggestion-gloss"> ({hebStrongToGlossMap.get(val)})</span> : ''}
                        </button>
                      ))}
                      {hebSearchSuggestions.length === 0 && <div className="no-suggestions">No matching Strong numbers found in Hebrew MT.</div>}
                    </div>
                  </div>
                )}
                {hebSearchSelectedValue && (
                  <div className="lxx-search-results">
                    <div className="data-info">
                      <span className="info-badge">{hebSearchSelectedValue}</span>
                      {hebStrongToLemmaMap.get(hebSearchSelectedValue) && (
                        <span className="info-badge hebrew-text">{hebStrongToLemmaMap.get(hebSearchSelectedValue)}</span>
                      )}
                      {hebStrongToGlossMap.get(hebSearchSelectedValue) && (
                        <span className="info-badge">{hebStrongToGlossMap.get(hebSearchSelectedValue)}</span>
                      )}
                      <span>Found in {hebSearchResults.length} verses in the Hebrew MT</span>
                      <button className="back-btn" onClick={() => setHebSearchSelectedValue(null)}>← Back to suggestions</button>
                    </div>

                    <div className="bibles-translation-selector">
                      <span className="bibles-translations-label">Translations:</span>
                      <div className="bibles-translation-chips">
                        {HEB_SEARCH_TRANSLATIONS.map(t => {
                          const isSelected = hebSearchSelectedTranslations.includes(t.key);
                          return (
                            <button
                              key={t.key}
                              className={`bibles-chip bibles-chip-${t.key} ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleHebSearchTranslation(t.key)}
                            >
                              {t.name}
                            </button>
                          );
                        })}
                      </div>
                      <div className="interlinear-toggle-row" style={{ marginTop: '0.5rem' }}>
                        <span className="interlinear-toggle-label">Font:</span>
                        <button
                          className="interlinear-toggle-chip"
                          onClick={() => setHebSearchStrongFontSize(prev => Math.max(8, prev - 1))}
                          title="Decrease font size"
                        >
                          🔡−
                        </button>
                        <span style={{ fontSize: '0.8rem', alignSelf: 'center', color: '#aaa' }}>{hebSearchStrongFontSize}px</span>
                        <button
                          className="interlinear-toggle-chip"
                          onClick={() => setHebSearchStrongFontSize(prev => Math.min(32, prev + 1))}
                          title="Increase font size"
                        >
                          🔡+
                        </button>
                      </div>
                    </div>

                    {hebSearchTranslationsLoading ? (
                      <div className="loading">Loading translations...</div>
                    ) : (
                      <div className="translation-verses">
                        {hebSearchResults.map((verse, idx) => (
                          <div key={verse.key} className="verse-card">
                            <div className="verse-reference">
                              <span className="verse-num">{idx + 1}.</span>
                              <span className="verse-ref-text">{verse.book} {verse.chapter}:{verse.verse}</span>
                            </div>
                            <div className="verse-translations">
                              {HEB_SEARCH_TRANSLATIONS
                                .filter(t => hebSearchSelectedTranslations.includes(t.key))
                                .map(t => {
                                const text = hebSearchTranslationsData[t.key] && hebSearchTranslationsData[t.key][verse.key];
                                return (
                                  <div key={t.key} className="verse-translation-item">
                                    <span className={`translation-label label-${t.key}`}>
                                      {t.name}
                                    </span>
                                    <p className={`verse-text-content ${t.key === 'hebrew_bible' ? 'hebrew-text' : ''} ${t.key === 'lxx' ? 'greek-text' : ''}`} style={{ fontSize: `${hebSearchStrongFontSize}px` }}>
                                      {text || '(verse not found in this translation)'}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        {hebSearchResults.length === 0 && (
                          <div className="no-columns">No verses found containing this Strong number.</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ==================== HEBREW SEARCH LEMMA TAB ==================== */}
        {activeMenu === 'hebrewSearchLemma' && (
          <div className="search-page">
            <h2>🔍 Search Hebrew by Lemma</h2>

            {hebDataLoading ? (
              <div className="loading">Loading Hebrew data...</div>
            ) : (
              <>
                <div className="search-input-area">
                  <div className="search-input-row">
                    <input
                      type="text"
                      className="search-input hebrew-text"
                      value={hebLemmaSearchText}
                      onChange={(e) => { setHebLemmaSearchText(e.target.value); setHebLemmaSelectedValue(null); }}
                      placeholder="Type or use the keyboard below..."
                      dir="rtl"
                    />
                    <button className="search-clear-btn" onClick={handleHebLemmaSearchClear}>Clear</button>
                    <button className="search-clear-btn" onClick={handleHebLemmaBackspace}>⌫</button>
                  </div>
                  <div className="greek-keyboard">
                    {hebrewKeyboard.map((row, rowIdx) => (
                      <div key={rowIdx} className="greek-keyboard-row">
                        {row.map((char, charIdx) => (
                          <button
                            key={charIdx}
                            className="greek-key hebrew-text"
                            onClick={() => handleHebLemmaKey(char)}
                          >
                            {char}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {hebLemmaSearchText && !hebLemmaSelectedValue && (
                  <div className="search-suggestions">
                    <h3>Matching Lemmas ({hebLemmaSearchSuggestions.length}{hebLemmaSearchSuggestions.length === 50 ? '+' : ''})</h3>
                    <div className="suggestions-list">
                      {hebLemmaSearchSuggestions.map((val, idx) => (
                        <button key={idx} className="suggestion-item suggestion-item--lemma" onClick={() => handleHebLemmaSelectSuggestion(val)}>
                          <span className="suggestion-word hebrew-text" dir="rtl">{val}</span>
                          {hebLemmaToEnglishMap.get(val) && <span className="suggestion-gloss">{hebLemmaToEnglishMap.get(val)}</span>}
                        </button>
                      ))}
                      {hebLemmaSearchSuggestions.length === 0 && <div className="no-suggestions">No matching lemmas found in the Hebrew MT.</div>}
                    </div>
                  </div>
                )}

                {hebLemmaSelectedValue && (
                  <div className="lxx-search-results">
                    <div className="data-info">
                      <span className="info-badge hebrew-text" dir="rtl">{hebLemmaSelectedValue}</span>
                      {hebLemmaToEnglishMap.get(hebLemmaSelectedValue) && (
                        <span className="info-badge">{hebLemmaToEnglishMap.get(hebLemmaSelectedValue)}</span>
                      )}
                      <span>Found in {hebLemmaSearchResults.length} verses in the Hebrew MT</span>
                      <button className="back-btn" onClick={() => setHebLemmaSelectedValue(null)}>← Back to suggestions</button>
                    </div>

                    <div className="bibles-translation-selector">
                      <span className="bibles-translations-label">Translations:</span>
                      <div className="bibles-translation-chips">
                        {HEB_SEARCH_TRANSLATIONS.map(t => {
                          const isSelected = hebLemmaSearchSelectedTranslations.includes(t.key);
                          return (
                            <button
                              key={t.key}
                              className={`bibles-chip bibles-chip-${t.key} ${isSelected ? 'selected' : ''}`}
                              onClick={() => toggleHebLemmaSearchTranslation(t.key)}
                            >
                              {t.name}
                            </button>
                          );
                        })}
                      </div>
                      <div className="interlinear-toggle-row" style={{ marginTop: '0.5rem' }}>
                        <span className="interlinear-toggle-label">Font:</span>
                        <button
                          className="interlinear-toggle-chip"
                          onClick={() => setHebSearchLemmaFontSize(prev => Math.max(8, prev - 1))}
                          title="Decrease font size"
                        >
                          🔡−
                        </button>
                        <span style={{ fontSize: '0.8rem', alignSelf: 'center', color: '#aaa' }}>{hebSearchLemmaFontSize}px</span>
                        <button
                          className="interlinear-toggle-chip"
                          onClick={() => setHebSearchLemmaFontSize(prev => Math.min(32, prev + 1))}
                          title="Increase font size"
                        >
                          🔡+
                        </button>
                      </div>
                    </div>

                    {hebLemmaSearchTranslationsLoading ? (
                      <div className="loading">Loading translations...</div>
                    ) : (
                      <div className="translation-verses">
                        {hebLemmaSearchResults.map((verse, idx) => (
                          <div key={verse.key} className="verse-card">
                            <div className="verse-reference">
                              <span className="verse-num">{idx + 1}.</span>
                              <span className="verse-ref-text">{verse.book} {verse.chapter}:{verse.verse}</span>
                            </div>
                            <div className="verse-translations">
                              {HEB_SEARCH_TRANSLATIONS
                                .filter(t => hebLemmaSearchSelectedTranslations.includes(t.key))
                                .map(t => {
                                  const text = hebLemmaSearchTranslationsData[t.key] && hebLemmaSearchTranslationsData[t.key][verse.key];
                                  return (
                                    <div key={t.key} className="verse-translation-item">
                                      <span className={`translation-label label-${t.key}`}>
                                        {t.name}
                                      </span>
                                      <p className={`verse-text-content ${t.key === 'hebrew_bible' ? 'hebrew-text' : ''} ${t.key === 'lxx' ? 'greek-text' : ''}`} style={{ fontSize: `${hebSearchLemmaFontSize}px` }}>
                                        {text || '(verse not found in this translation)'}
                                      </p>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        ))}
                        {hebLemmaSearchResults.length === 0 && (
                          <div className="no-columns">No verses found containing this lemma.</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ==================== HEBREW VERSE GRID TAB ==================== */}
        {activeMenu === 'hebrewVerseGrid' && (
          <div className="viewer-layout">
            <div className="interlinear-header">
              <h2>📊 Hebrew Verse Grid</h2>
              <p className="interlinear-subtitle">View Hebrew MT word data in a grid format</p>
            </div>

            {hebDataLoading ? (
              <div className="loading">Loading Hebrew data...</div>
            ) : (
              <>
                <div className="viewer-toolbar">
                  <button 
                    className={`toolbar-btn ${hebGridColumnPanelOpen ? 'active' : ''}`}
                    onClick={() => setHebGridColumnPanelOpen(prev => !prev)}
                  >
                    ⚙️ {hebGridColumnPanelOpen ? 'Hide' : 'Show'} Columns ({hebGridSelectedColumns.length}/{hebGridColumns.length})
                  </button>
                </div>

                {hebGridColumnPanelOpen && (
                  <div className="column-panel">
                    <div className="column-panel-header">
                      <h3>Select Columns</h3>
                      <div className="column-panel-actions">
                        <button onClick={() => setHebGridSelectedColumns([...hebGridColumns])}>Select All</button>
                        <button onClick={() => setHebGridSelectedColumns([])}>Deselect All</button>
                        <button className="close-panel-btn" onClick={() => setHebGridColumnPanelOpen(false)}>✕</button>
                      </div>
                    </div>
                    <div className="columns-list">
                      {hebGridColumns.map(column => (
                        <label key={column} className="column-checkbox">
                          <input
                            type="checkbox"
                            checked={hebGridSelectedColumns.includes(column)}
                            onChange={() => toggleHebGridColumn(column)}
                          />
                          <span>{column}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="display-data">
                  {hebGridSelectedColumns.length === 0 ? (
                    <div className="no-columns">
                      No columns selected. Click <strong>⚙️ Show Columns</strong> above to choose which columns to display.
                    </div>
                  ) : (
                    <div
                      className="table-container keyboard-scrollable"
                      tabIndex={0}
                      onKeyDown={handlePagedTableKeyDown}
                      onMouseDown={focusTableContainer}
                      aria-label="Hebrew verse grid. Use Page Up and Page Down to move by pages."
                    >
                      <div className="data-info">
                        Showing {hebGridFilteredData.length} rows × {hebGridSelectedColumns.length} columns
                        <span className="info-badge">{hebSelectedBook} {hebSelectedChapter}</span>
                      </div>
                      <PaginationControls currentPage={hebGridPage} totalRows={hebGridFilteredData.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setHebGridPage} />
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            {hebGridSelectedColumns.map(column => (
                              <th key={column}>{column}</th>
                            ))}
                            <th className="action-col"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {hebGridFilteredData.slice(hebGridPage * TABLE_PAGE_SIZE, (hebGridPage + 1) * TABLE_PAGE_SIZE).map((row, index) => (
                            <tr key={hebGridPage * TABLE_PAGE_SIZE + index}>
                              <td>{hebGridPage * TABLE_PAGE_SIZE + index + 1}</td>
                              {hebGridSelectedColumns.map(column => (
                                <td key={column}>{row[column]}</td>
                              ))}
                              <td className="action-col">
                                {row.strongnumberx && (
                                  <button
                                    className="row-occurrence-btn"
                                    onClick={() => handleHebSeeOccurrences(row.strongnumberx, row.gloss)}
                                  >
                                    📋 See Occurrences
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <PaginationControls currentPage={hebGridPage} totalRows={hebGridFilteredData.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setHebGridPage} />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ==================== HEBREW STRONG FREQUENCY ==================== */}
        {activeMenu === 'hebrewStrongFrequency' && (
          <div className="display-data">
            {hebDataLoading ? (
              <div className="loading">Loading Hebrew data...</div>
            ) : (
            <>
              <div className="data-info">
                <label>
                  Filter by Class:
                  <select
                    value={hebSelectedClassFilter}
                    onChange={(e) => {
                      setHebSelectedClassFilter(e.target.value);
                      setHebSelectedStemFilter('');
                      setHebSelectedPosFilter('');
                      setHebSelectedPersonFilter('');
                    }}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Classes</option>
                    {hebFilterValues.classes.map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by Stem:
                  <select
                    value={hebSelectedStemFilter}
                    onChange={(e) => setHebSelectedStemFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Stems</option>
                    {hebFilterValues.stems.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by POS:
                  <select
                    value={hebSelectedPosFilter}
                    onChange={(e) => setHebSelectedPosFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All POS</option>
                    {hebFilterValues.poses.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by Person:
                  <select
                    value={hebSelectedPersonFilter}
                    onChange={(e) => setHebSelectedPersonFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Persons</option>
                    {hebFilterValues.persons.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by Gender:
                  <select
                    value={hebSelectedGenderFilter}
                    onChange={(e) => setHebSelectedGenderFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Genders</option>
                    {hebFilterValues.genders.map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by Number:
                  <select
                    value={hebSelectedNumberFilter}
                    onChange={(e) => setHebSelectedNumberFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Numbers</option>
                    {hebFilterValues.numbers.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by State:
                  <select
                    value={hebSelectedStateFilter}
                    onChange={(e) => setHebSelectedStateFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All States</option>
                    {hebFilterValues.states.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginLeft: '20px' }}>
                  Filter by Type:
                  <select
                    value={hebSelectedTypeFilter}
                    onChange={(e) => setHebSelectedTypeFilter(e.target.value)}
                    style={{ marginLeft: '10px' }}
                  >
                    <option value="">All Types</option>
                    {hebFilterValues.types.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </label>
                <span style={{ marginLeft: '20px' }}>
                  Showing {hebStrongFrequencyData.length} unique Strong numbers
                  {hebSelectedClassFilter && ` (class: ${hebSelectedClassFilter})`}
                  {hebSelectedStemFilter && ` (stem: ${hebSelectedStemFilter})`}
                  {hebSelectedPosFilter && ` (pos: ${hebSelectedPosFilter})`}
                  {hebSelectedPersonFilter && ` (person: ${hebSelectedPersonFilter})`}
                  {hebSelectedGenderFilter && ` (gender: ${hebSelectedGenderFilter})`}
                  {hebSelectedNumberFilter && ` (number: ${hebSelectedNumberFilter})`}
                  {hebSelectedStateFilter && ` (state: ${hebSelectedStateFilter})`}
                  {hebSelectedTypeFilter && ` (type: ${hebSelectedTypeFilter})`}
                </span>
              </div>
              <div
                className="table-container keyboard-scrollable"
                tabIndex={0}
                onKeyDown={handlePagedTableKeyDown}
                onMouseDown={focusTableContainer}
                aria-label="Hebrew Strong frequency grid. Use Page Up and Page Down to move by pages."
              >
                <PaginationControls currentPage={hebStrongFreqPage} totalRows={hebStrongFrequencyData.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setHebStrongFreqPage} />
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Strong</th>
                      <th>Frequency</th>
                      <th>Class</th>
                      <th>Stem</th>
                      <th>POS</th>
                      <th>Person</th>
                      <th>Gender</th>
                      <th>Number</th>
                      <th>State</th>
                      <th>Type</th>
                      <th>Lemma</th>
                      <th>Gloss</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hebStrongFrequencyData.slice(hebStrongFreqPage * TABLE_PAGE_SIZE, (hebStrongFreqPage + 1) * TABLE_PAGE_SIZE).map((row, index) => (
                      <tr key={hebStrongFreqPage * TABLE_PAGE_SIZE + index}>
                        <td>{hebStrongFreqPage * TABLE_PAGE_SIZE + index + 1}</td>
                        <td>{row.strong}</td>
                        <td>{row.frequency}</td>
                        <td>{row.class}</td>
                        <td>{row.stem}</td>
                        <td>{row.pos}</td>
                        <td>{row.person}</td>
                        <td>{row.gender}</td>
                        <td>{row.number}</td>
                        <td>{row.state}</td>
                        <td>{row.type}</td>
                        <td>{row.lemma}</td>
                        <td>{row.gloss}</td>
                        <td>
                          <button
                            onClick={() => handleHebSeeOccurrences(row.strong, row.gloss)}
                            className="see-occurrences-btn"
                          >
                            See Occurrences
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <PaginationControls currentPage={hebStrongFreqPage} totalRows={hebStrongFrequencyData.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setHebStrongFreqPage} />
              </div>
            </>
            )}
          </div>
        )}

        {/* ==================== HEBREW FREQUENCY OCCURRENCES (drill-down) ==================== */}
        {activeMenu === 'hebrewFrequencyOccurrences' && (
          <div className="viewer-layout">
            <div className="viewer-toolbar">
              <button
                onClick={() => setActiveMenu('hebrewStrongFrequency')}
                className="back-btn"
              >
                ← Back to Hebrew Strong Frequency
              </button>
              <span className="toolbar-separator">|</span>
              <button
                className={`toolbar-btn ${hebFreqOccColumnPanelOpen ? 'active' : ''}`}
                onClick={() => setHebFreqOccColumnPanelOpen(prev => !prev)}
              >
                ⚙️ {hebFreqOccColumnPanelOpen ? 'Hide' : 'Show'} Columns ({hebGridSelectedColumns.length}/{hebGridColumns.length})
              </button>
              <span className="toolbar-separator">|</span>
              <strong>Strong Number: {hebFrequencyStrongNumber}</strong>{hebFrequencyEnglish && <><span className="toolbar-separator">|</span><span className="info-badge">{hebFrequencyEnglish}</span></>} — Found {hebFrequencyOccurrences.length} occurrences
              {(hebSelectedClassFilter || hebSelectedStemFilter || hebSelectedPosFilter || hebSelectedPersonFilter ||
                hebSelectedGenderFilter || hebSelectedNumberFilter || hebSelectedStateFilter || hebSelectedTypeFilter) ? ' (filtered' : ' in the entire Hebrew Bible'}
              {hebSelectedClassFilter && ` class: ${hebSelectedClassFilter}`}
              {hebSelectedStemFilter && ` stem: ${hebSelectedStemFilter}`}
              {hebSelectedPosFilter && ` pos: ${hebSelectedPosFilter}`}
              {hebSelectedPersonFilter && ` person: ${hebSelectedPersonFilter}`}
              {hebSelectedGenderFilter && ` gender: ${hebSelectedGenderFilter}`}
              {hebSelectedNumberFilter && ` number: ${hebSelectedNumberFilter}`}
              {hebSelectedStateFilter && ` state: ${hebSelectedStateFilter}`}
              {hebSelectedTypeFilter && ` type: ${hebSelectedTypeFilter}`}
              {(hebSelectedClassFilter || hebSelectedStemFilter || hebSelectedPosFilter || hebSelectedPersonFilter ||
                hebSelectedGenderFilter || hebSelectedNumberFilter || hebSelectedStateFilter || hebSelectedTypeFilter) && ')'}
              {hebFrequencyOccurrences[0]?.lemma && (
                <>
                  <span className="toolbar-separator">|</span>
                  <button
                    className="toolbar-btn"
                    onClick={() => {
                      const lemma = hebFrequencyOccurrences[0].lemma;
                      setHebLemmaSearchText(lemma);
                      setHebLemmaSelectedValue(lemma);
                      setActiveMenu('hebrewSearchLemma');
                    }}
                  >
                    📖 View Verse Translations
                  </button>
                </>
              )}
            </div>

            {hebFreqOccColumnPanelOpen && (
              <div className="column-panel">
                <div className="column-panel-header">
                  <h3>Select Columns</h3>
                  <div className="column-panel-actions">
                    <button onClick={() => setHebGridSelectedColumns([...hebGridColumns])}>Select All</button>
                    <button onClick={() => setHebGridSelectedColumns([])}>Deselect All</button>
                    <button className="close-panel-btn" onClick={() => setHebFreqOccColumnPanelOpen(false)}>✕</button>
                  </div>
                </div>
                <div className="columns-list">
                  {hebGridColumns.map(column => (
                    <label key={column} className="column-checkbox">
                      <input
                        type="checkbox"
                        checked={hebGridSelectedColumns.includes(column)}
                        onChange={() => toggleHebGridColumn(column)}
                      />
                      <span>{column}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="display-data">
              {hebGridSelectedColumns.length === 0 ? (
                <div className="no-columns">
                  No columns selected. Click <strong>⚙️ Show Columns</strong> above to choose which columns to display.
                </div>
              ) : (
                <div
                  className="table-container keyboard-scrollable"
                  tabIndex={0}
                  onKeyDown={handlePagedTableKeyDown}
                  onMouseDown={focusTableContainer}
                  aria-label="Hebrew Strong occurrences grid. Use Page Up and Page Down to move by pages."
                >
                  <div className="data-info">
                    Showing {hebFrequencyOccurrences.length} rows × {hebGridSelectedColumns.length + 1} columns
                    <span className="info-badge">Strong: {hebFrequencyStrongNumber}</span>{hebFrequencyEnglish && <span className="info-badge">{hebFrequencyEnglish}</span>}
                  </div>
                  <PaginationControls currentPage={hebFreqOccPage} totalRows={hebFrequencyOccurrences.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setHebFreqOccPage} />
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        {hebGridSelectedColumns.map(column => (
                          <th key={column}>{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {hebFrequencyOccurrences.slice(hebFreqOccPage * TABLE_PAGE_SIZE, (hebFreqOccPage + 1) * TABLE_PAGE_SIZE).map((row, index) => (
                        <tr key={hebFreqOccPage * TABLE_PAGE_SIZE + index}>
                          <td>{hebFreqOccPage * TABLE_PAGE_SIZE + index + 1}</td>
                          {hebGridSelectedColumns.map(column => (
                            <td key={column}>{row[column]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <PaginationControls currentPage={hebFreqOccPage} totalRows={hebFrequencyOccurrences.length} pageSize={TABLE_PAGE_SIZE} onPageChange={setHebFreqOccPage} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== ABOUT US TAB ==================== */}
        {activeMenu === 'about' && (
          <div className="about-page">
            <h2>ℹ️ About Us</h2>
            <div className="about-content">
              <section className="about-section">
                <h3>📚 Data Source</h3>
                <p>
                  MACULA Greek Linguistic Datasets, available at{' '}
                  <a 
                    href="https://github.com/Clear-Bible/macula-greek/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    https://github.com/Clear-Bible/macula-greek/
                  </a>
                </p>
              </section>
              
              <section className="about-section">
                <h3>👤 Organizer</h3>
                <p>Jesse Alves</p>
              </section>
              
              <section className="about-section">
                <h3>📍 Location</h3>
                <p>Brasilia, Brazil</p>
              </section>
              
              <section className="about-section">
                <h3>📧 Contact</h3>
                <p>
                  <a href="mailto:contato@alfaomegabrasil.com">contato@alfaomegabrasil.com</a>
                </p>
              </section>
            </div>
          </div>
        )}      </main>

    </div>
  );
}

export default App;
